import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  VStack,
  Heading,
  HStack,
  SimpleGrid,
  Button,
  IconButton,
  useToast,
  Icon,
  Badge,
  Text,
  Divider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Switch,
  Tooltip,
  Spinner,
  List,
  ListItem,
  ListIcon,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { MdArrowBack, MdRefresh, MdAdd, MdEdit, MdVisibility, MdVisibilityOff, MdHelpOutline, MdDelete, MdOpenWith, MdTune, MdTipsAndUpdates, MdRotateRight, MdLightbulbOutline, MdDragHandle } from 'react-icons/md'
import { LuActivity, LuInfo, LuFileText, LuBuilding2, LuScanLine, LuArrowRight, LuCircleHelp, LuCircleCheck, LuHistory, LuChevronDown, LuChevronUp } from 'react-icons/lu'
import { MdCheckCircle, MdWarning } from 'react-icons/md'
import { useState, useMemo, useEffect, useCallback, useRef, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import BlotFileUpload from './BlotFileUpload'
import BlotGallery, {
  BlotInterpretation,
  findControlLinePosition,
  resolveControlLinePosition,
  plateControlLineOffset,
} from './components/BlotGallery'
import ExportButton from './components/ExportButton'
import ImmunoBlotSelection, { ImmunoBlot, IMMUNOBLOTS, moduleOfPathologyId, resolvePathologyLabel } from './ImmunoBlotSelection'
import { AdminCHUSelector } from '../../admin/AdminCHUSelector'
import BlotOnboardingTour from './BlotOnboardingTour'
import BlotWhatsNew, { hasSeenBlotWhatsNew } from './BlotWhatsNew'
import FeedbackModal from './FeedbackModal'
import BugReportModal from './BugReportModal'
import BlotRecentCases from './BlotRecentCases'
import AnalysisProgress, { useAnalysisProgress } from './AnalysisProgress'
import { getSavedAuthData } from '../../../api/axios/interceptor'
import { useAIChat } from '../../../contexts/AIChatContext'
import { getImmunoblotConfigsByPathology, ImmunoblotConfig } from '../../../api/immunoblot-configs'
import { useConsumeCredits, useCheckCredits } from '../../../api/api_hooks/credits'
import { useCHUFeatures } from '../../../contexts/CHUConfigContext'
import { isAIAccessible } from '../../../config/chuConfig'
import { useMyProfileWithModules } from '../../../api/api_hooks/user/myProfile'
import { PlanType } from '../../../types/subscription'
import { useCHUConfig, getAdminCHUOverride } from '../../../contexts/CHUConfigContext'
import { CollaborationModal } from '../../CollaborationModal'
import { OperatorBadge } from '../../OperatorBadge'
import { PremiumBadge } from '../../PremiumFeature'
import { CaseResultSummary } from '../../ManualResultSelector'
import type { ManualResult } from '../../ManualResultSelector'
import { LuShare2, LuSparkles, LuLock, LuUserCheck, LuBug, LuEye, LuEyeOff, LuClipboardPen } from 'react-icons/lu'
import { useDisclosure, Collapse } from '@chakra-ui/react'
import { pathologyDisplayName, blotLabel, isCompleteStripId } from './utils/displayName'
import { fetchSourceImageBlob } from './utils/sourceImage'
import { readsOnBandPresence, verdictFromBands } from './utils/bandRules'
import { useLeaveGuard } from '../../../hooks/useLeaveGuard'
import axiosClient from '../../../api/axios'
import { apiFetch } from '../../../api/apiFetch'
import RetrospectiveComparison from './components/RetrospectiveComparison'

// Interfaces pour la nouvelle pipeline
interface BlotROI {
  x: number
  y: number
  width: number
  height: number
}

interface BlotDetectionResult {
  id: number
  crop_base64: string
  reconstructed_base64: string
  profile: number[]
  peaks: number[]
  x0: number
  x1: number
  y_top: number
  y_bottom: number
  num_bands: number
  /** Bordeaux/Dynablot multitest only: this strip's printed self-test name (OCR). */
  pathology_label?: string | null
}

interface NewBlotAnalysisResult {
  sample_id: string
  filename: string
  /** Chemin du fichier stocké côté serveur (/uploads/blots/…), pour recharger
   *  l'image source telle que le pipeline l'a vue — voir needsServerSourceImage. */
  fileUrl?: string
  pathology_id?: string
  pathology_name?: string
  timestamp: string
  roi: BlotROI
  blots: BlotDetectionResult[]
  total_blots: number
  total_bands: number
  detection_profile?: string
  /** PDF pages merged (stacked top-to-bottom) into this case's single image. 1 for a plain image upload. */
  page_count?: number
  processing_time_seconds: number
  bandCalibration?: Record<string, Record<string, { position: number; intensity: number }>> | null
  /** Bordeaux/Dynablot multitest only: each pathology's own T1/T2/T3 témoin slots, keyed by raw pathology_label. */
  positiveControlsByPathology?: Record<string, {
    positiveControlId?: number | null
    positiveControl2Id?: number | null
    positiveControl3Id?: number | null
  }> | null
  /** Manual override of the auto-detected control-line position, keyed by blot id (as a string). */
  controlLineOverrides?: Record<string, number> | null
  /** 'prospective' (le fonctionnement habituel) ou 'retrospective'. */
  evaluationMode?: 'prospective' | 'retrospective'
  /** Posé par le serveur : la lecture de paillasse manque encore, et le cas
   *  est servi sans rien de ce qu'IRIS en pense. */
  benchReadingPending?: boolean
  benchReading?: { blots?: Record<string, { bands: string[]; conclusion: string | null }>; lockedAt?: string } | null
  irisSnapshot?: { blots?: Record<string, unknown>; takenAt?: string } | null
  /** Paired-comparison couples the biologist flipped: { "12-13": true }. */
  pairedRoleSwaps?: Record<string, boolean> | null
}

type ViewMode = 'immunoblot-selection' | 'recent-cases' | 'new-analysis' | 'case-details'

type LabModule = 'iris' | 'protis'

/**
 * CE QUI CHANGE D'UN MODULE À L'AUTRE, ET RIEN DE PLUS.
 *
 * L'atelier est le même objet pour IRIS et PROTIS — même dépôt, même galerie,
 * mêmes cas, même export. Deux choses seulement ne peuvent pas être partagées :
 *
 *  - LE NOM. L'en-tête lisait `blotAnalysis.title`, donc « IRIS », sur toutes
 *    les vues : ouvrir PROTIS depuis le Lab Studio affichait le bandeau d'IRIS
 *    au-dessus d'un gel de recherche.
 *  - L'ADRESSE. Huit navigations internes écrivaient `/lab/blot-analysis` en
 *    dur. Depuis PROTIS, la flèche de retour, « Nouvelle analyse », « Modifier »
 *    et l'ouverture d'un cas sortaient donc du module pour atterrir dans IRIS,
 *    en emportant le test choisi — c'est ce qui donnait l'impression d'être
 *    jeté sur l'écran de dépôt.
 */
const MODULE_BRAND: Record<LabModule, {
  basePath: string
  titleKey: string
  subtitleKey: string
  gradient: string
  /** Le journal des nouveautés est celui d'IRIS ; PROTIS n'en a pas encore. */
  whatsNew: boolean
}> = {
  iris: {
    basePath: '/lab/blot-analysis',
    titleKey: 'blotAnalysis.title',
    subtitleKey: 'blotAnalysis.subtitle',
    gradient: 'linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)',
    whatsNew: true,
  },
  protis: {
    basePath: '/lab/protis',
    titleKey: 'blotAnalysis.modules.protis.title',
    subtitleKey: 'blotAnalysis.modules.protis.subtitle',
    gradient: 'linear(135deg, cyan.700 0%, cyan.500 50%, teal.300 100%)',
    whatsNew: false,
  },
}

interface BlotAnalysisWorkspaceProps {
  caseId?: string
  /**
   * Le module qui ouvre l'atelier. IRIS par défaut.
   *
   * PROTIS réutilise tout : dépôt, cas, galerie, profils, export, partage.
   * Seul son catalogue diffère — des gels de recherche, pas des tests de
   * fabricant — d'où ce seul paramètre plutôt qu'un second atelier.
   */
  module?: 'iris' | 'protis'
}

/**
 * `fetch()` itself throws a bare `TypeError` (message: "Failed to fetch" in
 * Chrome, "NetworkError when attempting to fetch resource." in Firefox) on
 * ANY transport-level failure — lost wifi mid-upload, a proxy/nginx timeout
 * on a large scan, the backend restarting mid-request. Surfacing that raw
 * message verbatim (as the catch blocks below used to) reads as gibberish to
 * an operator and gives no idea whether the file actually got through — a
 * pilot reported seeing exactly this ("erreur ... fetch...") after uploading
 * a scan. Clean HTTP error responses (bad request, server error with a real
 * status) are NOT TypeErrors and keep their own specific message untouched.
 */
function describeUploadError(error: unknown, fallback: string): string {
  if (error instanceof TypeError) {
    return "Connexion perdue pendant l'envoi du fichier. Vérifiez votre réseau et réessayez — si le fichier était volumineux, l'analyse a pu tout de même se terminer côté serveur : vérifiez vos cas récents avant de le renvoyer, pour éviter un doublon."
  }
  return error instanceof Error ? error.message : fallback
}

/**
 * Le fichier que le navigateur tient en local après un dépôt n'est PAS
 * toujours l'image que le pipeline a mesurée : un PDF est rastérisé côté
 * serveur et toutes ses pages sont empilées en une seule image haute (voir
 * ensurePreview/stackPdfPages côté back, stack_pdf_pages côté IA), et
 * certains scanners livrent du TIFF non compressé — que le navigateur ne
 * sait pas décoder — sous un nom .png. Les boîtes étant exprimées dans les
 * coordonnées de l'image empilée, les tracer sur le fichier local les décale
 * et ne montre au mieux que sa première page : il faut alors aller chercher
 * l'image du serveur.
 */
// Trace sur l'image brute le trait de collage que l'analyse a retenu. Outil de
// mise au point de la détection, laissé en place et éteint.
const SHOW_MOUNTING_LINE = false

function needsServerSourceImage(filename?: string | null, pageCount?: number): boolean {
  return (pageCount || 1) > 1 || /\.(pdf|tiff?)$/i.test(filename || '')
}

export default function BlotAnalysisWorkspace({ caseId, module = 'iris' }: BlotAnalysisWorkspaceProps) {
  // Déclaré avant tout le reste : l'adresse de base sert dans les navigations
  // et dans le garde de départ, tous deux posés plus haut dans le composant.
  const brand = MODULE_BRAND[module]

  /**
   * UN MODULE QUI N'OFFRE QU'UNE ANALYSE N'A PAS DE CATALOGUE À PRÉSENTER.
   *
   * IRIS en compte une vingtaine, et son catalogue — avec sa recherche — est
   * le bon point d'entrée. PROTIS n'en a qu'une : on arrivait sur un écran à
   * une seule tuile, et la tuile cliquée jetait aussitôt sur l'écran de dépôt.
   * On ouvre donc PROTIS sur SES cas, l'analyse étant choisie d'office, et le
   * dépôt reste à un clic par « Nouvelle analyse ».
   *
   * Qu'une deuxième entrée PROTIS arrive et le catalogue reparaît de lui-même,
   * sans rien changer ici.
   */
  const soleBlot = useMemo(() => {
    const mine = IMMUNOBLOTS.filter((b) => (b.module ?? 'iris') === module)
    return mine.length === 1 ? mine[0] : null
  }, [module])
  const navigate = useNavigate()
  // Entry-point tag — when set to 'admin-pilots' the back button returns to
  // /admin/pilots instead of the regular Recent Cases list. Lets supervisors
  // dive into a pilot's case without losing their place in the dashboard.
  const location = useLocation()
  const cameFromAdminPilots = (location.state as { from?: string } | null)?.from === 'admin-pilots'
  // Sticky across renders / case loads — once we entered via /admin/pilots
  // we want every back action to return there, even after URL replaces.
  const cameFromAdminPilotsRef = useRef(cameFromAdminPilots)
  if (cameFromAdminPilots) cameFromAdminPilotsRef.current = true
  // Venu du registre d'évaluation : la flèche de retour y RAMÈNE, au lieu de
  // déposer dans « Analyses Western Blot Récentes » d'où il faut re-cliquer
  // Registre. Même mécanique collante que le flux admin — l'état de
  // navigation se perd aux remplacements d'adresse, la ref survit tant que
  // l'atelier reste monté.
  const cameFromRegistry = (location.state as { from?: string } | null)?.from === 'registry'
  const cameFromRegistryRef = useRef(cameFromRegistry)
  if (cameFromRegistry) cameFromRegistryRef.current = true
  const toast = useToast()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setContext, isOpen: isAiChatOpen } = useAIChat()
  const chuConfig = useCHUConfig()
  const chuFeatures = useCHUFeatures()
  const { data: currentUser } = useMyProfileWithModules()
  const userPlan = currentUser?.plan
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'
  // AI interpretation unlocked only on Premium or Enterprise (or admin)
  const aiAccessible = isAdmin || userPlan === PlanType.PREMIUM || userPlan === PlanType.ENTERPRISE
  const isPremiumLocked = !aiAccessible
  // Per-blot pricing — aligns Blot with ALMA's per-image model. A "case" can
  // contain anywhere from 2 (Schisto/Toxocara) to 14 (Toxo) blots, and the
  // pipeline cost scales with the number of blots, so per-blot is fairer than
  // per-case. Premium pays double per blot for the extra AI classification.
  const creditCostPerBlot = (userPlan === PlanType.PREMIUM || isAdmin) ? 2 : 1
  const { isOpen: isCollabOpen, onOpen: onCollabOpen, onClose: onCollabClose } = useDisclosure()
  // "Nouveautés" (what's new) — auto-opens once per release, reopened via the
  // header UPDATED badge. `whatsNewUnseen` drives the badge's attention pulse.
  const { isOpen: isWhatsNewOpen, onOpen: onWhatsNewOpen, onClose: onWhatsNewCloseRaw } = useDisclosure()
  const [whatsNewUnseen, setWhatsNewUnseen] = useState(() => !hasSeenBlotWhatsNew())
  const onWhatsNewClose = useCallback(() => { setWhatsNewUnseen(false); onWhatsNewCloseRaw() }, [onWhatsNewCloseRaw])
  // Auto-open once on mount when this release hasn't been acknowledged yet.
  useEffect(() => {
    if (!hasSeenBlotWhatsNew()) onWhatsNewOpen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pilot feedback micro-survey — asked once per user (server-tracked), after
  // they've engaged with a few cases, and never on top of the What's New modal.
  // "Plus tard" snoozes for a week (localStorage); submitting marks it done
  // server-side so it never returns.
  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackCloseRaw } = useDisclosure()
  const feedbackContextRef = useRef<Record<string, unknown>>({})
  const FEEDBACK_SNOOZE_KEY = 'careflai.blotFeedback.snoozeUntil'
  const onFeedbackClose = useCallback(() => {
    try { localStorage.setItem(FEEDBACK_SNOOZE_KEY, String(Date.now() + 7 * 24 * 3600 * 1000)) } catch { /* ignore */ }
    onFeedbackCloseRaw()
  }, [onFeedbackCloseRaw])
  const onFeedbackSubmitted = useCallback(() => {
    try { localStorage.setItem(FEEDBACK_SNOOZE_KEY, String(Number.MAX_SAFE_INTEGER)) } catch { /* ignore */ }
    onFeedbackCloseRaw()
  }, [onFeedbackCloseRaw])

  // Manual "Signaler un bug" — reachable from the "Astuces d'édition" popover,
  // unlike the feedback survey above which opens itself. Can be sent as many
  // times as the user likes.
  const { isOpen: isBugReportOpen, onOpen: onBugReportOpen, onClose: onBugReportClose } = useDisclosure()
  // Relancer l'analyse efface tout le travail manuel du cas : la confirmation
  // propose aussi l'action non destructive que l'opérateur cherche la plupart
  // du temps — remettre les bandelettes supprimées sans rien relancer.
  const { isOpen: isReanalyzeOpen, onOpen: onReanalyzeOpen, onClose: onReanalyzeClose } = useDisclosure()
  const stayCancelRef = useRef<HTMLButtonElement>(null)
  const emptyBenchCancelRef = useRef<HTMLButtonElement>(null)
  const leaveRetroCancelRef = useRef<HTMLButtonElement>(null)
  const reanalyzeCancelRef = useRef<HTMLButtonElement>(null)
  // Admin : le sélecteur de config CHU (outil de test) survit d'un cas à
  // l'autre — testé Nancy le matin, rouvert un cas Bordeaux l'après-midi, une
  // relance repartait silencieusement sous la config Nancy. Quand la config
  // chargée diffère de celle du premier run du cas, la confirmation propose
  // les deux ; par défaut on garde celle du cas.
  const [reanalyzeProfileChoice, setReanalyzeProfileChoice] = useState<'case' | 'loaded'>('case')
  const [restoringHidden, setRestoringHidden] = useState(false)
  // Incrémenté pour demander à la galerie de vider son propre ensemble de
  // bandelettes masquées : son effet de synchronisation ne fait qu'ajouter
  // (pour ne jamais perdre une bandelette qu'elle vient de masquer), donc
  // repasser la liste à vide côté parent ne suffit pas à les faire revenir.
  const [restoreHiddenNonce, setRestoreHiddenNonce] = useState(0)
  useEffect(() => {
    // Don't stack on the What's New modal, and respect a "Plus tard" snooze.
    if (!hasSeenBlotWhatsNew()) return
    let snoozeUntil = 0
    try { snoozeUntil = Number(localStorage.getItem(FEEDBACK_SNOOZE_KEY)) || 0 } catch { /* ignore */ }
    if (Date.now() < snoozeUntil) return
    const { accessToken } = getSavedAuthData()
    if (!accessToken) return
    fetch(`${process.env.REACT_APP_API_DOMAIN || ''}/api/feedback/status`, {
      headers: { Authorization: accessToken },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || data.submitted) return
        if ((data.engagedCaseCount ?? 0) < 3) return
        feedbackContextRef.current = { engagedCaseCount: data.engagedCaseCount }
        onFeedbackOpen()
      })
      .catch(() => { /* silent — feedback is best-effort */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Collapsible "recent analyses" tucked under the upload step (so choosing a
  // test lands on the upload, not on the history).
  const { isOpen: isRecentOpen, onToggle: onToggleRecent } = useDisclosure()
  // Source image collapsed by default in the results view → land on the gallery;
  // reopen via the toggle (or automatically while drawing a manual blot).
  const { isOpen: isSourceOpen, onToggle: onToggleSource } = useDisclosure()
  const [operatorValidation] = useState<{ name: string; at: string } | null>(null)
  const [caseComment, setCaseComment] = useState('')
  // Manual result per blot — used when AI is not accessible
  const [manualBlotResults, setManualBlotResults] = useState<Record<number, ManualResult>>({})
  const [blotComments, setBlotComments] = useState<Record<number, string>>({})
  const [placedBandsForExport, setPlacedBandsForExport] = useState<Record<number, Array<{name: string; yFraction: number}>>>({})

  // View state
  // When the workspace mounts on a case URL (direct link / refresh), start in
  // case-details so we never flash the module-selection screen before the case
  // loads. A loading gate (below) covers the fetch.
  const [viewMode, setViewMode] = useState<ViewMode>(
    caseId ? 'case-details' : soleBlot ? 'recent-cases' : 'immunoblot-selection',
  )

  // Onboarding tour (auto on first visit, also re-launchable via "?" in header).
  // The phase is lifted here so it survives the workspace's two return branches
  // (selection view vs. all other views) — otherwise switching view would
  // unmount the tour and reset progress mid-walkthrough.
  // Phase semantics live in BlotOnboardingTour.tsx: -1 closed, 0 welcome modal, 1..N coachmarks, N+1 final modal.
  const [tourPhase, setTourPhase] = useState<number>(-1)

  // Immunoblot state
  const [selectedImmunoBlot, setSelectedImmunoBlot] = useState<ImmunoBlot | null>(soleBlot)
  // Raw pathology_label to pre-select in the gallery's pathology tabs when a
  // case is opened FROM a pathology-filtered recent-cases list (e.g. clicking
  // a multitest case under "Leishmania" should land scoped to just its
  // Leishmania strips, not the full unfiltered case) — see handleRecentCaseSelect.
  const [initialGalleryPathologyFilter, setInitialGalleryPathologyFilter] = useState<string | null>(null)
  const [immunoblotConfig, setImmunoblotConfig] = useState<ImmunoblotConfig | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  // Bordeaux/Dynablot multitest: one config per DISTINCT raw pathology_label
  // found on this case's strips (resolved to a catalog entry — see
  // resolvePathologyLabel), keyed by that raw label. The gallery swaps this in
  // for `immunoblotConfig` while its pathology filter is active — see its
  // `immunoblotConfig` useMemo. Stays empty for every non-multitest case.
  const [pathologyConfigMap, setPathologyConfigMap] = useState<Record<string, ImmunoblotConfig | null>>({})
  // Mirrors the gallery's active pathology filter (see onPathologyFilterChange
  // below) so export can restrict to it instead of exporting the whole case.
  const [activePathologyFilter, setActivePathologyFilter] = useState<string | null>(null)
  const [blotInterpretations, setBlotInterpretations] = useState<BlotInterpretation[]>([])

  // Upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [sourceImageDataUrl, setSourceImageDataUrl] = useState<string | null>(null)

  // Analysis state
  const [analyzing, setAnalyzing] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<NewBlotAnalysisResult | null>(null)
  // Control-line offset agreed by the plate — same computation as the gallery
  // (see plateControlLineOffset). The marker drawn on the source image is what
  // guides box repositioning: had it kept the per-strip scan while the gallery
  // followed the consensus, the two views would point at two different places
  // on the same strip.
  const plateControlOffset = useMemo(
    () => plateControlLineOffset(((analysisResult?.blots ?? []) as any[]).map((b) => b.profile ?? [])),
    [analysisResult?.blots],
  )
  // Share role of the current user for the loaded case ('viewer' | 'editor' | null).
  const [myShareRole, setMyShareRole] = useState<'viewer' | 'editor' | null>(null)
  // Who shared this case with the current user — null when they own it.
  // Replaces the "Inviter" button with "Invité(e) par X" for a recipient.
  const [mySharedBy, setMySharedBy] = useState<{ contactName: string | null; email: string; companyName: string | null } | null>(null)

  // ── Admin-shadow read-only mode ────────────────────────────────────────
  // When an admin opens a case that doesn't belong to them, we lock every
  // write surface (verdict, drag/rename of bands, reanalyze, add-blot) and
  // surface a yellow banner so the supervisor can browse the case without
  // accidentally mutating the pilot's data. Export stays enabled (read-only).
  const caseOwnerId = (analysisResult as any)?.userId ?? null
  const caseOwnerInfo = (analysisResult as any)?.user ?? null
  const isOwner = caseOwnerId != null && caseOwnerId === currentUser?.id
  const isAdminShadow = isAdmin && caseOwnerId != null && !isOwner
  // A case shared with the current user: 'editor' can modify, 'viewer' is
  // read-only (the backend enforces this too; here we just match the UI).
  const isShareViewer = !isOwner && myShareRole === 'viewer'
  const isReadOnly = isAdminShadow || isShareViewer

  // Case viewing state
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false)

  // Blot boxes overlay state
  const [showBlotBoxes, setShowBlotBoxes] = useState(false)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
  const sourceImageRef = useRef<HTMLImageElement>(null)

  // imageDimensions gates the blot-box overlay (no dimensions, no boxes).
  // Measured here off a throwaway Image() probe instead of the visible
  // <img>'s own onLoad: that element sits inside a Collapse that may not even
  // mount it before the user opens the source panel, and separately, a
  // browser-cached image can finish loading before React attaches the
  // listener at all — either way onLoad silently never fires. A fresh
  // Image() object's onload always fires for ITS OWN load, cached or not,
  // and doesn't depend on whether the visible <img> is mounted yet.
  useEffect(() => {
    if (!sourceImageDataUrl || sourceImageDataUrl === 'error') return
    const probe = new window.Image()
    probe.onload = () => setImageDimensions({ width: probe.naturalWidth, height: probe.naturalHeight })
    probe.src = sourceImageDataUrl
  }, [sourceImageDataUrl])

  // Pas de navigateur de pages : l'image d'un cas multi-pages (PDF, voir
  // page_count) s'affiche en entier, d'un seul bloc déroulant. Le pager qui
  // la découpait page par page a été retiré — les bandelettes se lisent mieux
  // à la suite.

  // Debug ROI state
  const [debugRoiImage, setDebugRoiImage] = useState<string | null>(null)
  const [showDebugRoi, setShowDebugRoi] = useState(false)
  const [loadingDebugRoi, setLoadingDebugRoi] = useState(false)
  const [reanalyzing, setReanalyzing] = useState(false)
  const [blotGalleryKey, setBlotGalleryKey] = useState(0)
  const [sourceImageRenderError, setSourceImageRenderError] = useState(false)

  // Manual blot drawing state
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [isSubmittingBlot, setIsSubmittingBlot] = useState(false)
  const [pendingSelection, setPendingSelection] = useState<{ x: number; y: number; w: number; h: number; angle: number } | null>(null)
  // Use refs for live drawing to avoid re-renders on every mousemove
  const isDrawingRef = useRef(false)
  const drawStartRef = useRef<{ x: number; y: number } | null>(null)
  const drawRectRef = useRef<HTMLDivElement>(null)

  // Drag-to-move an EXISTING blot box (reposition a false positive onto a missed
  // strip). Live position lives in `draggingBox`; `dragRef` holds the gesture
  // origin + the box geometry needed to compute the new region on drop.
  const [draggingBox, setDraggingBox] = useState<{ id: number; leftPct: number; topPct: number } | null>(null)
  const [repositioningBlotId, setRepositioningBlotId] = useState<number | null>(null)
  // Where the box was dropped — kept as the displayed position during the async
  // re-analysis so the box stays put (optimistic) instead of snapping back.
  // Bumped by the "Aidez-moi" popover to (re)start the gallery's guided tour.
  const [tourSignal, setTourSignal] = useState(0)
  const [repositionTarget, setRepositionTarget] = useState<{ id: number; leftPct: number; topPct: number; angle: number } | null>(null)
  const dragRef = useRef<{
    id: number
    startClientX: number
    startClientY: number
    startLeft: number
    startTop: number
    curLeft: number
    curTop: number
    wPct: number
    hPct: number
    angle: number
    rectW: number
    rectH: number
    moved: boolean
    // Precision gear (Shift). The gesture is rebased whenever the modifier flips
    // so engaging it mid-drag refines the current position instead of making the
    // box jump. `base*` is the origin of the current gear, `start*` stays the
    // gesture origin and is what the click-vs-drag threshold reads.
    fine: boolean
    baseClientX: number
    baseClientY: number
    baseLeft: number
    baseTop: number
    // The box DOM node — moved directly during the gesture (no React re-render
    // per pointermove, which was laggy on this large component).
    el: HTMLElement | null
    // True while the pointer hovers the delete zone — dropping there deletes the
    // blot (ghost) instead of repositioning it.
    overTrash: boolean
  } | null>(null)
  // Delete zone shown during a box drag; highlights when the box is over it.
  const trashRef = useRef<HTMLDivElement | null>(null)
  const [overTrash, setOverTrash] = useState(false)
  // Edit (move/resize) mode for the pending selection
  type EditMode = 'move' | 'rotate' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
  const editModeRef = useRef<EditMode | null>(null)
  const editAnchorRef = useRef<{ px: number; py: number; sel: { x: number; y: number; w: number; h: number; angle: number } } | null>(null)
  // Precision gear for the pending selection, mirroring the existing-box drag:
  // `editFineRef` tracks whether Shift is held and `editLastSelRef` keeps the last
  // computed geometry so the anchor can be rebased on the flip without a jump.
  const editFineRef = useRef(false)
  const editLastSelRef = useRef<{ x: number; y: number; w: number; h: number; angle: number } | null>(null)

  // Le cas avait-il déjà montré IRIS avant d'être basculé ? L'aveugle repose
  // alors sur la parole du biologiste, ce que la lecture enregistre.
  const [switchedAfterReveal, setSwitchedAfterReveal] = useState(false)

  const apiDomain = useMemo(() => {
    return process.env.REACT_APP_API_DOMAIN || ''
  }, [])

  // Avancement affiché pendant l'analyse. Un seul minuteur, ici, pour que le
  // bouton et le panneau nomment la même étape au même instant.
  const analysisProgress = useAnalysisProgress(
    analyzing || reanalyzing,
    /\.pdf$/i.test((analyzing ? uploadedFile?.name : analysisResult?.filename) ?? ''),
    reanalyzing,
  )

  const { mutateAsync: consumeCredits } = useConsumeCredits()
  const { mutateAsync: checkCredits } = useCheckCredits()

  /**
   * Charge l'image source depuis l'endpoint authentifié /api/uploads/* —
   * c'est-à-dire l'image telle que le pipeline l'a vue (PDF rastérisé, pages
   * empilées, TIFF converti), seule géométrie sur laquelle les boîtes
   * tombent juste. Utilisé à l'ouverture d'un cas ET juste après une analyse,
   * où l'on affichait le fichier local : les boîtes étaient décalées et seule
   * la première page apparaissait jusqu'à ce qu'un rafraîchissement repasse
   * par ici (signalé).
   */
  const loadSourceImageFromServer = useCallback(async (fileUrl?: string | null) => {
    // La révocation passe par le setter : sans cela ce callback devrait lire
    // sourceImageDataUrl et se recréer à chaque changement d'image.
    const swapIn = (next: string | null) => {
      setSourceImageDataUrl(prev => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
        return next
      })
      // Les dimensions mesurées sont celles de l'image précédente : tant que
      // la nouvelle n'est pas mesurée, aucune boîte ne doit être tracée.
      if (next !== 'error') setImageDimensions(null)
      setSourceImageRenderError(next === 'error')
    }

    if (!fileUrl) {
      swapIn('error')
      return
    }

    // Effacer AVANT la requête (le panneau affiche alors son spinner) : garder
    // l'image précédente — celle du cas d'avant, ou le fichier local qui n'est
    // pas la même image — revient à tracer les boîtes du nouveau cas sur une
    // géométrie qui n'est pas la leur, le temps du chargement.
    swapIn(null)

    try {
      const blob = await fetchSourceImageBlob(apiDomain, fileUrl)
      swapIn(blob ? URL.createObjectURL(blob) : 'error')
    } catch (_e) {
      swapIn('error')
    }
  }, [apiDomain])

  // Handler pour upload de fichier (SANS analyse automatique)
  const handleFileUploaded = async (file: File) => {
    setUploadedFile(file)
    setHasResults(false)
    setAnalysisResult(null)
    // These are keyed by numeric blot id, assigned sequentially (1, 2, 3…) per
    // case — a brand-new analysis reuses those same small ids, so without this
    // reset a previous case's validations/comments/placed bands "reappeared"
    // on the new one's blots (same id, different case). Only reachable here
    // (not e.g. handleNewAnalysis, which can bounce back to the catalog
    // without ever reaching a new upload) guarantees it always fires before
    // any new case's blots can render against a stale map.
    setManualBlotResults({})
    setBlotComments({})
    setPlacedBandsForExport({})

    // URL.createObjectURL évite de créer une string base64 de 100+ MB pour les gros TIFF
    if (sourceImageDataUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(sourceImageDataUrl)
    }
    setSourceImageDataUrl(URL.createObjectURL(file))
    setSourceImageRenderError(false)
    // No toast: the file preview appearing is confirmation enough.
  }

  // Handler pour lancer l'analyse MANUELLEMENT
  /**
   * Les bandelettes de la recherche affichée qui attendent encore leur lecture.
   *
   * Un seul calcul pour les deux bandeaux, sinon ils se contredisent : on a vu
   * « votre lecture est enregistrée » et « votre lecture de paillasse, avant
   * IRIS » affichés ensemble, l'un tiré du verrou de la plaque et l'autre d'un
   * drapeau du serveur qui ne parlait pas de la même chose.
   */
  const benchPending = useMemo(() => {
    if ((analysisResult as any)?.evaluationMode !== 'retrospective') return { all: [], inFilter: [] }
    const bench = (analysisResult as any)?.benchReading
    const wholeLocked = Boolean(bench?.lockedAt && !Object.values(bench?.blots ?? {})
      .some((e: any) => e?.lockedAt))
    const locked = new Set(
      Object.entries(bench?.blots ?? {})
        .filter(([, e]: [string, any]) => wholeLocked || e?.lockedAt)
        .map(([id]) => Number(id)),
    )
    const hidden = new Set((analysisResult as any)?.hiddenBlotIds ?? [])
    const controls = new Set([
      (analysisResult as any)?.selectedPositiveControlId,
      (analysisResult as any)?.selectedPositiveControl2Id,
      (analysisResult as any)?.selectedPositiveControl3Id,
      ...Object.values((analysisResult as any)?.positiveControlsByPathology ?? {})
        .flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id]),
    ].filter(Boolean))
    const awaiting = (analysisResult?.blots ?? []).filter(
      (b: any) => !hidden.has(b.id) && !controls.has(b.id) && !locked.has(b.id),
    )
    return {
      // Toute la plaque : ce que le garde de sortie doit ignorer.
      all: awaiting,
      // La recherche affichée : ce que les bandeaux et le bouton pilotent.
      inFilter: awaiting.filter(
        (b: any) => !activePathologyFilter || b.pathology_label === activePathologyFilter,
      ),
    }
  }, [analysisResult, activePathologyFilter])

  /**
   * Combien de bandelettes d'échantillon attendent encore un verdict.
   *
   * Les témoins n'en portent pas — ce sont des contrôles — et les bandelettes
   * supprimées par le biologiste non plus. Compté ici, au niveau du poste de
   * travail, parce que le garde de sortie en a besoin bien avant que la
   * colonne d'actions ne le recalcule pour la porte d'export.
   */
  const pendingValidationCount = useMemo(() => {
    if (!analysisResult?.blots?.length) return 0
    // Une bandelette encore voilée n'est pas validable — IRIS ne lui est pas
    // dévoilé — mais les AUTRES le sont. Taire le garde pour toute la plaque
    // dès qu'une lecture est en attente l'éteignait sur une plaque multitest
    // à moitié lue, c'est-à-dire précisément là où il sert.
    const awaitingBench = new Set(benchPending.all.map((b: any) => b.id))
    const hidden = new Set((analysisResult as any).hiddenBlotIds ?? [])
    const controls = new Set([
      (analysisResult as any).selectedPositiveControlId,
      (analysisResult as any).selectedPositiveControl2Id,
      (analysisResult as any).selectedPositiveControl3Id,
      ...Object.values((analysisResult as any).positiveControlsByPathology ?? {})
        .flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id]),
    ].filter(Boolean))
    return analysisResult.blots.filter(
      (b: any) => !hidden.has(b.id)
        && !controls.has(b.id)
        && !awaitingBench.has(b.id)
        && manualBlotResults[b.id] == null,
    ).length
  }, [analysisResult, manualBlotResults, benchPending])

  /**
   * Le départ que l'opérateur vient d'accorder, et où il mène.
   *
   * « Nouvelle analyse » ne navigue pas de lui-même : il remettait l'écran à
   * zéro sur place. Mais remettre à zéro, c'est poser `hasResults = false`
   * alors que l'identifiant du cas est encore dans l'adresse — et c'est
   * précisément la condition qui réveille le chargeur automatique. Il
   * refaisait donc un aller-retour serveur et rouvrait le cas que l'on
   * venait de quitter.
   *
   * On passe donc par l'adresse, comme le retour et « Modifier » : ce drapeau
   * désarme le garde, l'effet ci-dessous demande l'adresse, et la remise à
   * zéro se fait en réaction à l'adresse une fois le départ effectif.
   */
  const [pendingDeparture, setPendingDeparture] = useState<'new-analysis' | null>(null)

  // Ne retient que sur un cas ouvert, et jamais en lecture seule : un admin qui
  // regarde le cas d'un pilote n'a rien à y valider.
  const leaveGuard = useLeaveGuard(
    // Un départ ACCORDÉ désarme le garde, le temps que l'adresse change.
    // Sans cela « Quitter quand même » demandait une adresse que le garde
    // refusait aussitôt — la question se reposait, et l'identifiant restait
    // dans l'URL le temps que le chargeur automatique rouvre le cas.
    !pendingDeparture && viewMode === 'case-details' && !isReadOnly && pendingValidationCount > 0,
    // Quitter, c'est changer d'adresse ALORS QU'ON EST sur la fiche d'un cas.
    // La redirection qui suit une analyse part de l'adresse du module, sans
    // identifiant : elle n'est donc pas un départ, et ne déclenche rien.
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname.startsWith(`${brand.basePath}/`)
      && currentLocation.pathname.length > brand.basePath.length + 1
      && nextLocation.pathname !== currentLocation.pathname,
  )

  /**
   * La configuration de la recherche AFFICHÉE, et non celle du cas.
   *
   * Une plaque multitest est enregistrée sous « DYNABLOT MULTITEST », qui ne
   * déclare aucune bande : déduire une conclusion avec elle rendait `null`
   * partout, et le tableau de comparaison n'avait ni colonne de bande ni
   * verdict. Chaque bandelette porte sa propre recherche, c'est de là que la
   * notice doit venir.
   *
   * Hors multitest, `activePathologyFilter` est nul et c'est la configuration
   * du cas qui sert, inchangée.
   */
  const [filterConfig, setFilterConfig] = useState<ImmunoblotConfig | null>(null)
  useEffect(() => {
    if (!activePathologyFilter) {
      setFilterConfig(null)
      return
    }
    const resolved = resolvePathologyLabel(activePathologyFilter)
    if (!resolved?.name) {
      setFilterConfig(null)
      return
    }
    let cancelled = false
    getImmunoblotConfigsByPathology(resolved.name)
      .then((r) => {
        if (!cancelled) setFilterConfig(r.data?.data?.[0] ?? null)
      })
      .catch(() => { if (!cancelled) setFilterConfig(null) })
    return () => { cancelled = true }
  }, [activePathologyFilter])

  /** La notice à appliquer ici : celle de la recherche affichée, sinon celle
   *  du cas. */
  const activeConfig = filterConfig ?? immunoblotConfig

  /**
   * Les bandelettes portent-elles leur propre recherche ?
   *
   * Sur une plaque multitest réelle, le libellé de chaque bandelette est lu
   * sur le tableau du rapport PDF, et sa notice arrive dès qu'on choisit une
   * recherche — même si, vue sur « Toutes », aucune configuration n'est
   * active. C'est ce qui distingue une plaque mixte normale d'un cas lancé
   * sous « DYNABLOT MULTITEST » par erreur, où rien ne porte de recherche et
   * où aucune notice ne pourra jamais s'appliquer.
   */
  const hasPathologyLabels = useMemo(
    () => (analysisResult?.blots ?? []).some((b: any) => !!b?.pathology_label),
    [analysisResult?.blots],
  )

  // Les bandes cochées à la paillasse, par bandelette. Tenues ici parce que la
  // galerie n'est qu'un affichage : c'est ce parent qui verrouille.
  const [benchBands, setBenchBands] = useState<Record<number, string[]>>({})
  const [lockingBench, setLockingBench] = useState(false)

  const handleBenchBandToggle = useCallback((blotId: number, band: string) => {
    setBenchBands((prev) => {
      const current = prev[blotId] ?? []
      return {
        ...prev,
        [blotId]: current.includes(band)
          ? current.filter((b) => b !== band)
          : [...current, band],
      }
    })
  }, [])

  /**
   * Ce qui manque avant de pouvoir figer la comparaison.
   *
   * Mêmes conditions que l'export, et pour la même raison : une concordance se
   * mesure contre une lecture finie. Tant qu'une bandelette n'a pas de verdict,
   * ce qu'on figerait d'IRIS n'est pas ce que le biologiste a retenu ; tant
   * qu'un identifiant est incomplet, la ligne de la comparaison ne désigne
   * personne. Et l'instantané, lui, ne se reprend pas.
   *
   * Les identifiants ne sont exigés que des ÉCHANTILLONS : un témoin ne porte
   * pas d'identifiant patient — même règle que la porte d'export.
   */
  const snapshotReadiness = useMemo(() => {
    const hidden = new Set((analysisResult as any)?.hiddenBlotIds ?? [])
    const frozen = new Set(Object.keys((analysisResult as any)?.irisSnapshot?.blots ?? {}).map(Number))
    const controls = new Set([
      (analysisResult as any)?.selectedPositiveControlId,
      (analysisResult as any)?.selectedPositiveControl2Id,
      (analysisResult as any)?.selectedPositiveControl3Id,
      ...Object.values((analysisResult as any)?.positiveControlsByPathology ?? {})
        .flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id]),
    ].filter(Boolean))
    // Limité à la recherche AFFICHÉE, et aux bandelettes pas encore figées.
    // Sur une plaque multitest, une recherche se termine avant les autres :
    // exiger les 43 bandelettes de la plaque pour figer les 4 de l'Aspergillus
    // reviendrait à ne rien pouvoir figer avant la toute fin.
    const samples = (analysisResult?.blots ?? []).filter(
      (b: any) => !hidden.has(b.id)
        && !controls.has(b.id)
        && !frozen.has(b.id)
        && (!activePathologyFilter || b.pathology_label === activePathologyFilter),
    )
    const missingIds = samples.filter((b: any) => !isCompleteStripId(b.strip_id)).length
    const pending = samples.filter((b: any) => manualBlotResults[b.id] == null).length
    return {
      scope: samples.map((b: any) => b.id),
      missingIds,
      pending,
      blockedReason: pending > 0
        ? `Validez les ${pending > 1 ? `${pending} bandelettes` : 'bandelettes'} restantes : une concordance se mesure contre une lecture finie.`
        : missingIds > 0
          ? `Complétez l’identifiant de ${missingIds} bandelette${missingIds > 1 ? 's' : ''} : sans lui, la ligne de comparaison ne désigne personne.`
          : null,
    }
  }, [analysisResult, manualBlotResults, activePathologyFilter])

  /**
   * Ce qui manque avant de pouvoir verrouiller, et ce qui ne manque pas.
   *
   * Le témoin est un préalable dur : sans lui, IRIS n'a aucune référence pour
   * situer les bandes, il n'identifie rien, et la comparaison se fige sur un
   * IRIS négatif partout — exactement le tableau à 0 % qu'on a obtenu une fois.
   * Tant qu'il n'est pas désigné, le verrouillage est refusé.
   *
   * Une plaque sans aucune bande cochée, en revanche, n'est PAS une erreur :
   * c'est une plaque entièrement négative, et cela existe. La bloquer rendrait
   * ces plaques-là impossibles à saisir. Elle demande donc confirmation, ce qui
   * arrête le clic distrait sans interdire le cas légitime.
   */
  const benchReadiness = useMemo(() => {
    const controls = [
      (analysisResult as any)?.selectedPositiveControlId,
      (analysisResult as any)?.selectedPositiveControl2Id,
      (analysisResult as any)?.selectedPositiveControl3Id,
      ...Object.values((analysisResult as any)?.positiveControlsByPathology ?? {})
        .flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id]),
    ].filter(Boolean)
    const checked = Object.values(benchBands).reduce((n, bands) => n + bands.length, 0)
    return {
      hasControl: controls.length > 0,
      checkedBands: checked,
      blockedReason: controls.length === 0
        ? 'Désignez d’abord le témoin positif : sans lui, IRIS n’a aucune référence pour situer les bandes.'
        : null,
    }
  }, [analysisResult, benchBands])

  const [confirmEmptyBench, setConfirmEmptyBench] = useState(false)
  const [confirmLeaveRetrospective, setConfirmLeaveRetrospective] = useState(false)

  /**
   * Fige la lecture de paillasse, et lève le voile du même geste.
   *
   * Porte sur TOUTES les bandelettes d'échantillon, y compris celles où rien
   * n'a été coché : une bandelette sans bande est une lecture négative, pas
   * une lecture manquante, et exiger un geste pour dire « je n'ai rien vu »
   * ferait abandonner le recueil.
   */
  const handleLockBenchReading = useCallback(async (confirmedEmpty = false) => {
    if (!selectedCaseId || !analysisResult) return
    if (!benchReadiness.hasControl) return
    if (benchReadiness.checkedBands === 0 && !confirmedEmpty) {
      setConfirmEmptyBench(true)
      return
    }
    const controlIds = new Set([
      (analysisResult as any).selectedPositiveControlId,
      (analysisResult as any).selectedPositiveControl2Id,
      (analysisResult as any).selectedPositiveControl3Id,
      ...Object.values((analysisResult as any).positiveControlsByPathology ?? {})
        .flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id]),
    ].filter(Boolean))
    // Exactement les bandelettes que les bandeaux annoncent comme en attente :
    // un seul calcul, sinon le bouton verrouille autre chose que ce qui est
    // affiché.
    const samples = benchPending.inFilter
    if (!samples.length) {
      toast({
        title: 'Rien à verrouiller ici',
        description: activePathologyFilter
          ? 'Les bandelettes de cette recherche sont déjà verrouillées.'
          : 'Aucune bandelette d’échantillon à saisir.',
        status: 'warning',
        duration: 4000,
      })
      return
    }
    setLockingBench(true)
    try {
      const blots = Object.fromEntries(samples.map((b: any) => {
        const bands = benchBands[b.id] ?? []
        return [b.id, { bands, conclusion: verdictFromBands(activeConfig as any, bands) }]
      }))
      const { data } = await axiosClient.post(
        `/blot-analysis/case/${selectedCaseId}/bench-reading/lock`,
        {
          blots,
          blinding: switchedAfterReveal ? 'declared' : 'enforced',
          rulesVersion: (activeConfig as any)?.pathology ?? null,
        },
      )
      setSwitchedAfterReveal(false)
      toast({
        title: 'Lecture verrouillée',
        description: 'Les résultats d’IRIS sont maintenant visibles.',
        status: 'success',
        duration: 4000,
      })
      // Fusion, et non rechargement complet du cas.
      //
      // Passer par handleRecentCaseSelect rejoue toute l'ouverture d'un cas :
      // la galerie se remonte, et son filtre de pathologie avec elle. Le
      // biologiste qui verrouillait l'Aspergillus se retrouvait sur « Toutes »
      // et devait recliquer sa recherche pour continuer. Or le verrouillage ne
      // change que ce que le serveur accepte enfin de servir — les profils
      // dévoilés et la lecture figée. On ne reprend donc que cela.
      const fresh = data?.analysis
      if (fresh) {
        setAnalysisResult((prev) => (prev ? {
          ...prev,
          blots: fresh.blots ?? prev.blots,
          benchReading: fresh.benchReading ?? null,
          benchReadingPending: fresh.benchReadingPending === true,
          bandCalibration: fresh.bandCalibration ?? (prev as any).bandCalibration,
          placedBands: fresh.placedBands ?? (prev as any).placedBands,
          suppressedBands: fresh.suppressedBands ?? (prev as any).suppressedBands,
          manualBlotResults: fresh.manualBlotResults ?? (prev as any).manualBlotResults,
          controlLineOverrides: fresh.controlLineOverrides ?? (prev as any).controlLineOverrides,
        } as any : prev))
      }
    } catch (error) {
      console.error('Error locking bench reading:', error)
      toast({ title: 'Verrouillage impossible', status: 'error', duration: 4000 })
    } finally {
      setLockingBench(false)
    }
  }, [selectedCaseId, analysisResult, benchBands, benchPending, activeConfig, apiDomain, activePathologyFilter, switchedAfterReveal, toast])

  // Mode choisi sur l'écran d'upload, avant de lancer l'analyse. Déclaré ICI,
  // au-dessus de handleAnalyze : le callback le lit, donc il doit aussi figurer
  // dans ses dépendances — sans quoi il reste mémorisé sur la valeur du premier
  // rendu et l'interrupteur n'a aucun effet.
  const [retrospectiveUpload, setRetrospectiveUpload] = useState(false)

  const handleAnalyze = useCallback(async () => {
    if (!uploadedFile) {
      toast({
        title: t('blotAnalysis.workspace.noFile'),
        description: t('blotAnalysis.workspace.pleaseUploadImage'),
        status: 'warning',
        duration: 3000,
      })
      return
    }

    // Pre-flight credit check — we don't know yet how many blots YOLO will find,
    // so we conservatively require the worst-case minimum (2 blots, the smallest
    // real case we've seen) to refuse obviously-broke users without blocking the
    // analysis when the user actually has enough budget for the upcoming run.
    const PREFLIGHT_BLOT_ESTIMATE = 2
    try {
      const preflightAmount = creditCostPerBlot * PREFLIGHT_BLOT_ESTIMATE
      const creditCheck = await checkCredits({ amount: preflightAmount })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
    } catch (_e) {
      // Si le check échoue on laisse l'analyse continuer (fail open)
    }

    setAnalyzing(true)

    try {
      const formData = new FormData()
      formData.append('file', uploadedFile)
      // Le mode se choisit AVANT l'analyse : le cas naît voilé, et il n'y a
      // rien à défaire ensuite.
      formData.append('evaluation_mode', retrospectiveUpload ? 'retrospective' : 'prospective')
      if (selectedImmunoBlot) {
        formData.append('pathology_id', selectedImmunoBlot.id)
        formData.append('pathology_name', selectedImmunoBlot.name)
      }
      // Admin test override: force a CHU detection profile (e.g. "chu nancy") so
      // the blot service uses that profile instead of the admin's own org.
      const chuOverride = getAdminCHUOverride()
      if (chuOverride) {
        formData.append('organization', chuOverride)
      }

      const { accessToken } = getSavedAuthData()

      const response = await apiFetch(`${apiDomain}/api/blot-analysis/analyze-new`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': accessToken || '',
        },
      })

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`)
      }

      const result: NewBlotAnalysisResult & { id?: string } = await response.json()

      // No silent auto-selection of the positive control. Band pattern alone
      // can't tell a real témoin from a strongly positive patient (both show the
      // full band set), so an auto-pick was wrong more than half the time and,
      // committed silently, risked skewing every other lane's interpretation.
      // Instead the gallery highlights the best 1–2 candidates as a hint the
      // operator confirms with a click (see suggestControlCandidates there).
      // A new analysis is a new case, and blot ids restart at 1 — so whatever
      // was validated on the previous case lands straight on these blots.
      // handleFileUploaded already clears this, but re-analysing a file that
      // is still loaded never goes through it. Remounting the gallery covers
      // the per-blot state it keeps internally (placed bands, comments,
      // hidden ids, which blot is the témoin) in the same move.
      setManualBlotResults({})
      setBlotComments({})
      setPlacedBandsForExport({})
      setBlotGalleryKey((k) => k + 1)

      setAnalysisResult(result)
      setHasResults(true)
      setViewMode('case-details')
      // L'aperçu local ne convient que pour une image directement affichable
      // par le navigateur ET à page unique ; sinon on recharge celle du
      // serveur, en arrière-plan (l'image source est repliée par défaut).
      if (needsServerSourceImage(result.filename || uploadedFile?.name, result.page_count)) {
        void loadSourceImageFromServer(result.fileUrl)
      }

      // Consume credits AFTER successful analysis, scaled to the actual number of
      // blots detected by the pipeline. Falls back to 1 if YOLO returned nothing
      // (no blots → no real work, but we keep the action recorded for audit).
      const blotsCharged = Array.isArray(result.blots) ? result.blots.length : 0
      const billedAmount = Math.max(1, creditCostPerBlot * blotsCharged)
      chargeAnalysis({
        amount: billedAmount,
        action: 'blot_analysis',
        resourceId: result.id,
        resourceType: 'blot_case',
        metadata: {
          pathology: selectedImmunoBlot?.name,
          blotsCount: blotsCharged,
          costPerBlot: creditCostPerBlot,
        },
      })

      // Update URL with new case ID if available
      if (result.id) {
        setSelectedCaseId(result.id)
        navigate(`${brand.basePath}/${result.id}`, { replace: true })
      }

      toast({
        title: t('blotAnalysis.workspace.analysisComplete'),
        description: `${t('blotAnalysis.workspace.blotsDetected', { count: result.total_blots })}, ${t('blotAnalysis.workspace.totalBands', { count: result.total_bands })}`,
        status: 'success',
        duration: 5000,
      })
    } catch (error) {
      console.error('Analysis error:', error)
      toast({
        title: t('blotAnalysis.workspace.analysisError'),
        description: describeUploadError(error, t('blotAnalysis.workspace.analysisFailed')),
        status: 'error',
        duration: error instanceof TypeError ? 10000 : 5000,
        isClosable: true,
      })
    } finally {
      setAnalyzing(false)
    }
  }, [uploadedFile, selectedImmunoBlot, immunoblotConfig, apiDomain, toast, navigate, checkCredits, consumeCredits, loadSourceImageFromServer, retrospectiveUpload, t, brand.basePath])

  const handleImmunoBlotSelect = (immunoblot: ImmunoBlot) => {
    setSelectedImmunoBlot(immunoblot)
    // Land straight on the upload step — we're in the "new analysis" flow.
    // The history stays reachable via the collapsible below the upload.
    setViewMode('new-analysis')
    // No toast: landing on the upload step already shows what was selected.
  }

  // Load immunoblot configuration when immunoblot is selected
  useEffect(() => {
    if (!selectedImmunoBlot) {
      setImmunoblotConfig(null)
      return
    }

    const loadConfig = async () => {
      setLoadingConfig(true)
      try {
        const response = await getImmunoblotConfigsByPathology(selectedImmunoBlot.name)
        if (response.data.data && response.data.data.length > 0) {
          const config = response.data.data[0]
          setImmunoblotConfig(config)
          console.log('✅ Immunoblot config loaded:', config)
          // Note: Aletheia guide is available via the info button, not opened automatically
        } else {
          console.log('⚠️ No config found for pathology:', selectedImmunoBlot.name)
          setImmunoblotConfig(null)
          // Sauf pour la plaque mixte : elle n'a pas de notice propre par
          // construction, les siennes viennent de chaque bandelette.
          if (selectedImmunoBlot.id === 'dynablot-multitest') return
          // Le dire AVANT l'analyse, pas après : sans notice, les crédits sont
          // dépensés pour une détection de bandelettes qui ne donnera ni
          // suggestion de bandes ni conclusion. Averti ici, l'opérateur peut
          // encore changer de test.
          toast({
            title: 'Aucune notice pour ce test',
            description:
              'Les bandelettes seront détectées, mais sans règles de lecture : aucune bande suggérée, aucune conclusion automatique.',
            status: 'warning',
            duration: 9000,
            isClosable: true,
          })
        }
      } catch (error) {
        console.error('❌ Failed to load immunoblot config:', error)
        setImmunoblotConfig(null)
      } finally {
        setLoadingConfig(false)
      }
    }

    loadConfig()
  }, [selectedImmunoBlot, toast])

  // Bordeaux/Dynablot multitest: fetch a config per distinct pathology_label
  // found on the loaded case's strips, for the gallery's pathology filter to
  // swap in (see pathologyConfigMap above). No-op for every non-multitest case.
  useEffect(() => {
    // PROTIS : les étiquettes sont des projets, pas des recherches — aucune
    // notice à aller chercher par étiquette.
    if (module === 'protis') {
      setPathologyConfigMap({})
      return
    }
    const labels = Array.from(
      new Set(
        (analysisResult?.blots ?? [])
          .map((b) => b.pathology_label)
          .filter((l): l is string => !!l)
      )
    )
    if (labels.length === 0) {
      setPathologyConfigMap({})
      return
    }
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        labels.map(async (label) => {
          const resolved = resolvePathologyLabel(label)
          if (!resolved) return [label, null] as const
          try {
            const response = await getImmunoblotConfigsByPathology(resolved.name)
            const config = response.data.data?.[0] ?? null
            if (!config) console.log('⚠️ Multitest: no config found for resolved pathology:', resolved.name, '(raw label:', label, ')')
            return [label, config] as const
          } catch (error) {
            console.error('❌ Multitest: failed to load config for', label, error)
            return [label, null] as const
          }
        })
      )
      if (!cancelled) setPathologyConfigMap(Object.fromEntries(entries))
    })()
    return () => { cancelled = true }
  }, [analysisResult?.blots, module])

  const handleChangeImmunoBlot = useCallback(() => {
    // Rien à changer quand le module n'offre qu'une analyse : le catalogue
    // serait un écran à une tuile. On remonte au Lab Studio, qui est bien ce
    // qu'on cherche quand on veut changer de chose à faire.
    if (soleBlot) {
      navigate('/lab')
      return
    }
    // UN CAS OUVERT : L'ADRESSE D'ABORD.
    //
    // Même piège que le bouton de retour. Ce bouton vidait l'écran puis
    // demandait la navigation ; depuis qu'un garde peut retenir ce départ —
    // il reste des bandelettes sans verdict — la navigation était refusée
    // APRÈS que le cas ait disparu de l'écran. On partait au catalogue, la
    // question s'affichait par-dessus en annonçant « 0 bandelette » (le cas
    // n'existait déjà plus pour la compter), et le tout se dénouait par une
    // remise à zéro qui se lit comme un rafraîchissement de page.
    //
    // On demande donc l'adresse sans rien toucher, en disant où l'on va ; la
    // remise à zéro se fait en réaction à l'adresse, une fois le départ
    // accordé (voir l'effet « L'adresse fait foi » plus bas).
    if (selectedCaseId) {
      navigate(brand.basePath, { replace: true, state: { view: 'immunoblot-selection' } })
      return
    }
    // Sans cas ouvert — un test choisi mais pas encore analysé — il n'y a rien
    // à retenir et l'adresse ne change pas : la remise à zéro se fait ici.
    setViewMode('immunoblot-selection')
    setSelectedImmunoBlot(null)
    setUploadedFile(null)
    setHasResults(false)
    setAnalysisResult(null)
    setSourceImageDataUrl(null)
    loadedViaCaseSelectRef.current = null
    // Clear the case id from the URL — otherwise setting hasResults=false re-triggers
    // the URL auto-loader, which re-fetches the case and bounces the user back to it
    // ("Chargement…" / "Impossible de charger le cas") instead of the test catalog.
    navigate(brand.basePath, { replace: true })
  }, [navigate, selectedCaseId, brand.basePath, soleBlot])

  // « Nouvelle analyse » quitte le cas sans changer d'adresse : il remet
  // l'écran à zéro. Le garde du routeur ne le voit donc pas passer, et c'est
  // pourtant la sortie la plus courante — on finit de regarder, on enchaîne.
  // La même question est posée ici, par ce drapeau.
  const [newAnalysisBlocked, setNewAnalysisBlocked] = useState(false)

  const handleNewAnalysis = useCallback(() => {
    // If we're on new-analysis without a selected test, "new analysis" can't
    // land on the upload screen — bounce back to the catalog so the user can
    // actually pick a test first. Common state after finishing the tour.
    if (viewMode === 'new-analysis' && !selectedImmunoBlot) {
      setViewMode('immunoblot-selection')
      return
    }
    // UN CAS OUVERT : L'ADRESSE D'ABORD (voir `pendingDeparture`). On ne
    // touche à rien ici, sinon le chargeur automatique rouvre le cas avant
    // que l'adresse n'ait changé.
    if (selectedCaseId) {
      setPendingDeparture('new-analysis')
      return
    }
    setViewMode('new-analysis')
    setUploadedFile(null)
    setHasResults(false)
    setAnalyzing(false)
    setAnalysisResult(null)
    setSourceImageDataUrl(null)
    setSelectedCaseId(null)
    loadedViaCaseSelectRef.current = null
    // Clear case ID from URL
    navigate(brand.basePath, { replace: true })
  }, [navigate, viewMode, selectedImmunoBlot, selectedCaseId, brand.basePath])

  // Le départ accordé part d'ici, et pas du gestionnaire de clic : il faut un
  // rendu entre le désarmement du garde et la demande d'adresse, sinon le
  // garde — dont la closure date du rendu précédent — retient encore ce
  // départ et la question reparaît le temps d'une image.
  useEffect(() => {
    if (!pendingDeparture) return
    navigate(brand.basePath, { replace: true, state: { view: pendingDeparture } })
  }, [pendingDeparture, navigate, brand.basePath])

  // Save positive controls selection to backend (all 3 controls). When
  // pathologyLabel is set (Bordeaux/Dynablot multitest), these 3 ids belong
  // to THAT pathology's own slots instead of the case-wide ones — each
  // pathology in a mixed-self-test report has its own independent T1/T2/T3
  // (see BlotGallery's effectiveControlId / savedPositiveControlsByPathology).
  const handlePositiveControlChange = useCallback(async (
    control1Id: number | null,
    control2Id: number | null,
    control3Id: number | null,
    pathologyLabel?: string | null
  ) => {
    if (!selectedCaseId) {
      console.warn('⚠️ No case ID available to save positive controls')
      return
    }

    const payload = {
      positiveControlId: control1Id,
      positiveControl2Id: control2Id,
      positiveControl3Id: control3Id,
      ...(pathologyLabel ? { pathologyLabel } : {}),
    }

    try {
      const { accessToken } = getSavedAuthData()
      const url = `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/positive-control`
      const response = await apiFetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorBody = await response.text()
        console.error('Error saving positive controls:', response.status, errorBody)
        throw new Error(`Failed to save positive controls: ${response.statusText}`)
      }

      const result = await response.json()

      // Keep the in-memory case in sync with the PATCH. Without this, designating
      // a témoin during the session left analysisResult.selectedPositiveControlId
      // stale (null), so the control blot stayed counted as an unvalidated sample
      // in `sampleBlots` → `allValidated` never flipped → the export button
      // remained disabled until a page refresh reloaded the server copy.
      setAnalysisResult(prev => prev ? ({
        ...prev,
        selectedPositiveControlId: result.selectedPositiveControlId,
        selectedPositiveControl2Id: result.selectedPositiveControl2Id,
        selectedPositiveControl3Id: result.selectedPositiveControl3Id,
        positiveControlsByPathology: result.positiveControlsByPathology,
      } as any) : prev)
    } catch (error) {
      console.error('Error saving positive controls:', error)
      throw error // Re-throw to let BlotGallery handle the error
    }
  }, [selectedCaseId, apiDomain])

  // Handler to save band calibration
  const handleBandCalibrationChange = useCallback(async (
    controlId: number,
    calibration: Record<string, { position: number; intensity: number }> | null
  ) => {
    if (!selectedCaseId) {
      console.warn('No case ID available to save band calibration')
      return
    }

    try {
      console.log('Saving band calibration for case:', selectedCaseId, 'control:', controlId, calibration)
      const { accessToken } = getSavedAuthData()

      // Merge with existing calibrations for other controls
      const existingCalibrations = (analysisResult as any)?.bandCalibration || {}
      const updatedCalibrations = { ...existingCalibrations }

      // Clean out old flat format keys (P45, P40, P33, P31, P30) — they conflict with per-control format
      const oldFormatKeys = ['P45', 'P40', 'P33', 'P31', 'P30']
      oldFormatKeys.forEach(k => delete updatedCalibrations[k])

      if (calibration) {
        updatedCalibrations[String(controlId)] = calibration
      } else {
        delete updatedCalibrations[String(controlId)]
      }

      const url = `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/band-calibration`

      const response = await apiFetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ calibration: updatedCalibrations }),
      })

      if (!response.ok) {
        const errorBody = await response.text()
        console.error('Error response:', errorBody)
        throw new Error(`Failed to save band calibration: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('Band calibration saved successfully:', data)

      // Update local state
      if (analysisResult) {
        setAnalysisResult({
          ...analysisResult,
          bandCalibration: updatedCalibrations
        })
      }
    } catch (error) {
      console.error('Error saving band calibration:', error)
      throw error
    }
  }, [selectedCaseId, apiDomain, analysisResult])

  // Handler to save a manual control-line position override for one blot
  const handleControlLineOverrideChange = useCallback(async (
    blotId: number,
    position: number | null
  ) => {
    if (!selectedCaseId) {
      console.warn('No case ID available to save control-line override')
      return
    }

    try {
      const { accessToken } = getSavedAuthData()

      const existingOverrides = (analysisResult as any)?.controlLineOverrides || {}
      const updatedOverrides = { ...existingOverrides }

      if (position !== null) {
        updatedOverrides[String(blotId)] = position
      } else {
        delete updatedOverrides[String(blotId)]
      }

      const url = `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/control-line-overrides`

      const response = await apiFetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ overrides: updatedOverrides }),
      })

      if (!response.ok) {
        const errorBody = await response.text()
        console.error('Error response:', errorBody)
        throw new Error(`Failed to save control-line override: ${response.statusText}`)
      }

      await response.json()

      if (analysisResult) {
        setAnalysisResult({
          ...analysisResult,
          controlLineOverrides: updatedOverrides
        } as any)
      }
    } catch (error) {
      console.error('Error saving control-line override:', error)
      throw error
    }
  }, [selectedCaseId, apiDomain, analysisResult])

  /**
   * Flip one paired-comparison couple (CIP-WB mother/child, J0/J+N, …).
   *
   * The couples themselves are guessed from the printed row order, so a plate
   * mounted the other way round put the differences and the verdict on the
   * mother. Persisted per case: which strip is the child is a clinical reading,
   * it cannot be lost on a page reload.
   */
  const handlePairedRoleSwapChange = useCallback(async (
    swaps: Record<string, boolean>
  ) => {
    // Optimistic: the gallery re-labels both strips of the couple at once.
    setAnalysisResult(prev => prev ? { ...prev, pairedRoleSwaps: swaps } as any : prev)
    if (!selectedCaseId) return
    try {
      const { accessToken } = getSavedAuthData()
      const response = await apiFetch(
        `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/paired-roles`,
        {
          method: 'PATCH',
          headers: { 'Authorization': accessToken || '', 'Content-Type': 'application/json' },
          body: JSON.stringify({ swaps }),
        },
      )
      if (!response.ok) throw new Error(`Failed to save paired roles: ${response.statusText}`)
    } catch (error) {
      console.error('Error saving paired-comparison roles:', error)
    }
  }, [selectedCaseId, apiDomain])

  const handleHiddenBlotsChange = useCallback(async (hiddenIds: number[]) => {
    if (!selectedCaseId) return
    // Update local state immediately so UI reflects the change
    setAnalysisResult(prev => prev ? { ...prev, hiddenBlotIds: hiddenIds } as any : prev)
    try {
      const { accessToken } = getSavedAuthData()
      await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/hidden-blots`, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hiddenBlotIds: hiddenIds }),
      })
      // Invalidate the recent-cases cache so navigating back via sidebar
      // returns fresh data with the updated hiddenBlotIds
      queryClient.invalidateQueries({ queryKey: ['blotRecentCases'] })
      // Deleting a ghost blot refunds credits SERVER-side, so no consumeCredits
      // call runs here to invalidate the balance — without this the header kept
      // showing the pre-refund figure until a full page reload.
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] })
      queryClient.invalidateQueries({ queryKey: ['creditHistory'] })
      queryClient.invalidateQueries({ queryKey: ['creditStats'] })
    } catch (err) {
      console.error('Failed to save hidden blots', err)
    }
  }, [selectedCaseId, apiDomain, queryClient])

  /**
   * Remet les bandelettes supprimées, sans rien relancer. Une bandelette
   * supprimée est un faux positif que l'opérateur a écarté : elle n'a jamais
   * quitté le cas (voir hiddenBlotIds), il n'y a donc rien à redétecter — et
   * tout le travail manuel du cas est conservé, contrairement à une relance.
   * Les crédits remboursés à la suppression sont redébités côté serveur
   * (settleGhostBlotCredits règle le delta dans les deux sens).
   */
  const handleRestoreHiddenBlots = useCallback(async () => {
    const restored = ((analysisResult as any)?.hiddenBlotIds ?? []).length
    if (!restored) return
    setRestoringHidden(true)
    try {
      await handleHiddenBlotsChange([])
      setRestoreHiddenNonce((n) => n + 1)
      onReanalyzeClose()
      toast({
        title: restored > 1 ? `${restored} bandelettes récupérées` : 'Bandelette récupérée',
        description: 'Témoins, bandes placées, validations et commentaires sont conservés.',
        status: 'success',
        duration: 4000,
      })
    } finally {
      setRestoringHidden(false)
    }
  }, [analysisResult, handleHiddenBlotsChange, onReanalyzeClose, toast])

  // Persist manually-added bands so they survive a refresh (also keeps the
  // export payload + local case state in sync).
  const handlePlacedBandsChange = useCallback(async (bands: Record<number, Array<{ name: string; yFraction: number }>>) => {
    setPlacedBandsForExport(bands)
    setAnalysisResult(prev => prev ? { ...prev, placedBands: bands } as any : prev)
    if (!selectedCaseId) return
    try {
      const { accessToken } = getSavedAuthData()
      await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/placed-bands`, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placedBands: bands }),
      })
      queryClient.invalidateQueries({ queryKey: ['blotRecentCases'] })
    } catch (err) {
      console.error('Failed to save placed bands', err)
    }
  }, [selectedCaseId, apiDomain, queryClient])

  // Persist deleted bands so they survive a refresh (same pattern as placed bands).
  const handleSuppressedBandsChange = useCallback(async (bands: Record<number, string[]>) => {
    setAnalysisResult(prev => prev ? { ...prev, suppressedBands: bands } as any : prev)
    if (!selectedCaseId) return
    try {
      const { accessToken } = getSavedAuthData()
      await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/suppressed-bands`, {
        method: 'PATCH',
        headers: {
          'Authorization': accessToken || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ suppressedBands: bands }),
      })
      queryClient.invalidateQueries({ queryKey: ['blotRecentCases'] })
    } catch (err) {
      console.error('Failed to save suppressed bands', err)
    }
  }, [selectedCaseId, apiDomain, queryClient])

  // Correct a strip's recognised identifier (OCR draft). Optimistic local update
  // then persist; the blots jsonb carries strip_id, so it survives reloads.
  const handleStripIdChange = useCallback(async (blotId: number, stripId: string | null) => {
    // A hand-entered letter pulls every still-suggested / blank strip onto the
    // same letter (one plate = one prefix); the biologist just completes each
    // number. Confident reads ('sure') and other hand-edits are left untouched.
    const lead = (stripId ?? '').trim().match(/^[A-Za-z]/)?.[0]?.toUpperCase() || null
    const swapLetter = (sid: string | null | undefined, L: string) => {
      const m = (sid ?? '').match(/^[A-Za-z](.*)$/)
      return m ? L + m[1] : L
    }
    setAnalysisResult(prev => {
      if (!prev) return prev
      const blots = ((prev as any).blots || []).map((b: any) => {
        if (b.id === blotId) return { ...b, strip_id: stripId, strip_id_status: stripId ? 'edited' : null }
        if (lead && b.strip_id_status !== 'sure' && b.strip_id_status !== 'edited') {
          return { ...b, strip_id: swapLetter(b.strip_id, lead), strip_id_status: 'suggested' }
        }
        return b
      })
      return { ...prev, blots } as any
    })
    if (!selectedCaseId) return
    try {
      const { accessToken } = getSavedAuthData()
      await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/strip-id`, {
        method: 'PATCH',
        headers: { 'Authorization': accessToken || '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ blotId, stripId }),
      })
    } catch (err) {
      console.error('Failed to save strip id', err)
    }
  }, [selectedCaseId, apiDomain])

  // Refs always hold current values — no stale closure possible.
  const selectedCaseIdRef = useRef<string | null>(null)
  const apiDomainRef = useRef(apiDomain)
  // Même raison que apiDomainRef : cette sauvegarde vit dans une fermeture
  // stable, et doit pouvoir prévenir sans se faire recréer à chaque rendu.
  const toastRef = useRef(toast)
  useEffect(() => { selectedCaseIdRef.current = selectedCaseId }, [selectedCaseId])
  useEffect(() => { apiDomainRef.current = apiDomain }, [apiDomain])
  useEffect(() => { toastRef.current = toast }, [toast])

  // Fire-and-forget save — called directly on every result change.
  // Uses refs so it NEVER captures stale values.
  const saveManualResults = useCallback((results: Record<number, ManualResult>) => {
    const caseId = selectedCaseIdRef.current
    if (!caseId) {
      console.warn('[ManualResults] No caseId — save skipped')
      return
    }
    const toSave: Record<string, string> = {}
    Object.entries(results).forEach(([k, v]) => { if (v != null) toSave[k] = String(v) })
    if (Object.keys(toSave).length === 0) return

    const { accessToken } = getSavedAuthData()
    console.log('[ManualResults] Saving to case', caseId, toSave)
    apiFetch(`${apiDomainRef.current}/api/blot-analysis/case/${caseId}/manual-results`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manualBlotResults: toSave }),
    })
      .then(async res => {
        if (!res.ok) {
          const text = await res.text()
          console.error('[ManualResults] PATCH failed', res.status, text)
          // Un échec silencieux est le pire des cas : le biologiste continue
          // de valider, rien n'est enregistré, et un rafraîchissement de page
          // efface tout ce qu'il croyait avoir posé. C'est arrivé.
          toastRef.current?.({
            title: 'Validations non enregistrées',
            description: res.status === 401
              ? 'Votre session a expiré. Reconnectez-vous avant de continuer : rien n’est sauvegardé pour l’instant.'
              : 'Le serveur a refusé l’enregistrement. Ne rafraîchissez pas la page avant d’avoir réessayé.',
            status: 'error',
            duration: null,
            isClosable: true,
          })
        } else {
          console.log('[ManualResults] ✓ Saved:', toSave)
          queryClient.invalidateQueries({ queryKey: ['blotRecentCases'] })
        }
      })
      .catch(err => {
        console.error('[ManualResults] Network error:', err)
        toastRef.current?.({
          title: 'Validations non enregistrées',
          description: 'Le serveur est injoignable. Ne rafraîchissez pas la page avant d’avoir réessayé.',
          status: 'error',
          duration: null,
          isClosable: true,
        })
      })
  }, [queryClient])

  const handleBackToRecentCases = useCallback(() => {
    // Admin-shadow short-circuit: a supervisor who came in from /admin/pilots
    // should land back there, not in their own Recent Cases (which would mix
    // their cases with the pilot context they were inspecting).
    if (cameFromAdminPilotsRef.current) {
      navigate('/admin/pilots')
      return
    }
    // Le registre est un point d'entrée au même titre : on y retourne.
    if (cameFromRegistryRef.current) {
      navigate('/lab/registry')
      return
    }
    // L'ADRESSE D'ABORD, l'écran ensuite.
    //
    // Ce retour vidait l'écran puis demandait la navigation. Depuis qu'un
    // garde peut retenir ce départ — il reste des bandelettes sans verdict —
    // la navigation pouvait être refusée APRÈS que le cas ait déjà disparu de
    // l'écran : on cliquait « Rester et valider » et il n'y avait plus rien à
    // valider, l'adresse pointant toujours sur un cas que l'écran avait
    // oublié.
    //
    // La remise à zéro se fait donc en réaction à l'adresse (voir l'effet
    // ci-dessous), une fois le départ effectivement accordé.
    //
    // … encore faut-il que l'adresse change. Sur l'écran de dépôt, on est
    // déjà sur /lab/blot-analysis sans cas : la navigation ne change rien,
    // l'effet ne se déclenche pas, et la flèche de retour — comme « Tout
    // voir », qui passe par ici — restait sans effet. Hors d'un cas ouvert il
    // n'y a de toute façon rien à retenir : on remet l'écran à zéro sur
    // place, exactement comme le fait déjà « Modifier » dans cette situation.
    if (!caseId) {
      setViewMode('recent-cases')
      setSelectedCaseId(null)
      return
    }
    navigate(brand.basePath, { replace: true })
  }, [caseId, navigate, brand.basePath])

  // Manual blot drawing handlers
  const handleToggleDrawingMode = useCallback(() => {
    setIsDrawingMode(prev => {
      if (!prev) setShowBlotBoxes(true)
      return !prev
    })
    setPendingSelection(null)
    isDrawingRef.current = false
    drawStartRef.current = null
    if (drawRectRef.current) drawRectRef.current.style.display = 'none'
  }, [])

  const handleDrawPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDrawingMode || pendingSelection || !sourceImageRef.current) return
    if (e.button !== undefined && e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    const rect = sourceImageRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    isDrawingRef.current = true
    drawStartRef.current = { x, y }
    if (drawRectRef.current) {
      drawRectRef.current.style.display = 'block'
      drawRectRef.current.style.left = `${x}%`
      drawRectRef.current.style.top = `${y}%`
      drawRectRef.current.style.width = '0%'
      drawRectRef.current.style.height = '0%'
    }
  }, [isDrawingMode, pendingSelection])

  const handleDrawPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDrawingRef.current || !drawStartRef.current || !sourceImageRef.current || !drawRectRef.current) return
    const rect = sourceImageRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
    const start = drawStartRef.current
    drawRectRef.current.style.left = `${Math.min(start.x, x)}%`
    drawRectRef.current.style.top = `${Math.min(start.y, y)}%`
    drawRectRef.current.style.width = `${Math.abs(x - start.x)}%`
    drawRectRef.current.style.height = `${Math.abs(y - start.y)}%`
  }, [])

  const handleDrawPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    if (!isDrawingRef.current || !drawStartRef.current || !sourceImageRef.current) {
      isDrawingRef.current = false
      drawStartRef.current = null
      if (drawRectRef.current) drawRectRef.current.style.display = 'none'
      return
    }
    const rect = sourceImageRef.current.getBoundingClientRect()
    const endX = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
    const endY = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
    const start = drawStartRef.current

    const x = Math.min(start.x, endX)
    const y = Math.min(start.y, endY)
    const w = Math.abs(endX - start.x)
    const h = Math.abs(endY - start.y)

    isDrawingRef.current = false
    drawStartRef.current = null
    if (drawRectRef.current) drawRectRef.current.style.display = 'none'

    if (w < 1 || h < 1) return

    setPendingSelection({ x, y, w, h, angle: 0 })
  }, [])

  const handleConfirmSelection = useCallback(async () => {
    if (!pendingSelection || !analysisResult || !selectedCaseId) return

    setIsSubmittingBlot(true)

    try {
      const { accessToken } = getSavedAuthData()
      const response = await apiFetch(
        `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/add-blot`,
        {
          method: 'POST',
          headers: { 'Authorization': accessToken || '', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            xFrac: pendingSelection.x / 100,
            yFrac: pendingSelection.y / 100,
            wFrac: pendingSelection.w / 100,
            hFrac: pendingSelection.h / 100,
            angle: pendingSelection.angle || 0,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || errorData.message || 'Failed to add blot')
      }

      const result = await response.json()

      setAnalysisResult(prev => {
        if (!prev) return prev
        return {
          ...prev,
          blots: [...prev.blots, result.blot],
          total_blots: result.totalBlots,
          total_bands: result.totalBands,
        }
      })

      toast({
        title: 'Blot ajouté',
        description: `Blot #${result.blot.id} ajouté avec ${result.blot.num_bands} bande(s)`,
        status: 'success',
        duration: 3000,
      })

      setPendingSelection(null)
      setIsDrawingMode(false)
    } catch (error) {
      console.error('Error adding blot:', error)
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Impossible d\'ajouter le blot',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmittingBlot(false)
    }
  }, [pendingSelection, analysisResult, selectedCaseId, apiDomain, toast])

  const handleCancelSelection = useCallback(() => {
    setPendingSelection(null)
  }, [])

  // ── Drag-to-move an existing blot box ─────────────────────────────────────
  const clampPct = (v: number, max: number) => Math.max(0, Math.min(max, v))
  // Hold Shift while dragging or resizing to move at a fifth of the pointer
  // speed — the gear for the 2–3 px corrections that 1:1 tracking makes fiddly.
  const MOVE_FINE_GAIN = 0.2

  const repositionBlot = useCallback(async (
    blotId: number,
    leftPct: number, topPct: number, wPct: number, hPct: number, angle: number,
  ) => {
    if (!selectedCaseId) return
    setRepositioningBlotId(blotId)
    // Hold the box at the dropped spot / angle while the backend re-analyses
    // (optimistic), instead of snapping back to the stale AI geometry.
    setRepositionTarget({ id: blotId, leftPct, topPct, angle: angle || 0 })
    try {
      const { accessToken } = getSavedAuthData()
      const response = await apiFetch(
        `${apiDomain}/api/blot-analysis/case/${selectedCaseId}/update-blot/${blotId}`,
        {
          method: 'POST',
          headers: { Authorization: accessToken || '', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            xFrac: leftPct / 100,
            yFrac: topPct / 100,
            wFrac: wPct / 100,
            hFrac: hPct / 100,
            angle: angle || 0,
          }),
        },
      )
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(err.error || err.message || 'Échec du repositionnement')
      }
      const result = await response.json()
      setAnalysisResult(prev => {
        if (!prev) return prev
        const clearedCalib = { ...((prev as any).bandCalibration || {}) }
        delete clearedCalib[blotId]
        const clearedPlaced = { ...((prev as any).placedBands || {}) }
        delete clearedPlaced[blotId]
        // Repositioning re-reads the profile from scratch, so any manual
        // control-line override tied to the old profile's pixel indices is stale too.
        const clearedControlLineOverrides = { ...((prev as any).controlLineOverrides || {}) }
        delete clearedControlLineOverrides[String(blotId)]
        return {
          ...prev,
          blots: prev.blots.map((b: any) => {
            if (b.id !== blotId) return b
            // Repositioning re-reads the strip id from the new box (the point of
            // moving it when YOLO clipped the number). But never clobber a value
            // the operator typed by hand — keep an 'edited' id, else take the fresh read.
            if (b.strip_id_status === 'edited' && b.strip_id) {
              return { ...result.blot, strip_id: b.strip_id, strip_id_status: b.strip_id_status }
            }
            return result.blot
          }),
          total_bands: result.totalBands,
          bandCalibration: clearedCalib,
          placedBands: clearedPlaced,
          controlLineOverrides: clearedControlLineOverrides,
        } as typeof prev
      })
      // The region now holds a new sample → this blot's manual validation is stale.
      setManualBlotResults(prev => {
        if (prev[blotId] == null) return prev
        const next = { ...prev }
        delete next[blotId]
        return next
      })
      // No toast: the strip snapping to its new position is confirmation enough.
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Repositionnement impossible',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setRepositioningBlotId(null)
      setRepositionTarget(null)
    }
  }, [selectedCaseId, apiDomain, toast])

  const handleBoxPointerDown = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    geom: { id: number; leftPct: number; topPct: number; wPct: number; hPct: number; angle: number },
  ) => {
    if (isDrawingMode || repositioningBlotId != null) return
    const rect = sourceImageRef.current?.getBoundingClientRect()
    if (!rect) return
    e.preventDefault()
    e.stopPropagation()
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
    dragRef.current = {
      id: geom.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startLeft: geom.leftPct,
      startTop: geom.topPct,
      curLeft: geom.leftPct,
      curTop: geom.topPct,
      wPct: geom.wPct,
      hPct: geom.hPct,
      angle: geom.angle,
      rectW: rect.width,
      rectH: rect.height,
      moved: false,
      fine: false,
      baseClientX: e.clientX,
      baseClientY: e.clientY,
      baseLeft: geom.leftPct,
      baseTop: geom.topPct,
      el: e.currentTarget as HTMLElement,
      overTrash: false,
    }
    // One state update to switch on the "dragging" styling; position updates
    // during the move go straight to the DOM (see handleBoxPointerMove).
    setDraggingBox({ id: geom.id, leftPct: geom.leftPct, topPct: geom.topPct })
  }, [isDrawingMode, repositioningBlotId])

  const handleBoxPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    // Shift = precision gear. Nudging a box by 2–3 px is a common correction and
    // 1:1 tracking makes it a fight; at 0.2× the pointer travels 5× further than
    // the box, which also keeps the raw delta above the click-vs-drag threshold.
    const fine = e.shiftKey
    if (fine !== d.fine) {
      d.fine = fine
      d.baseClientX = e.clientX
      d.baseClientY = e.clientY
      d.baseLeft = d.curLeft
      d.baseTop = d.curTop
    }
    const gain = fine ? MOVE_FINE_GAIN : 1
    const dxPct = ((e.clientX - d.baseClientX) / d.rectW) * 100 * gain
    const dyPct = ((e.clientY - d.baseClientY) / d.rectH) * 100 * gain
    // Commit anything that actually moved. This only exists to tell a drag from a
    // plain click (which does nothing on a box anyway), so 1px is enough — at the
    // old 3px, nudging a box onto a strip by 1–2px was reverted on release and the
    // box appeared to snap back. Reads the raw gesture origin so a Shift-fine
    // adjustment, where the pointer travels 5x further than the box, also counts.
    if (Math.abs(e.clientX - d.startClientX) >= 1 || Math.abs(e.clientY - d.startClientY) >= 1) d.moved = true
    d.curLeft = clampPct(d.baseLeft + dxPct, 100 - d.wPct)
    d.curTop = clampPct(d.baseTop + dyPct, 100 - d.hPct)
    // Move the box directly — no setState → no re-render of the whole workspace,
    // so the box tracks the pointer 1:1 instead of lagging behind.
    if (d.el) {
      d.el.style.left = `${d.curLeft}%`
      d.el.style.top = `${d.curTop}%`
    }
    // Is the pointer over the delete zone? Store on the ref (read at drop, no
    // stale closure) and mirror to state only when it flips (highlight).
    const t = trashRef.current?.getBoundingClientRect()
    const on = !!t && e.clientX >= t.left && e.clientX <= t.right && e.clientY >= t.top && e.clientY <= t.bottom
    if (on !== d.overTrash) { d.overTrash = on; setOverTrash(on) }
  }, [])

  const handleBoxPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    dragRef.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    // Hand control of left/top back to React before it re-renders.
    if (d?.el) {
      d.el.style.left = ''
      d.el.style.top = ''
    }
    setDraggingBox(null)
    setOverTrash(false)
    if (!d) return
    // Dropped on the trash → delete (hide) this blot instead of repositioning.
    // Works even without moving far, so a small drag to the bin still deletes.
    if (d.overTrash) {
      const current: number[] = (analysisResult as any)?.hiddenBlotIds ?? []
      if (!current.includes(d.id)) handleHiddenBlotsChange([...current, d.id])
      return
    }
    if (!d.moved) return
    repositionBlot(d.id, d.curLeft, d.curTop, d.wPct, d.hPct, d.angle)
  }, [repositionBlot, analysisResult, handleHiddenBlotsChange])

  // ── Rotate an EXISTING blot box ───────────────────────────────────────────
  // Mirrors the rotation of the "add a blot" selection (same ±45°/90° snapping),
  // but applied to an already-detected strip so the operator can straighten a
  // tilted frame. Like the drag, the live feedback goes straight to the DOM and
  // only the drop commits — one re-analysis instead of one per degree.
  const rotateRef = useRef<{
    id: number
    el: HTMLElement | null
    cx: number
    cy: number
    startAngle: number
    startPointerA: number
    // Origin of the current precision gear — rebased whenever Shift flips.
    fine: boolean
    baseAngle: number
    basePointerA: number
    curAngle: number
    leftPct: number
    topPct: number
    wPct: number
    hPct: number
    moved: boolean
  } | null>(null)

  const pointerAngleDeg = (clientX: number, clientY: number, cx: number, cy: number) =>
    (Math.atan2(clientX - cx, -(clientY - cy)) * 180) / Math.PI

  // Rotation feel. Straightening a tilted strip is almost always a 1–3°
  // correction, which the old 3° snap window swallowed whole — the angle was
  // dragged straight back to 0° and the fine adjustment was impossible. Keep a
  // snap for the canonical angles but make it narrow enough to leave that range
  // usable, and offer Shift as a precision gear that slows the drag right down
  // and drops snapping entirely.
  const ROTATE_SNAPS = [-180, -135, -90, -45, 0, 45, 90, 135, 180]
  const ROTATE_SNAP_DEG = 1.2
  const ROTATE_FINE_GAIN = 0.2

  const resolveRotation = (startAngle: number, rawDelta: number, fine: boolean): number => {
    let angle = startAngle + (fine ? rawDelta * ROTATE_FINE_GAIN : rawDelta)
    if (!fine) {
      for (const s of ROTATE_SNAPS) {
        if (Math.abs(angle - s) < ROTATE_SNAP_DEG) { angle = s; break }
      }
    }
    while (angle > 180) angle -= 360
    while (angle < -180) angle += 360
    // 0.1° quantum — kills pointer jitter without being noticeable.
    return Math.round(angle * 10) / 10
  }

  const handleBoxRotateDown = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    geom: { id: number; leftPct: number; topPct: number; wPct: number; hPct: number; angle: number },
  ) => {
    if (isDrawingMode || repositioningBlotId != null) return
    e.preventDefault()
    // Keep the box's own drag handler from firing.
    e.stopPropagation()
    const boxEl = (e.currentTarget as HTMLElement).parentElement
    if (!boxEl) return
    // Rotation is about the box centre; the AABB centre equals it, so the
    // bounding rect is a safe reference even when already tilted.
    const r = boxEl.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
    rotateRef.current = {
      id: geom.id,
      el: boxEl,
      cx,
      cy,
      startAngle: geom.angle,
      startPointerA: pointerAngleDeg(e.clientX, e.clientY, cx, cy),
      fine: e.shiftKey,
      baseAngle: geom.angle,
      basePointerA: pointerAngleDeg(e.clientX, e.clientY, cx, cy),
      curAngle: geom.angle,
      leftPct: geom.leftPct,
      topPct: geom.topPct,
      wPct: geom.wPct,
      hPct: geom.hPct,
      moved: false,
    }
  }, [isDrawingMode, repositioningBlotId])

  const handleBoxRotateMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const r = rotateRef.current
    if (!r) return
    const curA = pointerAngleDeg(e.clientX, e.clientY, r.cx, r.cy)
    // Shift = precision gear; rebase on the flip so it refines from the current
    // angle rather than jumping. Snapping is handled inside resolveRotation.
    const fine = e.shiftKey
    if (fine !== r.fine) {
      r.fine = fine
      r.baseAngle = r.curAngle
      r.basePointerA = curA
    }
    const angle = resolveRotation(r.baseAngle, curA - r.basePointerA, fine)
    // Threshold sits below the 0.1° quantum so a fine correction still commits
    // on drop — at 0.5° the sub-degree adjustments were silently discarded.
    if (Math.abs(angle - r.startAngle) >= 0.05) r.moved = true
    r.curAngle = angle
    if (r.el) {
      r.el.style.transformOrigin = 'center center'
      r.el.style.transform = `rotate(${angle}deg)`
    }
  }, [])

  const handleBoxRotateUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const r = rotateRef.current
    rotateRef.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    if (!r) return
    // Hand the transform back to React before it re-renders.
    if (r.el) { r.el.style.transform = ''; r.el.style.transformOrigin = '' }
    if (!r.moved) return
    repositionBlot(r.id, r.leftPct, r.topPct, r.wPct, r.hPct, r.curAngle)
  }, [repositionBlot])

  const handleEditPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, mode: EditMode) => {
    if (!pendingSelection || !sourceImageRef.current) return
    if (e.button !== undefined && e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    const rect = sourceImageRef.current.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * 100
    const py = ((e.clientY - rect.top) / rect.height) * 100
    editModeRef.current = mode
    editAnchorRef.current = { px, py, sel: { ...pendingSelection } }
    editFineRef.current = e.shiftKey
    editLastSelRef.current = { ...pendingSelection }
  }, [pendingSelection])

  const handleEditPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const mode = editModeRef.current
    if (!mode || !editAnchorRef.current || !sourceImageRef.current) return
    const rect = sourceImageRef.current.getBoundingClientRect()
    const px = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
    const py = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
    // Shift = precision gear, same as dragging an existing box. Rebase the anchor
    // on the flip so engaging or releasing it mid-gesture refines from where the
    // selection currently sits instead of snapping to a recomputed position.
    const fine = e.shiftKey
    if (fine !== editFineRef.current) {
      editFineRef.current = fine
      editAnchorRef.current = { px, py, sel: { ...(editLastSelRef.current ?? editAnchorRef.current.sel) } }
    }
    const anchor = editAnchorRef.current
    const gain = fine ? MOVE_FINE_GAIN : 1
    const dxScreen = (px - anchor.px) * gain
    const dyScreen = (py - anchor.py) * gain
    const start = anchor.sel
    let x = start.x
    let y = start.y
    let w = start.w
    let h = start.h
    let angle = start.angle
    const MIN = 0.5

    if (mode === 'move') {
      x = Math.max(0, Math.min(100 - start.w, start.x + dxScreen))
      y = Math.max(0, Math.min(100 - start.h, start.y + dyScreen))
    } else if (mode === 'rotate') {
      // Angle from rect center (in screen coords) to pointer
      const cxStart = start.x + start.w / 2
      const cyStart = start.y + start.h / 2
      const aspect = rect.width / rect.height
      // Convert % to a metric where vertical/horizontal % feel equivalent
      const startVecX = (anchor.px - cxStart) * aspect
      const startVecY = -(anchor.py - cyStart)
      const curVecX = (px - cxStart) * aspect
      const curVecY = -(py - cyStart)
      const startA = Math.atan2(startVecX, startVecY) * 180 / Math.PI
      const curA = Math.atan2(curVecX, curVecY) * 180 / Math.PI
      // Same feel as rotating an existing blot: narrow snap, Shift for precision.
      angle = resolveRotation(start.angle, curA - startA, e.shiftKey)
    } else {
      // Resize — if rotated, transform the screen delta into the rect's local frame
      // so resize follows the rotated edges rather than screen axes.
      const aspect = rect.width / rect.height
      let dxLocal = dxScreen
      let dyLocal = dyScreen
      if (Math.abs(start.angle) > 0.01) {
        const a = -start.angle * Math.PI / 180
        const cos = Math.cos(a)
        const sin = Math.sin(a)
        // Scale to pixel-equivalent space (% → px-ish), rotate, then back
        const dxPx = dxScreen * aspect
        const dyPx = dyScreen
        dxLocal = (dxPx * cos - dyPx * sin) / aspect
        dyLocal = (dxPx * sin + dyPx * cos)
      }
      if (mode.includes('n')) { y = start.y + dyLocal; h = start.h - dyLocal }
      if (mode.includes('s')) { h = start.h + dyLocal }
      if (mode.includes('w')) { x = start.x + dxLocal; w = start.w - dxLocal }
      if (mode.includes('e')) { w = start.w + dxLocal }
      if (w < 0) { x = x + w; w = -w }
      if (h < 0) { y = y + h; h = -h }
      w = Math.max(MIN, w)
      h = Math.max(MIN, h)
      // Soft clamp only when not rotated (rotated rect AABB ≠ x/y/w/h)
      if (Math.abs(start.angle) < 0.01) {
        x = Math.max(0, Math.min(100 - MIN, x))
        y = Math.max(0, Math.min(100 - MIN, y))
        w = Math.min(100 - x, w)
        h = Math.min(100 - y, h)
      }
    }
    editLastSelRef.current = { x, y, w, h, angle }
    setPendingSelection({ x, y, w, h, angle })
  }, [])

  const handleEditPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    editModeRef.current = null
    editAnchorRef.current = null
    editFineRef.current = false
    editLastSelRef.current = null
  }, [])

  // Debug ROI: call backend to get annotated image with Hough lines
  // Always re-fetch to pick up latest pipeline changes
  const handleDebugRoi = useCallback(async () => {
    if (!selectedCaseId) return

    if (showDebugRoi) {
      // Toggle off
      setShowDebugRoi(false)
      return
    }

    setLoadingDebugRoi(true)
    try {
      const { accessToken } = getSavedAuthData()
      const response = await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/debug-roi`, {
        method: 'POST',
        headers: { 'Authorization': accessToken || '' },
      })

      if (!response.ok) throw new Error(`Debug ROI failed: ${response.statusText}`)

      const result = await response.json()
      setDebugRoiImage(`data:image/png;base64,${result.annotated_image_base64}`)
      setShowDebugRoi(true)

      console.log('Debug ROI info:', {
        profile: result.profile?.id,
        roi: result.roi,
        verticalLines: result.vertical_lines,
        horizontalBoundaries: result.horizontal_boundaries,
      })
    } catch (error) {
      console.error('Debug ROI error:', error)
      toast({
        title: 'Debug ROI',
        description: error instanceof Error ? error.message : 'Erreur',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setLoadingDebugRoi(false)
    }
  }, [selectedCaseId, showDebugRoi, debugRoiImage, apiDomain, toast])

  // Re-analyze: re-run analysis on the same stored file
  /**
   * Charge for an analysis and SURFACE a failure.
   *
   * This used to be `.catch(() => {})`: when the balance was too low the backend
   * answered 402, the error was swallowed, and the analysis was delivered without
   * ever being billed — silently, so neither the user nor the usage log knew. The
   * work is already done at this point, so we still don't roll it back; but the
   * user is told, instead of being quietly served for free.
   */
  const chargeAnalysis = useCallback(async (payload: Parameters<typeof consumeCredits>[0]) => {
    try {
      await consumeCredits(payload)
    } catch (err: any) {
      const insufficient = err?.response?.status === 402
      toast({
        title: insufficient ? t('credits.insufficient') : 'Crédits non décomptés',
        description: insufficient
          ? "Cette analyse n'a pas pu être décomptée : votre solde est insuffisant. Rechargez vos crédits pour continuer."
          : "L'analyse a abouti mais le décompte des crédits a échoué.",
        status: insufficient ? 'error' : 'warning',
        duration: 8000,
        isClosable: true,
      })
    }
  }, [consumeCredits, toast, t])

  const [freezingSnapshot, setFreezingSnapshot] = useState(false)

  /**
   * Les bandes qu'IRIS retient, par bandelette d'échantillon.
   *
   * Lues depuis les interprétations que la galerie remonte, donc disponibles
   * seulement une fois le voile levé ET la galerie recalculée — d'où le geste
   * explicite plus bas plutôt qu'un effet : pris automatiquement, l'instantané
   * captait les interprétations de l'état VOILÉ, où les profils sont vides par
   * construction, et figeait un IRIS négatif partout.
   */
  const irisReadingForSnapshot = useMemo(() => {
    const bench = (analysisResult as any)?.benchReading
    if (!bench?.lockedAt) return null
    // Celles que la garde vient de déclarer prêtes : la recherche affichée,
    // moins ce qui est déjà figé.
    const sampleIds = new Set(snapshotReadiness.scope)
    const entries = blotInterpretations
      .filter((i) => sampleIds.has(i.blotId))
      .map((i) => {
        const bands = i.identifiedBandNames ?? []
        return [i.blotId, {
          bands,
          // La conclusion vient de l'ÉVALUATEUR sur les bandes d'IRIS, pas de
          // son verdict affiché : comparer deux conclusions suppose qu'elles
          // sortent de la même lecture de la notice, sinon un écart entre deux
          // implémentations des mêmes règles se lirait comme un désaccord
          // entre la machine et la biologiste. Le verdict affiché part aussi,
          // pour référence.
          conclusion: verdictFromBands(activeConfig as any, bands),
          displayedVerdict: i.result ?? null,
          // L'intensité de chaque bande, rapportée à la même bande du témoin.
          // Elle se fige MAINTENANT ou jamais : elle n'est reconstructible
          // qu'avec le profil, le témoin et la calibration du moment, et une
          // relance du cas les remplace. Sans elle, l'analyse ne peut pas
          // dire si les désaccords se concentrent sur les bandes faibles —
          // la question la plus utile pour corriger la détection.
          intensities: i.bandRatios && Object.keys(i.bandRatios).length ? i.bandRatios : undefined,
        }] as const
      })
    if (!entries.length) return null
    return {
      blots: Object.fromEntries(entries),
      bandsFound: entries.reduce((n, [, v]) => n + v.bands.length, 0),
    }
  }, [analysisResult, blotInterpretations, immunoblotConfig, snapshotReadiness])

  /** Fige la lecture d'IRIS, sur demande, et jamais à vide. */
  const handleFreezeIrisSnapshot = useCallback(async () => {
    if (!selectedCaseId || !irisReadingForSnapshot?.bandsFound) return
    setFreezingSnapshot(true)
    try {
      // axiosClient et non fetch : lui seul rafraîchit le jeton quand il a
      // expiré. En fetch brut l'appel repartait en 401 sans rien dire, et
      // « Impossible de figer la comparaison » désignait une session finie,
      // pas un refus de la règle.
      const { data } = await axiosClient.put(
        `/blot-analysis/case/${selectedCaseId}/iris-snapshot`,
        {
          blots: irisReadingForSnapshot.blots,
          rulesVersion: (activeConfig as any)?.pathology ?? null,
        },
      )
      setAnalysisResult((prev) => (prev ? { ...prev, irisSnapshot: data.irisSnapshot } : prev))
      toast({ title: 'Comparaison figée', status: 'success', duration: 3000 })
    } catch (error) {
      console.error('Error storing IRIS snapshot:', error)
      toast({ title: 'Impossible de figer la comparaison', status: 'error', duration: 4000 })
    } finally {
      setFreezingSnapshot(false)
    }
  }, [selectedCaseId, irisReadingForSnapshot, immunoblotConfig, apiDomain, toast])

  const handleReanalyzeRef = useRef<(() => Promise<void>) | null>(null)
  const switchEvaluationModeRef = useRef<((m: 'prospective' | 'retrospective') => Promise<void>) | null>(null)
  const [switchingMode, setSwitchingMode] = useState(false)
  // Une bascule en attente de la confirmation de relance. Une REF et non un
  // état : la relance confirmée rappelle la relance via la bascule, et une
  // valeur d'état resterait figée dans la fermeture le temps de ce aller-retour
  // — le drapeau serait encore vrai au second passage, et la relance
  // s'appellerait elle-même sans fin.
  const isRetrospective = analysisResult?.evaluationMode === 'retrospective'
  // Les CIP-WB se lisent en comparant deux bandelettes appariées : il n'y a
  // pas de bandes à cocher, donc pas de lecture de paillasse à saisir.
  const canReadOnBands = Boolean(selectedCaseId) && readsOnBandPresence(activeConfig as any)

  /**
   * Bascule le mode d'évaluation.
   *
   * Vers le rétrospectif, le cas repart de zéro : le serveur efface la lecture
   * et l'instantané, et l'analyse est relancée — une plaque déjà interprétée à
   * l'écran ne peut pas fournir une lecture en aveugle, et ses bandelettes
   * seront de toute façon renumérotées. La lecture gardera la trace que
   * l'aveugle a été déclaré et non garanti.
   */
  /**
   * Demande la bascule. Vers le rétrospectif, elle passe par la confirmation
   * de relance : le geste efface témoins, placements et validations, et ce
   * dialogue le dit déjà. Dans l'autre sens rien n'est détruit, donc rien à
   * confirmer.
   */
  const requestEvaluationModeSwitch = useCallback(() => {
    // UN SEUL SENS : QUITTER LE RÉTROSPECTIF.
    //
    // Le mode se choisit au dépôt du fichier, et ce choix est définitif. Un
    // cas déjà analysé ne pouvait de toute façon pas fournir d'aveugle : la
    // bascule relançait l'analyse et enregistrait la lecture avec
    // `blinding: 'declared'` au lieu de `'enforced'`, c'est-à-dire un aveugle
    // annoncé que rien ne garantissait. Une série ne vaut pas la réserve
    // qu'il faudrait écrire à côté : la porte est fermée, et toutes les
    // lectures sont désormais `'enforced'` par construction.
    //
    // Le retour vers le prospectif reste ouvert — il ne fabrique aucune
    // donnée trompeuse, il abandonne une saisie — mais il se confirme :
    // dévoiler IRIS est sans retour pour cette plaque.
    if (!isRetrospective) return
    setConfirmLeaveRetrospective(true)
  }, [isRetrospective])

  const handleToggleEvaluationMode = useCallback(async (forced?: 'prospective' | 'retrospective') => {
    if (!selectedCaseId) return
    const next = forced ?? 'prospective'
    // Le rétrospectif se choisit au dépôt, jamais après. Le serveur refuse
    // déjà cette bascule ; on ne lui fait pas faire le trajet pour rien, et
    // un appel futur ne rouvrira pas la porte par inadvertance.
    if (next === 'retrospective') return
    setSwitchingMode(true)
    try {
      const { data } = await axiosClient.patch(
        `/blot-analysis/case/${selectedCaseId}/evaluation-mode`,
        { mode: next },
      )
      {
        // Reprendre le cas DÉVOILÉ, et pas seulement le drapeau de mode.
        //
        // Le voile n'est pas un affichage : le serveur sert des profils vides
        // pour les échantillons sous voile. Poser `evaluationMode` sans
        // reprendre les bandelettes laissait donc l'écran exactement comme
        // avant — aucune bande suggérée, aucun verdict — et le retour en
        // prospectif paraissait sans effet. Même fusion qu'après un
        // verrouillage, pour ne pas rejouer l'ouverture du cas et perdre le
        // filtre de recherche en cours.
        const fresh = data?.analysis
        setAnalysisResult((prev) => (prev ? {
          ...prev,
          ...(fresh ? {
            blots: fresh.blots ?? prev.blots,
            bandCalibration: fresh.bandCalibration ?? (prev as any).bandCalibration,
            placedBands: fresh.placedBands ?? (prev as any).placedBands,
            suppressedBands: fresh.suppressedBands ?? (prev as any).suppressedBands,
            manualBlotResults: fresh.manualBlotResults ?? (prev as any).manualBlotResults,
            controlLineOverrides: fresh.controlLineOverrides ?? (prev as any).controlLineOverrides,
            total_bands: fresh.totalBands ?? (prev as any).total_bands,
          } : {}),
          evaluationMode: 'prospective',
          benchReadingPending: false,
          // La comparaison n'a plus d'objet : le serveur a remis à zéro la
          // lecture en cours et l'instantané avec elle.
          benchReading: null,
          irisSnapshot: null,
        } as any : prev))
        // Les verdicts vivent aussi dans leur propre état, celui que lit la
        // galerie : sans cela les échantillons restaient « à valider » alors
        // que le cas portait déjà leurs verdicts.
        const restored = fresh?.manualBlotResults
        if (restored && typeof restored === 'object') {
          setManualBlotResults(
            Object.fromEntries(
              Object.entries(restored)
                .filter(([blotId, verdict]) => !Number.isNaN(Number(blotId)) && verdict)
                .map(([blotId, verdict]) => [
                  Number(blotId),
                  (verdict === 'DOUTEUX' ? 'NÉGATIF' : verdict) as ManualResult,
                ]),
            ),
          )
        }
        setBenchBands({})
      }
    } catch (error) {
      console.error('Error switching evaluation mode:', error)
      toast({ title: 'Changement de mode impossible', status: 'error', duration: 4000 })
    } finally {
      setSwitchingMode(false)
    }
  }, [selectedCaseId, isRetrospective, apiDomain, toast])
  switchEvaluationModeRef.current = handleToggleEvaluationMode

  // Conflit de config sur une relance (admin) : le sélecteur CHU chargé ne
  // correspond pas au profil du premier run du cas. Les clés du sélecteur
  // (« chu nancy ») et les ids de profil IA (« chu-nancy ») ne diffèrent que
  // par le séparateur. PROTIS est exclu : le serveur force son profil quel que
  // soit le choix de CHU (voir buildReanalyzeFormData côté BE).
  const reanalyzeProfileConflict = useMemo(() => {
    const overrideKey = getAdminCHUOverride()
    const caseProfile = (analysisResult as any)?.detection_profile as string | undefined
    if (!overrideKey || !caseProfile || caseProfile === 'protis') return null
    const overrideProfile = overrideKey.trim().toLowerCase().replace(/\s+/g, '-')
    if (overrideProfile === caseProfile) return null
    return { overrideProfile, caseProfile }
    // isReanalyzeOpen : relire le localStorage à chaque ouverture de la
    // confirmation, au cas où le sélecteur a changé depuis le montage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisResult, isReanalyzeOpen])

  // Chaque ouverture de la confirmation repart sur le choix sûr : la config du cas.
  useEffect(() => {
    if (isReanalyzeOpen) setReanalyzeProfileChoice('case')
  }, [isReanalyzeOpen])

  const handleReanalyze = useCallback(async () => {
    if (!selectedCaseId) return
    onReanalyzeClose()

    // Pre-flight credit check against the REAL cost. A re-analysis re-bills every
    // blot of a case we already have loaded, so checking a flat 2-blot estimate
    // (as this did) waved through a 24-credit charge on a 12-credit balance — the
    // charge then failed silently and the work was served for free.
    try {
      const knownBlots = Array.isArray(analysisResult?.blots) ? analysisResult!.blots.length : 0
      const expectedCost = Math.max(1, creditCostPerBlot * (knownBlots || 2))
      const creditCheck = await checkCredits({ amount: expectedCost })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error', duration: 5000, isClosable: true,
        })
        return
      }
    } catch (_e) {}

    setReanalyzing(true)
    try {
      const { accessToken } = getSavedAuthData()
      // Admin test override (persisted CHU selection) so a re-analysis can force the
      // same profile the admin picked for the first run — otherwise the backend
      // falls back to the admin's own org and downgrades a CHU case to 'default'.
      // Mais quand la config chargée DIFFÈRE de celle du cas, l'override ne part
      // que si l'admin l'a explicitement choisie dans la confirmation : sans
      // override envoyé, le serveur garde le profil stocké du cas.
      const chuOverride = getAdminCHUOverride()
      const sendOverride =
        !!chuOverride && (!reanalyzeProfileConflict || reanalyzeProfileChoice === 'loaded')
      const response = await apiFetch(`${apiDomain}/api/blot-analysis/case/${selectedCaseId}/reanalyze`, {
        method: 'POST',
        headers: { 'Authorization': accessToken || '', 'Content-Type': 'application/json' },
        body: JSON.stringify(sendOverride ? { organization: chuOverride } : {}),
      })

      if (!response.ok) throw new Error(`Re-analysis failed: ${response.statusText}`)

      const result = await response.json()

      setAnalysisResult({
        // Le mode et le drapeau d'attente viennent de la réponse : sans eux
        // l'écran restait sur la galerie après une relance en rétrospectif, et
        // la galerie n'avait rien à montrer puisque le serveur la voilait.
        evaluationMode: result.evaluationMode,
        benchReadingPending: result.benchReadingPending === true,
        sample_id: result.sample_id,
        filename: result.filename || analysisResult?.filename || '',
        pathology_id: result.pathology_id,
        pathology_name: result.pathology_name,
        timestamp: new Date().toISOString(),
        roi: result.roi || { x: 0, y: 0, width: 0, height: 0 },
        blots: result.blots || [],
        total_blots: result.total_blots || 0,
        total_bands: result.total_bands || 0,
        processing_time_seconds: result.processing_time_seconds || 0,
        detection_profile: result.detection_profile || undefined,
        page_count: result.page_count || 1,
        hiddenBlotIds: [],
      } as any)
      // The server wipes the case's manual state on a re-analysis (see the
      // reanalyze route: manualBlotResults, bandCalibration, placedBands and
      // hiddenBlotIds all reset) because the strips were re-detected from
      // scratch and the old verdicts describe regions that no longer exist.
      // These client-side maps are keyed by the same blot ids, which the new
      // detection reuses, so without clearing them too the blots come back
      // already "Validé" until the page is reloaded.
      setManualBlotResults({})
      setBlotComments({})
      setPlacedBandsForExport({})
      setBlotGalleryKey(k => k + 1)
      setHasResults(true)
      // Reset debug view
      setShowDebugRoi(false)
      setDebugRoiImage(null)

      toast({
        title: 'Re-analyse terminée',
        description: `${result.total_blots} blots, ${result.total_bands} bandes`,
        status: 'success',
        duration: 4000,
      })

      // Consume credits scaled to actual number of blots returned by the pipeline.
      const blotsCharged = Array.isArray(result.blots) ? result.blots.length : (result.total_blots ?? 0)
      const billedAmount = Math.max(1, creditCostPerBlot * blotsCharged)
      chargeAnalysis({
        amount: billedAmount,
        action: 'blot_analysis',
        resourceId: selectedCaseId,
        resourceType: 'blot_case',
        metadata: {
          type: 'reanalysis',
          blotsCount: blotsCharged,
          costPerBlot: creditCostPerBlot,
        },
      })

    } catch (error) {
      console.error('Reanalyze error:', error)
      toast({
        title: 'Erreur de re-analyse',
        description: describeUploadError(error, 'Erreur'),
        status: 'error',
        duration: error instanceof TypeError ? 10000 : 5000,
        isClosable: true,
      })
    } finally {
      setReanalyzing(false)
    }
  }, [selectedCaseId, apiDomain, toast, analysisResult, checkCredits, consumeCredits, creditCostPerBlot, onReanalyzeClose, t, reanalyzeProfileConflict, reanalyzeProfileChoice])

  // La bascule en rétrospectif relance l'analyse, mais elle est déclarée plus
  // haut : la ref évite de déplacer ces cent lignes pour une question d'ordre.
  handleReanalyzeRef.current = handleReanalyze

  // Smart back navigation based on current view
  const handleSmartBack = useCallback(() => {
    // Admin shadow flow always wins: every back from anywhere inside the
    // workspace returns to /admin/pilots until the supervisor leaves the
    // shadow context themselves.
    if (cameFromAdminPilotsRef.current) {
      navigate('/admin/pilots')
      return
    }
    if (viewMode === 'immunoblot-selection') {
      // From immunoblot selection, go back to lab store
      navigate('/lab')
    } else if (viewMode === 'recent-cases') {
      // From recent cases, go back to immunoblot selection
      handleChangeImmunoBlot()
    } else {
      // From new-analysis or case-details, go back to recent cases
      handleBackToRecentCases()
    }
  }, [viewMode, navigate, handleBackToRecentCases, handleChangeImmunoBlot])

  // Track which case was loaded via handleRecentCaseSelect to prevent useEffect from reloading it
  const loadedViaCaseSelectRef = useRef<string | null>(null)

  /**
   * L'adresse fait foi : plus de cas dans l'URL, plus de cas à l'écran.
   *
   * Tient aussi bien pour le bouton de retour que pour la flèche du
   * navigateur ou un lien externe, et surtout : ne s'applique qu'une fois le
   * départ accordé, puisque le garde retient la navigation avant qu'elle
   * n'arrive ici.
   */
  useEffect(() => {
    if (caseId || viewMode !== 'case-details') return
    // Où l'on va est porté par la navigation elle-même : « Modifier » vise le
    // catalogue des tests, « Nouvelle analyse » l'écran de dépôt, tout le
    // reste la liste des cas récents.
    const view = (location.state as { view?: string } | null)?.view
    const destination: ViewMode = view === 'immunoblot-selection'
      ? 'immunoblot-selection'
      : view === 'new-analysis' ? 'new-analysis' : 'recent-cases'
    if (destination !== 'recent-cases') {
      // Le catalogue oublie aussi le test choisi ; l'écran de dépôt le garde,
      // c'est tout son intérêt — on enchaîne sur le même test.
      if (destination === 'immunoblot-selection') setSelectedImmunoBlot(null)
      setUploadedFile(null)
      setHasResults(false)
      setAnalysisResult(null)
      setSourceImageDataUrl(null)
    }
    if (destination === 'new-analysis') setAnalyzing(false)
    setViewMode(destination)
    setSelectedCaseId(null)
    loadedViaCaseSelectRef.current = null
    // Le départ est consommé : le garde se réarme pour le prochain cas.
    setPendingDeparture(null)
  }, [caseId, viewMode, location.state])

  // Track a case fetch already in flight — StrictMode (or a fast caseId/apiDomain
  // dependency re-fire) can invoke the URL-load effect below twice back to back
  // for the SAME caseId. Two concurrent fetch+restore cycles racing each other
  // isn't just wasted work: whichever GET happens to land on a briefly
  // inconsistent moment (e.g. this dev backend restarting mid-session) sets
  // manualBlotResults/positiveControlsByPathology from ITS OWN snapshot, and the
  // LAST one to resolve wins regardless of which was actually current — verified
  // on a real case where the exact same blot's control-line offset computed to
  // two different values (13 then 184) across the double load. One in-flight
  // fetch per caseId at a time removes the race outright.
  const loadingCaseIdRef = useRef<string | null>(null)

  const handleRecentCaseSelect = useCallback(async (caseItem: any, filterPathologyId?: string | null) => {
    console.log('📊 Recent case selected:', caseItem)

    // Mark this case as loaded via direct selection (prevents useEffect reload)
    loadedViaCaseSelectRef.current = caseItem.id

    // The list this case was opened FROM may have been scoped to one
    // pathology (e.g. the "Leishmania" recent-cases tab) — a multitest case's
    // OWN blots carry the real per-strip pathology_label, distinct from the
    // single catalog id the case itself was uploaded under (see
    // pathologyWhereFragment on the backend for why the case can show up
    // under a pathology that isn't its own pathologyId). Find the first blot
    // whose label resolves to that filter and pre-scope the gallery to it,
    // instead of always opening on the full unfiltered case.
    const matchingBlot = filterPathologyId
      ? (caseItem.blots || []).find(
          (b: any) => resolvePathologyLabel(b?.pathology_label)?.id === filterPathologyId,
        )
      : null
    setInitialGalleryPathologyFilter(matchingBlot?.pathology_label ?? null)

    // Share role + sharer identity for this case (from the recent-cases list
    // or the URL loader). null when the current user owns the case (or it
    // isn't shared with them).
    setMyShareRole(caseItem.shareRole ?? null)
    setMySharedBy(caseItem.sharedBy ?? null)

    setViewMode('case-details')
    setSelectedCaseId(caseItem.id)
    setUploadedFile(null)
    setAnalyzing(false)
    setHasResults(true)
    // Reset debug state for the new case
    setShowDebugRoi(false)
    setDebugRoiImage(null)

    // Set immunoblot to match the case's pathology so the correct config (IgG vs IgM) is loaded
    if (caseItem.pathologyId && caseItem.pathologyName) {
      setSelectedImmunoBlot({
        id: caseItem.pathologyId,
        name: caseItem.pathologyName,
        antibodyType: caseItem.pathologyName.toLowerCase().includes('igg') ? 'IgG' : 'IgM',
        category: 'Unknown',
        colorScheme: 'teal',
      })
    }

    // Update URL to include case ID
    navigate(`${brand.basePath}/${caseItem.id}`, { replace: true })

    // Charger les données du cas — TOUT est déjà dans caseItem, donc on peut
    // afficher la galerie immédiatement. L'image source (plus lourde) est
    // chargée en arrière-plan plus bas, sans bloquer le rendu des résultats.
    const analysisData = {
      sample_id: caseItem.sampleId || '',
      filename: caseItem.filename || '',
      pathology_id: caseItem.pathologyId,
      pathology_name: caseItem.pathologyName,
      timestamp: caseItem.createdAt || new Date().toISOString(),
      roi: caseItem.roi || { x: 0, y: 0, width: 0, height: 0 },
      blots: caseItem.blots || [],
      total_blots: caseItem.totalBlots || 0,
      total_bands: caseItem.totalBands || 0,
      processing_time_seconds: caseItem.processingTimeSeconds || 0,
      detection_profile: caseItem.detectionProfile || caseItem.detection_profile || undefined,
      page_count: caseItem.pageCount || caseItem.page_count || 1,
      selectedPositiveControlId: caseItem.selectedPositiveControlId || null,
      selectedPositiveControl2Id: caseItem.selectedPositiveControl2Id || null,
      selectedPositiveControl3Id: caseItem.selectedPositiveControl3Id || null,
      positiveControlsByPathology: caseItem.positiveControlsByPathology || null,
      bandCalibration: caseItem.bandCalibration || null,
      controlLineOverrides: caseItem.controlLineOverrides || null,
      pairedRoleSwaps: caseItem.pairedRoleSwaps || {},
      hiddenBlotIds: caseItem.hiddenBlotIds || [],
      manualBlotResults: caseItem.manualBlotResults || {},
      placedBands: caseItem.placedBands || {},
      suppressedBands: caseItem.suppressedBands || {},
      repositionedBlots: caseItem.repositionedBlots || {},
      // Mode d'évaluation : en rétrospectif, le serveur ne sert ni profils ni
      // verdict tant que la lecture de paillasse n'est pas verrouillée, et
      // `benchReadingPending` est le drapeau qu'il pose pour le dire.
      evaluationMode: caseItem.evaluationMode || 'prospective',
      benchReadingPending: caseItem.benchReadingPending === true,
      benchReading: caseItem.benchReading || null,
      irisSnapshot: caseItem.irisSnapshot || null,
      // Preserve ownership info so admin-shadow read-only mode can detect
      // when the admin is browsing someone else's case (banner + button locks).
      userId: caseItem.userId,
      user: caseItem.user,
    }

    console.log('📝 Analysis result set:', analysisData)
    setAnalysisResult(analysisData)

    // Restore persisted manual validation results from DB
    const rawResults = caseItem.manualBlotResults
    console.log('[ManualResults] Restoring from DB:', rawResults)
    if (rawResults && typeof rawResults === 'object' && Object.keys(rawResults).length > 0) {
      const restored: Record<number, ManualResult> = {}
      Object.entries(rawResults).forEach(([blotId, result]) => {
        const id = Number(blotId)
        if (!isNaN(id) && result) {
          // DOUTEUX is deprecated app-wide (binary verdicts only). Coerce any legacy
          // value stored before that change so it never resurfaces in the UI/report.
          restored[id] = (result === 'DOUTEUX' ? 'NÉGATIF' : result) as ManualResult
        }
      })
      console.log('[ManualResults] Restored state:', restored)
      setManualBlotResults(restored)
    } else {
      console.log('[ManualResults] No persisted results found')
      setManualBlotResults({})
    }
    setBlotGalleryKey(k => k + 1)

    // Charger l'image source EN ARRIÈRE-PLAN (endpoint authentifié
    // /api/uploads/:filename) — l'image est repliée par défaut, donc son
    // chargement ne doit jamais retarder l'affichage de la galerie.
    await loadSourceImageFromServer(caseItem.fileUrl)
  }, [navigate, loadSourceImageFromServer, brand.basePath])

  // Load case automatically if caseId is provided in URL
  useEffect(() => {
    if (!caseId) return

    // Avoid reloading if this case was just loaded via handleRecentCaseSelect
    if (loadedViaCaseSelectRef.current === caseId) {
      console.log('⏭️ Case loaded via direct selection, skipping URL load:', caseId)
      return
    }

    // Avoid reloading if this case is already loaded
    if (selectedCaseId === caseId && hasResults) {
      console.log('⏭️ Case already loaded, skipping:', caseId)
      return
    }

    // Avoid a second concurrent fetch+restore cycle for the same case (see
    // loadingCaseIdRef) — StrictMode's double-effect-invoke otherwise races
    // two independent loads against each other.
    if (loadingCaseIdRef.current === caseId) {
      console.log('⏭️ Case already loading, skipping duplicate fetch:', caseId)
      return
    }

    const loadCaseFromUrl = async () => {
      loadingCaseIdRef.current = caseId
      try {
        console.log('🔗 Loading case from URL:', caseId)
        setIsLoadingFromUrl(true)
        const { accessToken } = getSavedAuthData()

        const apiUrl = `${apiDomain}/api/blot-analysis/case/${caseId}`
        console.log('📡 API URL:', apiUrl)
        console.log('🔑 Authorization token:', accessToken ? 'Present' : 'Missing')

        const response = await apiFetch(apiUrl, {
          headers: {
            'Authorization': accessToken || '',
            'Content-Type': 'application/json',
          },
        })

        console.log('📥 Response status:', response.status, response.statusText)

        if (!response.ok) {
          const errorBody = await response.text()
          console.error('❌ Error response body:', errorBody)
          throw new Error(`Failed to load case: ${response.statusText} - ${errorBody}`)
        }

        const data = await response.json()
        // Carry the share role + sharer identity (present when the case was
        // shared with the current user) so the workspace can lock the UI for
        // 'viewer' recipients and show "Invité(e) par X" instead of the
        // "Inviter" button for any share recipient.
        const caseData = { ...data.analysis, shareRole: data.share?.role, sharedBy: data.share?.sharedBy ?? null }

        // Set immunoblot if pathology info is available
        if (caseData.pathologyId && caseData.pathologyName) {
          setSelectedImmunoBlot({
            id: caseData.pathologyId,
            name: caseData.pathologyName,
            antibodyType: caseData.pathologyName.toLowerCase().includes('igg') ? 'IgG' : 'IgM',
            category: 'Unknown',
            colorScheme: 'teal',
          })
        }

        // Load the case using existing handler
        await handleRecentCaseSelect(caseData)
      } catch (error) {
        console.error('❌ Error loading case from URL:', error)
        toast({
          title: t('shared.common.error'),
          description: t('blotAnalysis.errors.unableToLoadCase'),
          status: 'error',
          duration: 5000,
        })
        // Navigate back to recent cases on error
        navigate(brand.basePath, { replace: true })
      } finally {
        setIsLoadingFromUrl(false)
        if (loadingCaseIdRef.current === caseId) loadingCaseIdRef.current = null
      }
    }

    loadCaseFromUrl()
  }, [caseId, apiDomain, toast, navigate, handleRecentCaseSelect, selectedCaseId, hasResults, brand.basePath])

  /**
   * LE CAS CONNAÎT SON MODULE ; L'ADRESSE S'Y RANGE.
   *
   * Un cas PROTIS ouvert par /lab/blot-analysis — clic depuis l'accueil, le
   * registre, la liste des cas récents, un vieux favori — chargeait l'atelier
   * en mode IRIS : marqueur de ligne de contrôle jaune sur un gel, pas de
   * graduations, catalogue de témoins… Toute l'interface d'un module appliquée
   * aux données de l'autre. Le test du cas dit à qui il appartient
   * (moduleOfPathologyId) ; si l'adresse se trompe de module, on la corrige,
   * et tout le reste — bandeau, galerie, retours — suit le bon chemin.
   */
  useEffect(() => {
    const pid = (analysisResult as any)?.pathology_id
    if (!pid || !selectedCaseId) return
    const mod = moduleOfPathologyId(pid)
    if (mod !== module) {
      navigate(`${MODULE_BRAND[mod].basePath}/${selectedCaseId}`, { replace: true })
    }
  }, [analysisResult, selectedCaseId, module, navigate])

  // Définir le contexte Blot pour l'agent IA
  useEffect(() => {
    if (!selectedImmunoBlot) {
      setContext({ type: 'general' })
      return
    }

    setContext({
      type: 'blot',
      blotAnalysisResults: analysisResult
        ? {
            total_blots: analysisResult.total_blots,
            total_bands: analysisResult.total_bands,
            processing_time_seconds: analysisResult.processing_time_seconds,
            pathology_id: analysisResult.pathology_id,
            pathology_name: analysisResult.pathology_name,
            sample_id: analysisResult.sample_id,
          }
        : undefined,
      blotWorkflowCallbacks: {
        onLaunchAnalysis: handleAnalyze,
        onNewAnalysis: handleNewAnalysis,
        onViewRecentCases: handleBackToRecentCases,
      },
      blotWorkflowState: {
        currentView: viewMode === 'immunoblot-selection' ? 'recent-cases' : (viewMode as any),
        hasUploadedFile: !!uploadedFile,
        isAnalyzing: analyzing,
        hasResults,
        selectedPathology: {
          id: selectedImmunoBlot.id,
          name: selectedImmunoBlot.name,
          color: selectedImmunoBlot.colorScheme,
        },
      },
    })

    return () => {
      setContext({ type: 'general' })
    }
  }, [
    analysisResult,
    selectedImmunoBlot,
    viewMode,
    uploadedFile,
    analyzing,
    hasResults,
    handleAnalyze,
    handleNewAnalysis,
    handleBackToRecentCases,
    setContext,
  ])

  const getImmunoBlotGradient = () => {
    if (!selectedImmunoBlot) return 'linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)'

    const colorScheme = selectedImmunoBlot.colorScheme
    return `linear(135deg, ${colorScheme}.600 0%, ${colorScheme}.500 50%, ${colorScheme}.400 100%)`
  }

  // Écran de sélection d'immunoblot
  // Loading a case from a direct URL / refresh — show a spinner instead of
  // flashing the module-selection or landing screen before the case appears.
  if (caseId && !analysisResult) {
    return (
      <VStack h="100vh" justify="center" spacing={4} bg="gray.50">
        <Spinner size="xl" color="teal.500" thickness="3px" speed="0.7s" />
        <Text color="gray.500" fontWeight="medium">{t('shared.common.loading', 'Chargement…')}</Text>
      </VStack>
    )
  }

  if (viewMode === 'immunoblot-selection') {
    return (
      <Box h="100vh" overflow="hidden" bg="gray.50" p={6}>
        <VStack spacing={6} align="stretch" h="100%" overflowY="auto">
          {/* Header */}
          <Box
            bgGradient={brand.gradient}
            borderRadius="2xl"
            p={6}
            position="relative"
            overflow="hidden"
            boxShadow="2xl"
            flexShrink={0}
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          >
            <HStack justify="space-between" position="relative" zIndex={1}>
              <HStack spacing={4}>
                <IconButton
                  aria-label="Go Back"
                  icon={<MdArrowBack />}
                  onClick={handleSmartBack}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  size="lg"
                />
                <Box
                  p={3}
                  bg="whiteAlpha.300"
                  backdropFilter="blur(10px)"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="whiteAlpha.400"
                >
                  <Icon as={LuActivity} boxSize={8} color="white" />
                </Box>
                <VStack spacing={1} align="start">
                  <HStack spacing={2.5} align="baseline">
                    <Heading size="lg" color="white" fontWeight="bold" letterSpacing="tight">
                      {t(brand.titleKey)}
                    </Heading>
                    <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                      {t(brand.subtitleKey)}
                    </Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Badge colorScheme="green" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                      AI-Powered
                    </Badge>
                    {/* Le journal des nouveautés est celui d'IRIS. */}
                    {brand.whatsNew && (
                      <Tooltip label="Voir les nouveautés du module" hasArrow openDelay={200}>
                        <Badge
                          as="button"
                          onClick={onWhatsNewOpen}
                          colorScheme="orange"
                          variant="solid"
                          fontSize="2xs"
                          px={1.5}
                          py={0.5}
                          borderRadius="md"
                          cursor="pointer"
                          sx={whatsNewUnseen ? {
                            animation: 'updatedPulse 1.6s ease-in-out infinite',
                            '@keyframes updatedPulse': {
                              '0%, 100%': { boxShadow: '0 0 0 0 rgba(251,146,60,0.6)' },
                              '50%': { boxShadow: '0 0 0 5px rgba(251,146,60,0)' },
                            },
                          } : undefined}
                        >
                          UPDATED
                        </Badge>
                      </Tooltip>
                    )}
                  </HStack>
                </VStack>
              </HStack>
              <Tooltip label={t('blotAnalysis.tour.replayHint')} placement="bottom-end" hasArrow>
                <IconButton
                  aria-label={t('blotAnalysis.tour.replayHint')}
                  icon={<LuCircleHelp />}
                  onClick={() => setTourPhase(0)}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  size="lg"
                  borderRadius="full"
                />
              </Tooltip>
            </HStack>
          </Box>

          {/* Admin-only: test the workspace as a specific CHU config (Nancy, Lyon…).
              Pas dans PROTIS : son profil de détection est dicté par le TEST
              (voir le serveur, detection_profile=protis), un choix de CHU n'y
              ferait que mentir. */}
          {module === 'iris' && (
            <HStack justify="flex-end" px={1}>
              <AdminCHUSelector />
            </HStack>
          )}

          {/* ImmunoBlot Selection */}
          <Box
            bg="white"
            p={6}
            borderRadius="xl"
            shadow="lg"
            border="1px solid"
            borderColor="gray.100"
          >
            <ImmunoBlotSelection onSelect={handleImmunoBlotSelect} module={module} />
          </Box>
        </VStack>

        {/* First-visit onboarding tour (also relaunchable via the ? button) */}
        <BlotOnboardingTour
          currentView={viewMode}
          phase={tourPhase}
          setPhase={setTourPhase}
          onChangeView={setViewMode}
          blockedByWhatsNew={isWhatsNewOpen}
        />
        <BlotWhatsNew isOpen={isWhatsNewOpen} onClose={onWhatsNewClose} />
        <FeedbackModal isOpen={isFeedbackOpen} onClose={onFeedbackClose} onSubmitted={onFeedbackSubmitted} context={feedbackContextRef.current} />
        <BugReportModal isOpen={isBugReportOpen} onClose={onBugReportClose} context={{ viewMode }} />
      </Box>
    )
  }

  return (
    <VStack spacing={4} align="stretch" h="100%" w="100%" maxW="1400px" mx="auto" px={4}>
      {/* ── Admin-shadow read-only banner ──────────────────────────────────
          Visible only when an admin opens a case they don't own. Makes the
          mode explicit so the supervisor knows clicks are gated, and surfaces
          the owner's identity so audit is obvious. Buttons (Relancer, Ajouter
          blot, verdict, drag/rename of bands) are disabled in this mode. */}
      {isReadOnly && (
        <Box
          bgGradient="linear(to-r, amber.50, orange.50)"
          border="1px solid"
          borderColor="orange.300"
          borderRadius="xl"
          px={4}
          py={3}
          boxShadow="0 4px 14px -4px rgba(249, 115, 22, 0.25)"
        >
          <HStack spacing={3} align="center">
            <Box w="40px" h="40px" borderRadius="lg" bg="orange.100" display="grid" sx={{ placeItems: 'center' }} flexShrink={0}>
              <Text fontSize="xl">🛡️</Text>
            </Box>
            <VStack align="start" spacing={0} flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="700" color="orange.800">
                {isAdminShadow
                  ? 'Mode lecture admin — modifications désactivées'
                  : 'Lecture seule — cas partagé en consultation'}
              </Text>
              <Text fontSize="xs" color="orange.700" noOfLines={1}>
                Ce cas appartient à{' '}
                <strong>{caseOwnerInfo?.contactName ?? caseOwnerInfo?.email ?? 'un autre utilisateur'}</strong>
                {caseOwnerInfo?.companyName ? ` (${caseOwnerInfo.companyName})` : ''}.
                Vous pouvez consulter et exporter, mais pas modifier.
              </Text>
            </VStack>
            {cameFromAdminPilotsRef.current && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="orange"
                onClick={() => navigate('/admin/pilots')}
              >
                ← Retour aux pilotes
              </Button>
            )}
            <Badge colorScheme="orange" variant="solid" borderRadius="full" px={2.5} py={0.5} fontSize="2xs">
              {isAdminShadow ? 'ADMIN SHADOW' : 'LECTURE SEULE'}
            </Badge>
          </HStack>
        </Box>
      )}

      {/* Header */}
      <Box
        bgGradient={brand.gradient}
        borderRadius="2xl"
        px={5}
        py={3}
        position="relative"
        overflow="hidden"
        boxShadow="2xl"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" position="relative" zIndex={1}>
          <HStack spacing={4}>
            <IconButton
              aria-label="Go Back"
              icon={<MdArrowBack />}
              onClick={handleSmartBack}
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              size="md"
            />
            <Box
              p={2.5}
              bg="whiteAlpha.300"
              backdropFilter="blur(10px)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.400"
            >
              <Icon as={LuActivity} boxSize={6} color="white" />
            </Box>
            <VStack spacing={0.5} align="start">
              <HStack spacing={2} align="baseline">
                <Heading size="md" color="white" fontWeight="bold" letterSpacing="tight">
                  {t(brand.titleKey)}
                </Heading>
                <Text fontSize="xs" color="whiteAlpha.700" fontWeight="medium">
                  {t(brand.subtitleKey)}
                </Text>
              </HStack>
              <HStack spacing={1.5}>
                <Badge colorScheme="green" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  AI-Powered
                </Badge>
                {/* Le journal des nouveautés est celui d'IRIS. */}
                {brand.whatsNew && (
                  <Tooltip label="Voir les nouveautés du module" hasArrow openDelay={200}>
                    <Badge
                      as="button"
                      onClick={onWhatsNewOpen}
                      colorScheme="orange"
                      variant="solid"
                      fontSize="2xs"
                      px={1.5}
                      py={0.5}
                      borderRadius="md"
                      cursor="pointer"
                      sx={whatsNewUnseen ? {
                        animation: 'updatedPulse 1.6s ease-in-out infinite',
                        '@keyframes updatedPulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(251,146,60,0.6)' },
                          '50%': { boxShadow: '0 0 0 5px rgba(251,146,60,0)' },
                        },
                      } : undefined}
                    >
                      UPDATED
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={3}>
            {/* A share recipient (viewer or collaborateur) didn't choose to
                share this case — showing them "Inviter" invites them to
                re-share someone else's case, and the premium upsell makes
                no sense either since they're not the one picking a plan.
                Show who invited them instead. */}
            {selectedCaseId && !isOwner && mySharedBy ? (
              <Tooltip label={mySharedBy.email} hasArrow placement="bottom">
                <HStack
                  spacing={2}
                  bg="whiteAlpha.200"
                  px={3}
                  py={1.5}
                  borderRadius="xl"
                >
                  <Icon as={LuUserCheck} color="white" boxSize={4} />
                  <Text fontSize="sm" color="white">
                    Invité(e) par {mySharedBy.contactName || mySharedBy.email}
                  </Text>
                </HStack>
              </Tooltip>
            ) : selectedCaseId && (
              aiAccessible ? (
                <Button
                  leftIcon={<Icon as={LuShare2} />}
                  onClick={onCollabOpen}
                  variant="solid"
                  bg="whiteAlpha.300"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.400' }}
                  size="sm"
                  borderRadius="xl"
                >
                  {t('chu.collaboration.invite')}
                </Button>
              ) : (
                <Tooltip
                  label={
                    <Box>
                      <Text fontWeight="bold" mb={1}>✦ Fonctionnalité Premium</Text>
                      <Text fontSize="xs" color="whiteAlpha.800">
                        Partagez ce cas avec un collègue, invitez un expert ou collaborez en temps réel.
                      </Text>
                    </Box>
                  }
                  placement="bottom"
                  hasArrow
                  bg="gray.900"
                  color="white"
                  borderRadius="xl"
                  px={4}
                  py={3}
                >
                  <Box position="relative" display="inline-flex">
                    <Button
                      leftIcon={<Icon as={LuShare2} />}
                      onClick={() => navigate('/change-plan')}
                      size="sm"
                      borderRadius="xl"
                      bg="rgba(255,255,255,0.12)"
                      color="white"
                      border="1px solid"
                      borderColor="rgba(251,191,36,0.35)"
                      _hover={{
                        bg: 'rgba(251,191,36,0.18)',
                        borderColor: 'rgba(251,191,36,0.6)',
                        transform: 'translateY(-1px)',
                      }}
                      transition="all 0.2s"
                    >
                      Partager le cas
                    </Button>
                    <Box
                      position="absolute"
                      top="-4px"
                      right="-4px"
                      w="16px"
                      h="16px"
                      borderRadius="full"
                      bg="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow="0 2px 6px rgba(0,0,0,0.25)"
                    >
                      <Icon as={LuLock} boxSize={2.5} color="gray.600" />
                    </Box>
                  </Box>
                </Tooltip>
              )
            )}
            <Button
              data-tour-id="btn-new-analysis"
              leftIcon={
                viewMode === 'new-analysis' && !selectedImmunoBlot
                  ? <MdArrowBack />
                  : viewMode === 'new-analysis' ? <MdRefresh /> : <MdAdd />
              }
              onClick={() => {
                // Une analyse en cours n'est pas interrompable : le cas est
                // déjà créé côté serveur et les crédits sont engagés.
                if (analyzing || reanalyzing) return
                if (viewMode === 'case-details' && !isReadOnly && pendingValidationCount > 0) {
                  setNewAnalysisBlocked(true)
                  return
                }
                handleNewAnalysis()
              }}
              isDisabled={analyzing || reanalyzing}
              variant="solid"
              bg="whiteAlpha.300"
              color="white"
              _hover={{ bg: 'whiteAlpha.400' }}
              _disabled={{ bg: 'whiteAlpha.200', color: 'whiteAlpha.600', cursor: 'not-allowed' }}
              transition="all 0.3s"
              size="md"
              borderRadius="xl"
            >
              {viewMode === 'new-analysis' && !selectedImmunoBlot
                ? t('blotAnalysis.chooseTest')
                : viewMode === 'new-analysis'
                  ? t('blotAnalysis.reset')
                  : t('blotAnalysis.newAnalysis')}
            </Button>
            <Tooltip label={t('blotAnalysis.tour.replayHint')} placement="bottom-end" hasArrow>
              <IconButton
                aria-label={t('blotAnalysis.tour.replayHint')}
                icon={<LuCircleHelp />}
                onClick={() => setTourPhase(0)}
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.300' }}
                size="md"
                borderRadius="full"
              />
            </Tooltip>
          </HStack>
        </HStack>
      </Box>

      {/* ImmunoBlot Badge */}
      {selectedImmunoBlot && (
        <Box
          bg="white"
          px={5}
          py={3}
          borderRadius="xl"
          shadow="lg"
          border="1px solid"
          borderColor="gray.100"
        >
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Box
                p={2.5}
                borderRadius="xl"
                bgGradient={getImmunoBlotGradient()}
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="md"
              >
                <Icon as={LuActivity} boxSize={5} color="white" />
              </Box>
              <VStack spacing={0} align="start">
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  {t('blotAnalysis.workspace.selectedAnalysis')}
                </Text>
                <Text fontSize="md" fontWeight="bold" color="gray.800">
                  {pathologyDisplayName(selectedImmunoBlot.name)}
                </Text>
              </VStack>

              {/* Info Button with Popover */}
              <Popover placement="bottom-start" trigger="click">
                <PopoverTrigger>
                  <IconButton
                    aria-label="Guide d'analyse et performances"
                    icon={loadingConfig ? <Spinner size="xs" /> : <Icon as={LuInfo} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    borderRadius="full"
                    isDisabled={loadingConfig}
                    title="Guide d'analyse et performances"
                  />
                </PopoverTrigger>
                <PopoverContent w="400px" maxH="500px" overflowY="auto">
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="bold" borderBottomWidth="1px">
                    <HStack>
                      <Icon as={LuFileText} color="teal.500" />
                      <Text>Guide d'analyse - {pathologyDisplayName(selectedImmunoBlot.name)}</Text>
                    </HStack>
                  </PopoverHeader>
                  <PopoverBody>
                    {immunoblotConfig ? (
                      <VStack spacing={4} align="stretch">
                        {/* Performance Section */}
                        {immunoblotConfig.clinicalNotes?.performance && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                              Performances du test
                            </Text>
                            <HStack spacing={4} bg="teal.50" p={3} borderRadius="md">
                              <Box textAlign="center">
                                <Text fontSize="lg" fontWeight="bold" color="green.600">
                                  {immunoblotConfig.clinicalNotes.performance.sensitivity}
                                </Text>
                                <Text fontSize="xs" color="gray.600">Sensibilité</Text>
                                {immunoblotConfig.clinicalNotes.performance.sensitivityCI && (
                                  <Text fontSize="2xs" color="gray.500">
                                    IC: {immunoblotConfig.clinicalNotes.performance.sensitivityCI}
                                  </Text>
                                )}
                              </Box>
                              <Divider orientation="vertical" h="50px" />
                              <Box textAlign="center">
                                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                                  {immunoblotConfig.clinicalNotes.performance.specificity}
                                </Text>
                                <Text fontSize="xs" color="gray.600">Spécificité</Text>
                                {immunoblotConfig.clinicalNotes.performance.specificityCI && (
                                  <Text fontSize="2xs" color="gray.500">
                                    IC: {immunoblotConfig.clinicalNotes.performance.specificityCI}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          </Box>
                        )}

                        {/* Interpretation Criteria */}
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                            Critères d'interprétation
                          </Text>
                          <VStack spacing={2} align="stretch">
                            <Box bg="green.50" p={2} borderRadius="md" borderLeft="3px solid" borderLeftColor="green.400">
                              <Text fontSize="xs" fontWeight="bold" color="green.700">POSITIF</Text>
                              <Text fontSize="xs" color="gray.700">
                                {immunoblotConfig.interpretation?.positive?.criteria?.map((c: any) => c.description).join(' ET ') || 'Non défini'}
                              </Text>
                            </Box>
                            <Box bg="red.50" p={2} borderRadius="md" borderLeft="3px solid" borderLeftColor="red.400">
                              <Text fontSize="xs" fontWeight="bold" color="red.700">NEGATIF</Text>
                              <Text fontSize="xs" color="gray.700">
                                {immunoblotConfig.interpretation?.negative?.criteria?.map((c: any) => c.description).join(' ') || 'Non défini'}
                              </Text>
                            </Box>
                          </VStack>
                        </Box>

                        {/* Bands to Identify */}
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                            Bandes à identifier ({immunoblotConfig.bands?.reference?.primaryZone || '30-45 kDa'})
                          </Text>
                          <List spacing={1}>
                            {immunoblotConfig.bands?.specific?.map((band: any) => (
                              <ListItem key={band.id} fontSize="xs">
                                {/* Colour code shared with the gallery + PDF report:
                                    green is reserved for the témoin, so expected
                                    (auto-detected) bands use the "détectée" purple. */}
                                <ListIcon
                                  as={band.required ? MdWarning : MdCheckCircle}
                                  color={band.required ? 'red.500' : 'purple.400'}
                                />
                                <Text as="span" fontWeight={band.required ? 'bold' : 'normal'}>
                                  {band.name} ({band.molecularWeight} kDa)
                                </Text>
                                {band.required && (
                                  <Badge ml={1} colorScheme="red" fontSize="2xs">Obligatoire</Badge>
                                )}
                              </ListItem>
                            ))}
                            {(immunoblotConfig.bands as any)?.intermediate?.map((band: any) => (
                              <ListItem key={band.id} fontSize="xs">
                                <ListIcon as={MdCheckCircle} color="purple.400" />
                                <Text as="span">
                                  {band.name} ({band.molecularWeight} kDa)
                                </Text>
                                {band.species && band.species !== 'common' && (
                                  <Badge ml={1} colorScheme="purple" fontSize="2xs" fontStyle="italic">
                                    {band.species}
                                  </Badge>
                                )}
                              </ListItem>
                            ))}
                            {immunoblotConfig.bands?.nonSpecific?.map((band: any) => (
                              <ListItem key={band.id} fontSize="xs" color="gray.500">
                                <ListIcon as={MdWarning} color="gray.400" />
                                <Text as="span">{band.name} - Non spécifique (ignorer)</Text>
                              </ListItem>
                            ))}
                          </List>
                          {((immunoblotConfig.bands as any)?.intermediate?.length ?? 0) > 0 && (
                            <Text fontSize="2xs" color="gray.500" mt={1} fontStyle="italic">
                              Les bandes intermédiaires (violet) servent au diagnostic d'espèce.
                            </Text>
                          )}
                        </Box>

                        {/* Species-differentiation profiles (Echinococcus P1-P5) */}
                        {(immunoblotConfig.interpretation as any)?.profiles && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                              Diagnostic d'espèce
                            </Text>
                            <VStack spacing={1.5} align="stretch">
                              {Object.values((immunoblotConfig.interpretation as any).profiles as Record<string, any>).map((p: any) => {
                                const known = p.species && p.species !== 'indéterminé'
                                return (
                                  <Box
                                    key={p.name}
                                    bg={known ? 'purple.50' : 'gray.50'}
                                    p={2}
                                    borderRadius="md"
                                    borderLeft="3px solid"
                                    borderLeftColor={known ? 'purple.300' : 'gray.300'}
                                  >
                                    <HStack justify="space-between" mb={0.5}>
                                      <Text fontSize="xs" fontWeight="bold" color={known ? 'purple.700' : 'gray.600'}>
                                        {p.name}
                                      </Text>
                                      <Text fontSize="xs" fontWeight="bold" fontStyle="italic" color={known ? 'purple.700' : 'gray.500'}>
                                        {p.species}
                                      </Text>
                                    </HStack>
                                    <Text fontSize="2xs" color="gray.600" lineHeight="1.4">
                                      {p.description}
                                    </Text>
                                  </Box>
                                )
                              })}
                            </VStack>
                          </Box>
                        )}

                        {/* Quality Control — paired-comparison pathologies (CIP-WB) have no
                            témoin at all, so positiveControl/negativeControl descriptions
                            don't apply; show their own analysisGuide instead (see below). */}
                        {immunoblotConfig.qualityControl?.positiveControl && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                              Contrôle qualité
                            </Text>
                            <VStack spacing={1} align="stretch" fontSize="xs">
                              <Text>
                                <Text as="span" fontWeight="bold">Témoin positif:</Text>{' '}
                                {immunoblotConfig.qualityControl.positiveControl?.description}
                              </Text>
                              <Text>
                                <Text as="span" fontWeight="bold">Témoin négatif:</Text>{' '}
                                {immunoblotConfig.qualityControl.negativeControl?.description}
                              </Text>
                            </VStack>
                          </Box>
                        )}

                        {/* Guide d'analyse (comparaison par paire — CIP-WB) — même
                            emplacement standard que pour tous les autres tests. */}
                        {(immunoblotConfig.qualityControl as any)?.analysisGuide && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm" color="teal.600" mb={2}>
                              {(immunoblotConfig.qualityControl as any).analysisGuide.title || 'Guide d\'analyse (comparaison par paire)'}
                            </Text>
                            <VStack spacing={3} align="stretch" fontSize="xs">
                              {(immunoblotConfig.qualityControl as any).analysisGuide.summary && (
                                <Text color="gray.700">{(immunoblotConfig.qualityControl as any).analysisGuide.summary}</Text>
                              )}
                              {(immunoblotConfig.qualityControl as any).analysisGuide.steps?.length > 0 && (
                                <Box>
                                  <Text fontWeight="bold" mb={1}>Étapes :</Text>
                                  <List as="ol" spacing={1} pl={4} sx={{ listStyleType: 'decimal' }}>
                                    {(immunoblotConfig.qualityControl as any).analysisGuide.steps.map((s: string, i: number) => (
                                      <ListItem key={i}>{s}</ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                              {(immunoblotConfig.qualityControl as any).analysisGuide.warnings?.length > 0 && (
                                <Box>
                                  <Text fontWeight="bold" mb={1} color="orange.600">Avertissements :</Text>
                                  <List spacing={1} color="orange.700">
                                    {(immunoblotConfig.qualityControl as any).analysisGuide.warnings.map((s: string, i: number) => (
                                      <ListItem key={i}>
                                        <ListIcon as={MdWarning} color="orange.400" />
                                        {s}
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                              {(immunoblotConfig.qualityControl as any).analysisGuide.goodPractice?.length > 0 && (
                                <Box>
                                  <Text fontWeight="bold" mb={1} color="green.700">Bonnes pratiques :</Text>
                                  <List spacing={1} color="green.700">
                                    {(immunoblotConfig.qualityControl as any).analysisGuide.goodPractice.map((s: string, i: number) => (
                                      <ListItem key={i}>
                                        <ListIcon as={MdCheckCircle} color="green.400" />
                                        {s}
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                            </VStack>
                          </Box>
                        )}

                        {/* Limitations */}
                        {immunoblotConfig.clinicalNotes?.limitations && (
                          <Box>
                            <Text fontWeight="bold" fontSize="sm" color="orange.600" mb={2}>
                              Limitations
                            </Text>
                            <List spacing={1} fontSize="xs" color="gray.600">
                              {immunoblotConfig.clinicalNotes.limitations.map((limitation: string, idx: number) => (
                                <ListItem key={idx}>
                                  <ListIcon as={MdWarning} color="orange.400" />
                                  {limitation}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <Box textAlign="center" py={4}>
                        <Text color="gray.500" fontSize="sm">
                          Configuration non disponible pour ce test
                        </Text>
                      </Box>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </HStack>
            {/* Pas de « Modifier » quand il n'y a rien d'autre à choisir. */}
            {!soleBlot && (
              <Button
                leftIcon={<Icon as={MdEdit} />}
                size="sm"
                variant="outline"
                colorScheme="teal"
                borderRadius="lg"
                onClick={handleChangeImmunoBlot}
              >
                {t('blotAnalysis.workspace.change')}
              </Button>
            )}
          </HStack>
        </Box>
      )}

      {/* Main Content Area */}
      <Box flex={1} overflow="auto">
        {/* Recent Cases View */}
        {viewMode === 'recent-cases' && (
          <Box>
            <VStack spacing={4} align="stretch">
              <HStack spacing={2.5} align="center">
                <Box
                  w={7}
                  h={7}
                  borderRadius="lg"
                  bgGradient="linear(135deg, teal.50, cyan.50)"
                  border="1px solid"
                  borderColor="teal.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={LuHistory} boxSize={4} color="teal.500" />
                </Box>
                <Heading
                  size="md"
                  bgGradient="linear(135deg, teal.600, cyan.500)"
                  bgClip="text"
                  letterSpacing="-0.01em"
                >
                  {t('blotAnalysis.workspace.recentWesternBlotAnalyses')}
                </Heading>
              </HStack>
              <BlotRecentCases
                limit={15}
                onCaseSelect={(c: any) => handleRecentCaseSelect(c, selectedImmunoBlot?.id)}
                selectedCaseId={selectedCaseId}
                pathologyId={selectedImmunoBlot?.id}
              />
            </VStack>
          </Box>
        )}

        {/* New Analysis View */}
        {viewMode === 'new-analysis' && (
          <VStack spacing={5} align="stretch" maxW="1000px" mx="auto">
            {!uploadedFile ? (
              <>
                {/* Upload File */}
                <Box>
                  <Box data-tour-id="blot-upload" bg="white" p={6} borderRadius="xl" shadow="lg" border="1px solid" borderColor="gray.100">
                    <BlotFileUpload
                      onFileUploaded={handleFileUploaded}
                      disabled={analyzing}
                    />
                  </Box>
                </Box>

                {/* Recent analyses — tucked under the upload, collapsed by default,
                    so picking a test lands on the upload (not the history). */}
                <Box>
                  <HStack justify="space-between" align="center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onToggleRecent}
                      leftIcon={<Icon as={LuHistory} boxSize={4} />}
                      rightIcon={<Icon as={isRecentOpen ? LuChevronUp : LuChevronDown} boxSize={4} />}
                      color="gray.600"
                      fontWeight="600"
                    >
                      {t('blotAnalysis.workspace.recentWesternBlotAnalyses')}
                    </Button>
                    {/* "Tout voir" → the full list WITH filters (owner / période),
                        which is otherwise not directly reachable from here. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      colorScheme="purple"
                      onClick={handleBackToRecentCases}
                      rightIcon={<Icon as={LuArrowRight} boxSize={4} />}
                      fontWeight="600"
                    >
                      Tout voir
                    </Button>
                  </HStack>
                  <Collapse in={isRecentOpen} animateOpacity>
                    <Box mt={3}>
                      <BlotRecentCases
                        limit={3}
                        onCaseSelect={(c: any) => handleRecentCaseSelect(c, selectedImmunoBlot?.id)}
                        selectedCaseId={selectedCaseId}
                        pathologyId={selectedImmunoBlot?.id}
                        showFilters={false}
                      />
                    </Box>
                  </Collapse>
                </Box>
              </>
            ) : (
              <>
                {/* Sticky "verify & launch" bar — image loaded, so put it forward
                    and keep the launch button visible without scrolling. */}
                <Box position="sticky" top={0} zIndex={3} bg="#F1EFEE" pt={1} pb={2}>
                  <HStack
                    data-tour-id="btn-analyze"
                    justify="space-between"
                    bg="white"
                    borderRadius="xl"
                    shadow="md"
                    border="1px solid"
                    borderColor="teal.100"
                    px={{ base: 3, md: 4 }}
                    py={3}
                    spacing={3}
                  >
                    <HStack spacing={3} minW={0}>
                      <Box w="38px" h="38px" borderRadius="lg" bg="teal.50" display="grid" sx={{ placeItems: 'center' }} flexShrink={0}>
                        <Icon as={LuCircleCheck} boxSize={5} color="teal.500" />
                      </Box>
                      <VStack align="start" spacing={0} minW={0}>
                        <Text fontWeight="700" fontSize="sm" color="gray.800" noOfLines={1}>
                          {analyzing
                            ? analysisProgress.label
                            : t('blotAnalysis.workspace.step2VerifyAndLaunch')}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {uploadedFile.name} · {(uploadedFile.size / 1024 / 1024).toFixed(1)} MB
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={3} flexShrink={0}>
                      {/* Le mode se choisit ICI, avant de lancer : le cas naît
                          voilé et rien n'est à défaire. Basculer un cas déjà
                          analysé reste possible depuis le cas lui-même, mais
                          cela oblige à relancer et détruit le travail fait.
                          IRIS seulement : le rétrospectif mesure l'accord avec
                          une lecture de paillasse à la notice — PROTIS n'a ni
                          notice ni verdict à confronter. */}
                      {module === 'iris' && (
                      <Tooltip
                        hasArrow
                        placement="top"
                        maxW="320px"
                        label="Vous saisissez d'abord votre lecture visuelle, bandelette par bandelette ; IRIS ne se dévoile qu'une fois cette saisie verrouillée. Le témoin se désigne, mais se calibre après le verrouillage — ses bandes révéleraient où chercher."
                      >
                        <HStack
                          spacing={2}
                          px={3}
                          py={2}
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor={retrospectiveUpload ? 'purple.300' : 'gray.200'}
                          bg={retrospectiveUpload ? 'purple.50' : 'transparent'}
                          position="relative"
                        >
                          {/* Nouveauté du 29 octobre — même signal que l'onglet
                              Registre de la barre latérale ; à retirer quand le
                              geste sera entré dans les habitudes. */}
                          <Badge
                            position="absolute"
                            top="-9px"
                            right="-8px"
                            colorScheme="purple"
                            variant="solid"
                            fontSize="2xs"
                            px={1.5}
                            py={0}
                            borderRadius="md"
                            boxShadow="0 0 10px rgba(168,85,247,0.45)"
                            sx={{
                              animation: 'retroNewPulse 1.8s ease-in-out infinite',
                              '@keyframes retroNewPulse': {
                                '0%, 100%': { boxShadow: '0 0 0 0 rgba(168,85,247,0.45)' },
                                '50%': { boxShadow: '0 0 0 5px rgba(168,85,247,0)' },
                              },
                            }}
                          >
                            Nouveau
                          </Badge>
                          <Icon
                            as={retrospectiveUpload ? LuEyeOff : LuEye}
                            boxSize={4}
                            color={retrospectiveUpload ? 'purple.600' : 'gray.400'}
                          />
                          <Text fontSize="sm" color={retrospectiveUpload ? 'purple.700' : 'gray.600'} whiteSpace="nowrap">
                            Rétrospectif
                          </Text>
                          <Switch
                            size="sm"
                            colorScheme="purple"
                            isChecked={retrospectiveUpload}
                            isDisabled={analyzing}
                            onChange={(e) => setRetrospectiveUpload(e.target.checked)}
                          />
                        </HStack>
                      </Tooltip>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Icon as={MdRefresh} />}
                        color="gray.600"
                        isDisabled={analyzing}
                        onClick={() => {
                          if (sourceImageDataUrl?.startsWith('blob:')) URL.revokeObjectURL(sourceImageDataUrl)
                          setUploadedFile(null)
                          setSourceImageDataUrl(null)
                        }}
                      >
                        {t('blotAnalysis.workspace.change')}
                      </Button>
                      <Button
                        colorScheme="teal"
                        size="lg"
                        onClick={handleAnalyze}
                        isLoading={analyzing}
                        loadingText={chuFeatures.ui_labels.analysis_button_loading}
                        leftIcon={<Icon as={LuScanLine} />}
                      >
                        {chuFeatures.ui_labels.analysis_button}
                      </Button>
                    </HStack>
                  </HStack>
                </Box>

                {/* Avancement — le pipeline ne renvoie rien avant d'avoir fini,
                    donc à défaut de progression réelle on nomme les étapes
                    traversées et on affiche le temps écoulé (voir
                    AnalysisProgress). Utile surtout en staging, où la machine
                    est lente et le premier lancement charge les modèles. */}
                {analyzing && <AnalysisProgress progress={analysisProgress} />}

                {/* Loaded raw image — front and centre, capped so it fits without scroll */}
                <Box bg="white" p={{ base: 3, md: 4 }} borderRadius="xl" shadow="lg" border="1px solid" borderColor="gray.100">
                  {sourceImageDataUrl ? (
                    <img
                      src={sourceImageDataUrl}
                      alt="Western Blot"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                      }}
                      onError={(e) => {
                        // Le navigateur ne peut pas afficher ce format (ex: TIFF non compressé)
                        e.currentTarget.style.display = 'none'
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                        if (fallback) fallback.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <Box
                    display="none"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={10}
                    gap={2}
                  >
                    <Icon as={LuFileText} boxSize={12} color="gray.400" />
                    <Text color="gray.600" fontWeight="semibold">{uploadedFile.name}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {(uploadedFile.size / 1024 / 1024).toFixed(1)} MB — aperçu non disponible dans le navigateur
                    </Text>
                    <Text fontSize="xs" color="gray.400">Le fichier sera analysé normalement</Text>
                  </Box>
                </Box>
              </>
            )}
          </VStack>
        )}

        {/* Case Details View */}
        {viewMode === 'case-details' && analysisResult && (
          <VStack spacing={6} align="stretch" maxW="1200px" mx="auto">
            <HStack justify="space-between">
              <Box>
                <Heading
                  size="lg"
                  bgGradient="linear(135deg, teal.600, cyan.500)"
                  bgClip="text"
                  letterSpacing="-0.02em"
                >
                  {t('blotAnalysis.workspace.analysisResults')}
                </Heading>
                <HStack spacing={2} mt={1.5}>
                  <Box w={2} h={2} borderRadius="full" bgGradient="linear(to-r, teal.400, cyan.400)" />
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" letterSpacing="0.04em" textTransform="uppercase">
                    {pathologyDisplayName(analysisResult.pathology_name) || 'Western Blot'}
                  </Text>
                </HStack>
                {analysisResult.timestamp && (
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    Analyse lancée le{' '}
                    {new Date(analysisResult.timestamp).toLocaleDateString('fr-FR', {
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </Text>
                )}
              </Box>

              {/* Analysis Summary */}
              <HStack spacing={6} bg="white" p={4} borderRadius="xl" borderWidth={1} borderColor="gray.200" shadow="md">
                <Box textAlign="center">
                  <Text fontSize="xs" color="gray.600">{t('blotAnalysis.workspace.detectedBlots')}</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                    {(analysisResult.blots ?? []).filter(
                      (b: any) => !((analysisResult as any).hiddenBlotIds ?? []).includes(b.id)
                    ).length}
                  </Text>
                </Box>
                <Divider orientation="vertical" h="50px" />
                <Box textAlign="center">
                  <Text fontSize="xs" color="gray.600">{t('blotAnalysis.workspace.time')}</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.600">
                    {analysisResult.processing_time_seconds.toFixed(2)}s
                  </Text>
                </Box>
                {analysisResult.detection_profile && (
                  <>
                    <Divider orientation="vertical" h="50px" />
                    <Tooltip label={t('blotAnalysis.workspace.detectionProfileTooltip', { defaultValue: 'Profil de détection utilisé pour adapter les paramètres au type de scanner' })}>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="gray.600">{t('blotAnalysis.workspace.detectionProfile', { defaultValue: 'Profil' })}</Text>
                        <HStack spacing={1.5} justify="center">
                          <Icon as={LuBuilding2} boxSize={4} color="purple.500" />
                          <Text fontSize="md" fontWeight="bold" color="purple.600">
                            {analysisResult.detection_profile.toUpperCase()}
                          </Text>
                        </HStack>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </HStack>
            </HStack>

            {/* AUCUNE NOTICE NE S'APPLIQUE : le dire, plutôt que de laisser
                l'écran vide.

                Sans configuration, IRIS n'a ni gabarit de bandes ni règles de
                lecture : désigner le témoin ne place rien, aucune conclusion
                n'est déduite, et le mode rétrospectif n'a rien à proposer. Ces
                trois absences ont la même cause, et n'avaient aucune
                explication à l'écran — seulement un message dans la console.

                Le cas typique : une plaque analysée sous « DYNABLOT MULTITEST »,
                qui n'est pas un test mais un contenant. Les recherches y sont
                lues une par une sur le tableau du rapport PDF du lecteur ; un
                scan d'image ne porte pas ces libellés, donc aucune recherche
                n'est identifiée et aucune notice ne peut s'appliquer. */}
            {!activeConfig && !loadingConfig && !hasPathologyLabels && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="xl" p={4}>
                <HStack align="start" spacing={3}>
                  <Icon as={MdWarning} color="orange.500" boxSize={5} mt={0.5} flexShrink={0} />
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color="orange.800">
                      Aucune notice ne s'applique à ce cas
                    </Text>
                    <Text fontSize="sm" color="orange.800" mt={1}>
                      Les bandelettes sont bien détectées, mais sans notice IRIS n'a ni gabarit de
                      bandes ni règles de lecture : <b>désigner le témoin ne place aucune bande</b>,
                      aucune conclusion n'est déduite, et le mode rétrospectif reste indisponible.
                    </Text>
                    {analysisResult?.pathology_name === 'DYNABLOT MULTITEST' ? (
                      <Text fontSize="sm" color="orange.700" mt={2}>
                        Ce cas a été lancé sous <b>DYNABLOT MULTITEST</b>, qui n'est pas un test mais
                        une plaque mixte : chaque recherche y est lue sur le tableau du rapport PDF du
                        lecteur Dynablot. Un scan d'image ne porte pas ces libellés. Relancez l'analyse
                        en choisissant la recherche réellement portée par la plaque.
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="orange.700" mt={2}>
                        La notice de <b>{analysisResult?.pathology_name ?? 'ce test'}</b> n'est pas
                        enregistrée en base. Signalez-le pour qu'elle soit ajoutée.
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>
            )}

            {/* ── Phase 2 upsell — Advanced AI modules ─────────────────── */}
            {isPremiumLocked && (
              <Box
                position="relative"
                overflow="hidden"
                bg="linear-gradient(145deg, #fff7ed 0%, #fff 35%, #fdf4ff 100%)"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="xl"
                p={{ base: 5, md: 6 }}
                boxShadow="lg"
              >
                <Box position="absolute" top="-60px" right="-40px" w="180px" h="180px" borderRadius="full" bg="radial-gradient(circle, rgba(251,191,36,0.28) 0%, rgba(251,191,36,0) 70%)" pointerEvents="none" />
                <Box position="absolute" bottom="-70px" left="-30px" w="180px" h="180px" borderRadius="full" bg="radial-gradient(circle, rgba(217,70,239,0.18) 0%, rgba(217,70,239,0) 72%)" pointerEvents="none" />
                <VStack align="stretch" spacing={5} position="relative" zIndex={1}>
                  <HStack justify="space-between" align={{ base: 'stretch', md: 'center' }} flexDirection={{ base: 'column', md: 'row' }} spacing={4}>
                    <VStack align="start" spacing={2} maxW="720px">
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge bg="orange.500" color="white" px={2.5} py={1} borderRadius="full" fontSize="2xs" letterSpacing="0.04em">{t('chu.premium.beta', 'BETA')}</Badge>
                        <PremiumBadge />
                      </HStack>
                      <HStack spacing={3} align="center">
                        <Box w="42px" h="42px" borderRadius="xl" bg="linear-gradient(135deg, #f59e0b 0%, #d946ef 100%)" display="flex" alignItems="center" justifyContent="center" boxShadow="0 12px 30px rgba(217, 70, 239, 0.18)">
                          <Icon as={LuSparkles} color="white" boxSize={5} />
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" fontSize={{ base: 'lg', md: 'xl' }} color="gray.900">{t('chu.premium.aiModulesTitle')}</Text>
                          <Text fontSize="xs" color="orange.600" fontWeight="semibold" letterSpacing="0.03em">{t('chu.premium.betaNotice', "Accès anticipé aux modules d'IA en cours de validation")}</Text>
                        </VStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.6">{t('chu.premium.aiModulesSubtitle')}</Text>
                    </VStack>
                    <Button alignSelf={{ base: 'stretch', md: 'center' }} colorScheme="orange" bgGradient="linear(to-r, orange.400, pink.500)" color="white" rightIcon={<Icon as={LuArrowRight} />} borderRadius="xl" px={6} _hover={{ bgGradient: 'linear(to-r, orange.500, pink.600)', transform: 'translateY(-1px)', boxShadow: 'lg' }} onClick={() => navigate('/change-plan')}>
                      {t('chu.premium.upgradeCTA', 'Passer au Premium')}
                    </Button>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
                    {[
                      { key: 'interpretation', icon: LuSparkles, color: 'linear(to-br, orange.100, pink.100)', accent: 'orange.500', title: t('chu.premium.interpretationModule'), desc: t('chu.premium.interpretationDesc') },
                      { key: 'profile', icon: LuScanLine, color: 'linear(to-br, blue.100, cyan.100)', accent: 'blue.500', title: t('chu.premium.profileModule'), desc: t('chu.premium.profileDesc') },
                      { key: 'scoring', icon: LuActivity, color: 'linear(to-br, purple.100, pink.100)', accent: 'purple.500', title: t('chu.premium.scoringModule'), desc: t('chu.premium.scoringDesc') },
                    ].map(({ key, icon, color, accent, title, desc }) => (
                      <Box key={key} bg="whiteAlpha.900" backdropFilter="blur(10px)" p={4} borderRadius="xl" border="1px solid" borderColor="whiteAlpha.700" boxShadow="md">
                        <HStack align="start" justify="space-between" spacing={3} mb={4}>
                          <HStack align="start" spacing={3}>
                            <Box w="44px" h="44px" borderRadius="xl" bgGradient={color} color={accent} display="flex" alignItems="center" justifyContent="center" flexShrink={0}><Icon as={icon} boxSize={5} /></Box>
                            <VStack align="start" spacing={1}>
                              <Text fontSize="sm" fontWeight="bold" color="gray.800">{title}</Text>
                              <Text fontSize="xs" color="gray.600" lineHeight="1.55">{desc}</Text>
                            </VStack>
                          </HStack>
                          <VStack spacing={1} align="end" flexShrink={0}>
                            <Badge colorScheme="purple" variant="subtle" borderRadius="full">{t('chu.premium.badge')}</Badge>
                            <Badge colorScheme="orange" variant="subtle" borderRadius="full">{t('chu.premium.beta', 'BETA')}</Badge>
                          </VStack>
                        </HStack>
                        <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                          <Text fontSize="xs" color="gray.500" lineHeight="1.55">{t('chu.premium.cardFootnote', 'Disponible en accès anticipé pour accélérer la validation et enrichir le compte-rendu.')}</Text>
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                </VStack>
              </Box>
            )}

            {/* Avancement de la relance — même jauge que le premier lancement,
                posée en haut du cas puisque c'est le bouton flottant, à droite,
                qui la déclenche (voir AnalysisProgress). */}
            {reanalyzing && <AnalysisProgress progress={analysisProgress} />}

            {/* Source Image — always shown when a case is loaded */}
            {(sourceImageDataUrl || analysisResult) && (
              <Box data-tour-id="blot-source-image">
                <HStack spacing={2.5} mb={3}>
                  <Box w="4px" h="22px" borderRadius="full" bgGradient="linear(to-b, teal.400, cyan.400)" />
                  <Text fontWeight="bold" color="gray.800" fontSize="lg" letterSpacing="-0.01em">
                    {t('blotAnalysis.workspace.sourceImage')}
                  </Text>
                  {/* Uploaded file name — the image is collapsed by default, so this
                      is the only way to tell which scan a case came from without
                      unfolding it. Truncated, full name on hover. */}
                  {(analysisResult?.filename || uploadedFile?.name) && (
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      fontWeight="medium"
                      isTruncated
                      maxW={{ base: '130px', md: '260px', lg: '380px' }}
                      title={analysisResult?.filename || uploadedFile?.name}
                    >
                      {analysisResult?.filename || uploadedFile?.name}
                    </Text>
                  )}
                  <Button
                    size="xs"
                    variant="ghost"
                    color="gray.600"
                    isDisabled={isDrawingMode}
                    rightIcon={<Icon as={(isSourceOpen || isDrawingMode) ? LuChevronUp : LuChevronDown} boxSize={3.5} />}
                    onClick={onToggleSource}
                    flexShrink={0}
                  >
                    {(isSourceOpen || isDrawingMode) ? 'Masquer' : "Afficher l'image"}
                  </Button>
                </HStack>

                {/* ── Operator tracking (always shown when enabled) ─────────── */}
                {chuFeatures.operator.enabled && operatorValidation && (
                  <OperatorBadge
                    operatorName={operatorValidation.name}
                    validatedAt={operatorValidation.at}
                  />
                )}

                <Collapse in={isSourceOpen || isDrawingMode} animateOpacity>
                <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px solid" borderColor={isDrawingMode ? 'orange.300' : 'gray.100'} overflow="visible">
                  <Box
                    position="relative"
                    overflow="visible"
                    cursor={isDrawingMode && !pendingSelection ? 'crosshair' : 'default'}
                    onPointerDown={isDrawingMode && !pendingSelection ? handleDrawPointerDown : undefined}
                    onPointerMove={isDrawingMode && !pendingSelection ? handleDrawPointerMove : undefined}
                    onPointerUp={isDrawingMode && !pendingSelection ? handleDrawPointerUp : undefined}
                    onPointerCancel={isDrawingMode && !pendingSelection ? handleDrawPointerUp : undefined}
                    sx={isDrawingMode ? { userSelect: 'none', touchAction: 'none' } : undefined}
                  >
                    {sourceImageRenderError ? (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2} bg="gray.50" borderRadius="8px">
                        <Icon as={LuFileText} boxSize={12} color="gray.400" />
                        <Text color="gray.600" fontWeight="semibold">{analysisResult.filename}</Text>
                        <Text fontSize="sm" color="gray.500">Aperçu non disponible (format TIFF non compressé)</Text>
                        <Text fontSize="xs" color="gray.400">L'analyse a bien été réalisée — utilisez la galerie ci-dessous</Text>
                      </Box>
                    ) : !sourceImageDataUrl ? (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2} bg="gray.50" borderRadius="8px">
                        <Spinner size="md" color="purple.500" thickness="3px" />
                        <Text fontSize="sm" color="gray.500">Chargement de l'image…</Text>
                      </Box>
                    ) : sourceImageDataUrl === 'error' ? (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2} bg="gray.50" borderRadius="8px">
                        <Icon as={LuFileText} boxSize={12} color="gray.400" />
                        <Text color="gray.600" fontWeight="semibold">{analysisResult.filename}</Text>
                        <Text fontSize="sm" color="gray.500">Fichier image non disponible</Text>
                        <Text fontSize="xs" color="gray.400">Utilisez la galerie de blots ci-dessous</Text>
                      </Box>
                    ) : (
                      <img
                        ref={sourceImageRef}
                        src={showDebugRoi && debugRoiImage ? debugRoiImage : sourceImageDataUrl}
                        alt={showDebugRoi ? "Debug ROI - Hough Lines" : "Western Blot Source"}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '8px',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitUserDrag: 'none',
                          pointerEvents: isDrawingMode ? 'none' : 'auto',
                        } as React.CSSProperties}
                        onLoad={(e) => {
                          const img = e.currentTarget
                          setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight })
                        }}
                        onError={() => setSourceImageRenderError(true)}
                      />
                    )}
                    {/* Trait de collage retenu par l'analyse, tracé en rouge sur toute
                        la largeur. C'est le repère sur lequel les bandelettes sont
                        collées et dont la bande de contrôle dépend : le voir sur
                        l'image brute dit d'un coup d'œil si la détection a visé le
                        bon trait, les chiffres imprimés ou une bande — ce qu'il
                        fallait sinon mesurer cas par cas. Chaque bandelette porte la
                        même position, en fraction de SA propre hauteur ; on prend la
                        médiane pour qu'une bandelette mal cadrée ne déplace pas le
                        trait.

                        Masqué : c'était un outil de mise au point, pas quelque chose
                        à montrer au biologiste. Repasser SHOW_MOUNTING_LINE à true
                        pour le revoir. */}
                    {SHOW_MOUNTING_LINE && showBlotBoxes && imageDimensions && (() => {
                      const roi = analysisResult.roi
                      const backendImgH = (roi?.height && roi.height > 0)
                        ? roi.height
                        : imageDimensions.height * (analysisResult.filename?.toLowerCase().endsWith('.pdf') ? (300 / 72) : 1)
                      const ys = analysisResult.blots
                        .map((b: any) => (typeof b.control_line_frac === 'number'
                          ? b.y_top + b.control_line_frac * (b.y_bottom - b.y_top)
                          : null))
                        .filter((y): y is number => y !== null)
                        .sort((a, b) => a - b)
                      if (!ys.length || !backendImgH) return null
                      const y = ys[Math.floor(ys.length / 2)]
                      const topPct = (((roi?.y || 0) + y) / backendImgH) * 100
                      return (
                        <Box
                          position="absolute"
                          left={0}
                          right={0}
                          top={`${topPct}%`}
                          height="2px"
                          bg="red.500"
                          opacity={0.85}
                          pointerEvents="none"
                          zIndex={15}
                          title={`Trait de collage retenu par l'analyse (y ≈ ${Math.round(y)})`}
                        />
                      )
                    })()}
                    {showBlotBoxes && imageDimensions && analysisResult.blots
                      .filter((blot) => !((analysisResult as any).hiddenBlotIds ?? []).includes(blot.id))
                      .map((blot) => {
                      const roi = analysisResult.roi
                      const roiX = roi?.x || 0
                      const roiY = roi?.y || 0

                      // Use roi.w/roi.h as the backend processing dimensions (accounts for
                      // downsampling, DPI differences between PDF/TIFF and browser display).
                      // Fall back to DPI-scaled naturalWidth only if roi dimensions are unavailable.
                      const isPdf = analysisResult.filename?.toLowerCase().endsWith('.pdf')
                      const dpiScale = isPdf ? (300 / 72) : 1
                      const backendImgW = (roi?.width && roi.width > 0) ? roi.width : imageDimensions.width * dpiScale
                      const backendImgH = (roi?.height && roi.height > 0) ? roi.height : imageDimensions.height * dpiScale

                      // Blot coords (x0,x1,y_top,y_bottom) describe the AABB (axis-aligned bbox) of the OBB.
                      // For tilted strips (Amiens YOLO can return angles up to ~20°), the AABB is larger
                      // than the actual OBB. The backend also returns image_width/image_height = real OBB
                      // dimensions (warped strip dimensions). Use those + the AABB center to draw the OBB.
                      const blotAngle = (blot as any).angle ?? 0
                      const obbW = (blot as any).image_width || (blot.x1 - blot.x0)
                      const obbH = (blot as any).image_height || (blot.y_bottom - blot.y_top)
                      // Amiens strips are landscape (control line runs down the RIGHT
                      // end, vertical); Nancy strips are portrait (control line across
                      // the TOP, horizontal). Drives the control-marker orientation.
                      const isLandscape = obbW > obbH
                      const centerX = (blot.x0 + blot.x1) / 2
                      const centerY = (blot.y_top + blot.y_bottom) / 2

                      // Position = OBB top-left in pre-rotation space, then rotate around its center
                      const leftPct = ((roiX + centerX - obbW / 2) / backendImgW) * 100
                      const topPct = ((roiY + centerY - obbH / 2) / backendImgH) * 100
                      const widthPct = (obbW / backendImgW) * 100
                      const heightPct = (obbH / backendImgH) * 100

                      const isDragging = draggingBox?.id === blot.id
                      const isReanalyzing = repositioningBlotId === blot.id
                      // While re-analysing, keep the box at the dropped position
                      // (optimistic) instead of the stale AI position.
                      const optimistic = isReanalyzing && repositionTarget?.id === blot.id ? repositionTarget : null
                      const dispLeft = isDragging && draggingBox ? draggingBox.leftPct : (optimistic ? optimistic.leftPct : leftPct)
                      const dispTop = isDragging && draggingBox ? draggingBox.topPct : (optimistic ? optimistic.topPct : topPct)
                      // Keep the freshly-set angle during re-analysis too, so a
                      // straightened strip doesn't visibly snap back to its tilt.
                      const dispAngle = optimistic ? optimistic.angle : blotAngle

                      // Control-line marker INSIDE the box: the peak the pipeline
                      // took as the control band, mapped onto the box's own height
                      // (the profile runs along obbH). It refreshes on every drop
                      // (reposition → re-analysis), so the operator drags the box up
                      // until this line sits at the top = control band captured =
                      // the box is aligned on its calibration landmark.
                      const blotProfile = (blot as any).profile as number[] | undefined
                      const controlLineOverride = (analysisResult as any)?.controlLineOverrides?.[String(blot.id)]
                      // PROTIS : pas de ligne de contrôle sur un gel de
                      // recherche — aucun repère à tracer sur l'image brute.
                      const ctrlPos = module !== 'iris'
                        ? 0
                        : typeof controlLineOverride === 'number'
                        ? controlLineOverride
                        : Array.isArray(blotProfile) && blotProfile.length
                          ? resolveControlLinePosition(blotProfile, (blot as any).peaks ?? [], plateControlOffset, (blot as any).control_line_frac)
                          : 0
                      const ctrlFrac = ctrlPos > 0 && blotProfile?.length
                        ? Math.min(0.99, Math.max(0.01, ctrlPos / blotProfile.length))
                        : null
                      // Bordeaux/Dynablot only: a strip mounted backwards gets rotated
                      // 180° BEFORE reconstruction (see app.py), which flips which
                      // original edge the profile's index 0 (ctrlFrac→0) maps to —
                      // the right edge normally, the LEFT edge for a flipped strip.
                      // Without this, the marker below is mirrored to the wrong side
                      // of the box for every flipped strip.
                      const blotFlipped = (blot as any).flipped === true

                      return (
                        <Box
                          key={blot.id}
                          position="absolute"
                          left={`${dispLeft}%`}
                          top={`${dispTop}%`}
                          width={`${widthPct}%`}
                          height={`${heightPct}%`}
                          border="1.5px solid"
                          borderColor={isDragging ? 'purple.400' : 'cyan.400'}
                          borderRadius="3px"
                          bg={isDragging ? 'rgba(128, 90, 213, 0.18)' : 'rgba(0, 188, 212, 0.08)'}
                          boxShadow={isDragging ? '0 6px 20px rgba(0,0,0,0.28)' : undefined}
                          zIndex={isDragging ? 25 : 10}
                          pointerEvents={isDrawingMode || isReanalyzing ? 'none' : 'auto'}
                          _hover={isDragging ? undefined : { bg: 'rgba(0, 188, 212, 0.18)', borderColor: 'cyan.300' }}
                          transition={isDragging ? 'none' : 'all 0.2s'}
                          role="group"
                          cursor={isDragging ? 'grabbing' : 'grab'}
                          title={`${blotLabel(blot)} — ${blot.num_bands} bandes · glissez pour repositionner, poignée du haut pour pivoter · maintenir Maj pour un réglage fin`}
                          onPointerDown={(e) => handleBoxPointerDown(e, { id: blot.id, leftPct, topPct, wPct: widthPct, hPct: heightPct, angle: blotAngle })}
                          onPointerMove={handleBoxPointerMove}
                          onPointerUp={handleBoxPointerUp}
                          style={{
                            touchAction: 'none',
                            ...(dispAngle !== 0
                              ? { transformOrigin: 'center center', transform: `rotate(${dispAngle}deg)` }
                              : {}),
                          }}
                        >
                          {/* Control-line reference — the alignment étalon. Amber
                              dashed line across the box at the detected control band;
                              the "contrôle" tag shows on hover to avoid clutter with
                              many strips. Hidden while dragging (stale until the drop
                              re-analyses). */}
                          {ctrlFrac != null && !isDragging && (
                            isLandscape ? (
                              // Landscape strip: control line is VERTICAL. Profile index 0
                              // maps to the right edge normally (1 - ctrlFrac), but to the
                              // LEFT edge for a Bordeaux strip that was rotated 180° before
                              // reconstruction (mounted backwards in the reader) — see
                              // `blotFlipped` above.
                              <Box
                                position="absolute"
                                top={0}
                                bottom={0}
                                left={`${(blotFlipped ? ctrlFrac : 1 - ctrlFrac) * 100}%`}
                                w="0"
                                borderLeft="2px dashed #fbbf24"
                                pointerEvents="none"
                                zIndex={22}
                                style={{ transform: 'translateX(-50%)' }}
                              >
                                <Box
                                  position="absolute"
                                  top="1px"
                                  left="50%"
                                  transform="translateX(-50%)"
                                  bg="#f59e0b"
                                  color="gray.900"
                                  fontSize="7px"
                                  fontWeight="bold"
                                  lineHeight="1"
                                  px={1}
                                  py="1px"
                                  borderRadius="sm"
                                  opacity={0}
                                  _groupHover={{ opacity: 1 }}
                                  transition="opacity 0.15s"
                                  whiteSpace="nowrap"
                                >
                                  contrôle
                                </Box>
                              </Box>
                            ) : (
                              // Nancy portrait strip: control line horizontal near the
                              // top (unchanged).
                              <Box
                                position="absolute"
                                left={0}
                                right={0}
                                top={`${ctrlFrac * 100}%`}
                                h="0"
                                borderTop="2px dashed #fbbf24"
                                pointerEvents="none"
                                zIndex={22}
                                style={{ transform: 'translateY(-50%)' }}
                              >
                                <Box
                                  position="absolute"
                                  left="1px"
                                  top="50%"
                                  transform="translateY(-50%)"
                                  bg="#f59e0b"
                                  color="gray.900"
                                  fontSize="7px"
                                  fontWeight="bold"
                                  lineHeight="1"
                                  px={1}
                                  py="1px"
                                  borderRadius="sm"
                                  opacity={0}
                                  _groupHover={{ opacity: 1 }}
                                  transition="opacity 0.15s"
                                  whiteSpace="nowrap"
                                >
                                  contrôle
                                </Box>
                              </Box>
                            )
                          )}
                          {/* Rotation handle — connector + grip above the top edge.
                              Lets the operator straighten a tilted strip; commits
                              on release (same snapping as the add-blot selection). */}
                          {!isDrawingMode && !isReanalyzing && (
                            <>
                              <Box
                                position="absolute"
                                top="-24px"
                                left="calc(50% - 1px)"
                                w="2px"
                                h="18px"
                                bg="purple.400"
                                pointerEvents="none"
                                zIndex={26}
                                opacity={0.4}
                                _groupHover={{ opacity: 1 }}
                                transition="opacity 0.15s"
                              />
                              <Box
                                position="absolute"
                                top="-32px"
                                left="calc(50% - 7px)"
                                w="14px"
                                h="14px"
                                bg="white"
                                border="2px solid"
                                borderColor="purple.500"
                                borderRadius="full"
                                boxShadow="0 1px 4px rgba(0,0,0,0.25)"
                                cursor="grab"
                                zIndex={27}
                                onPointerDown={(e) => handleBoxRotateDown(e, {
                                  id: blot.id, leftPct, topPct, wPct: widthPct, hPct: heightPct, angle: blotAngle,
                                })}
                                onPointerMove={handleBoxRotateMove}
                                onPointerUp={handleBoxRotateUp}
                                onPointerCancel={handleBoxRotateUp}
                                sx={{ touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
                                opacity={0.4}
                                _groupHover={{ opacity: 1 }}
                                transition="opacity 0.15s"
                                _hover={{ bg: 'purple.50', opacity: 1 }}
                                title={`Pivoter la bandelette${blotAngle ? ` (${blotAngle.toFixed(1)}°)` : ''} — maintenir Maj pour un réglage fin`}
                              />
                            </>
                          )}
                          <Badge
                            position="absolute"
                            top="-16px"
                            left="0"
                            colorScheme={isDragging ? 'purple' : 'cyan'}
                            fontSize="2xs"
                            borderRadius="sm"
                            px={1.5}
                            zIndex={11}
                          >
                            #{blot.id}
                          </Badge>
                          {isReanalyzing && (
                            <Box
                              position="absolute"
                              inset={0}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              bg="rgba(255,255,255,0.6)"
                              borderRadius="3px"
                            >
                              <Spinner size="sm" color="purple.500" thickness="2px" />
                            </Box>
                          )}
                        </Box>
                      )
                    })}

                    {/* Live drawing rectangle — manipulated via ref for performance */}
                    <div
                      ref={drawRectRef}
                      style={{
                        display: 'none',
                        position: 'absolute',
                        border: '2px dashed #ED8936',
                        background: 'rgba(237, 137, 54, 0.12)',
                        borderRadius: '3px',
                        pointerEvents: 'none',
                        zIndex: 20,
                        boxShadow: '0 0 0 1px rgba(237, 137, 54, 0.25), 0 2px 8px rgba(237, 137, 54, 0.18)',
                        willChange: 'left, top, width, height',
                      }}
                    />

                    {/* Drag-to-delete zone — appears while a box is being dragged.
                        Dropping a box here hides it (ghost blot), which also refunds
                        its credits. Saves hunting for the right blot number in the
                        gallery. pointerEvents off: the box has pointer capture, so we
                        detect hover geometrically in handleBoxPointerMove. */}
                    {draggingBox && (
                      <Box
                        ref={trashRef}
                        position="fixed"
                        bottom="32px"
                        left="50%"
                        zIndex={2000}
                        px={6}
                        py={3.5}
                        borderRadius="2xl"
                        border="2px dashed"
                        borderColor={overTrash ? 'red.600' : 'red.300'}
                        bg={overTrash ? 'red.500' : 'rgba(254, 226, 226, 0.96)'}
                        color={overTrash ? 'white' : 'red.600'}
                        boxShadow="0 16px 40px -10px rgba(239,68,68,0.5)"
                        display="flex"
                        alignItems="center"
                        gap={3}
                        pointerEvents="none"
                        transition="background 0.15s, color 0.15s, border-color 0.15s"
                        style={{ transform: `translateX(-50%) scale(${overTrash ? 1.08 : 1})` }}
                      >
                        <Icon as={MdDelete} boxSize={overTrash ? 7 : 6} />
                        <Text fontWeight="bold" fontSize="sm" whiteSpace="nowrap">
                          {overTrash ? 'Relâchez pour supprimer' : 'Glissez ici pour supprimer'}
                        </Text>
                      </Box>
                    )}

                    {/* Pending selection rectangle + confirmation */}
                    {pendingSelection && (
                      <>
                        <Box
                          position="absolute"
                          left={`${pendingSelection.x}%`}
                          top={`${pendingSelection.y}%`}
                          width={`${pendingSelection.w}%`}
                          height={`${pendingSelection.h}%`}
                          border="2.5px solid"
                          borderColor="orange.400"
                          bg="rgba(237, 137, 54, 0.15)"
                          borderRadius="3px"
                          cursor={isSubmittingBlot ? 'wait' : 'move'}
                          zIndex={20}
                          boxShadow="0 0 0 1px rgba(237, 137, 54, 0.3), 0 4px 12px rgba(237, 137, 54, 0.25)"
                          onPointerDown={isSubmittingBlot ? undefined : (e) => handleEditPointerDown(e, 'move')}
                          onPointerMove={isSubmittingBlot ? undefined : handleEditPointerMove}
                          onPointerUp={isSubmittingBlot ? undefined : handleEditPointerUp}
                          onPointerCancel={isSubmittingBlot ? undefined : handleEditPointerUp}
                          style={{
                            transform: `rotate(${pendingSelection.angle || 0}deg)`,
                            transformOrigin: 'center center',
                          }}
                          sx={{
                            touchAction: 'none',
                            userSelect: 'none',
                            animation: 'blotPendingIn 140ms ease-out',
                            '@keyframes blotPendingIn': { from: { opacity: 0, transform: 'scale(0.97)' }, to: { opacity: 1, transform: 'scale(1)' } },
                          }}
                        >
                          {!isSubmittingBlot && (
                            <>
                              {/* Rotation handle — connector line + circle above the top edge */}
                              <Box
                                position="absolute"
                                top="-26px"
                                left="calc(50% - 1px)"
                                w="2px"
                                h="20px"
                                bg="orange.400"
                                pointerEvents="none"
                                zIndex={21}
                              />
                              <Box
                                position="absolute"
                                top="-34px"
                                left="calc(50% - 8px)"
                                w="16px"
                                h="16px"
                                bg="white"
                                border="2px solid"
                                borderColor="orange.500"
                                borderRadius="full"
                                boxShadow="0 1px 4px rgba(0,0,0,0.2)"
                                cursor="grab"
                                zIndex={23}
                                onPointerDown={(e) => handleEditPointerDown(e, 'rotate')}
                                onPointerMove={handleEditPointerMove}
                                onPointerUp={handleEditPointerUp}
                                onPointerCancel={handleEditPointerUp}
                                sx={{ touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
                                _hover={{ bg: 'orange.50' }}
                                title={`Rotation${pendingSelection.angle ? ` (${pendingSelection.angle.toFixed(1)}°)` : ''} — maintenir Maj pour un réglage fin`}
                              />
                            </>
                          )}
                          {!isSubmittingBlot && ([
                            { dir: 'nw' as EditMode, top: '-6px', left: '-6px', cursor: 'nwse-resize' },
                            { dir: 'n'  as EditMode, top: '-6px', left: 'calc(50% - 6px)', cursor: 'ns-resize' },
                            { dir: 'ne' as EditMode, top: '-6px', right: '-6px', cursor: 'nesw-resize' },
                            { dir: 'e'  as EditMode, top: 'calc(50% - 6px)', right: '-6px', cursor: 'ew-resize' },
                            { dir: 'se' as EditMode, bottom: '-6px', right: '-6px', cursor: 'nwse-resize' },
                            { dir: 's'  as EditMode, bottom: '-6px', left: 'calc(50% - 6px)', cursor: 'ns-resize' },
                            { dir: 'sw' as EditMode, bottom: '-6px', left: '-6px', cursor: 'nesw-resize' },
                            { dir: 'w'  as EditMode, top: 'calc(50% - 6px)', left: '-6px', cursor: 'ew-resize' },
                          ].map((h) => (
                            <Box
                              key={h.dir}
                              position="absolute"
                              top={h.top}
                              left={h.left}
                              right={h.right}
                              bottom={h.bottom}
                              w="12px"
                              h="12px"
                              bg="white"
                              border="2px solid"
                              borderColor="orange.500"
                              borderRadius="2px"
                              boxShadow="0 1px 3px rgba(0,0,0,0.18)"
                              cursor={h.cursor}
                              zIndex={22}
                              onPointerDown={(e) => handleEditPointerDown(e, h.dir)}
                              onPointerMove={handleEditPointerMove}
                              onPointerUp={handleEditPointerUp}
                              onPointerCancel={handleEditPointerUp}
                              sx={{ touchAction: 'none' }}
                              _hover={{ bg: 'orange.50' }}
                            />
                          )))}
                        </Box>
                        <HStack
                          position="absolute"
                          left={`${Math.max(8, Math.min(92, pendingSelection.x + pendingSelection.w / 2))}%`}
                          top={pendingSelection.y + pendingSelection.h / 2 > 50
                            ? `${pendingSelection.y}%`
                            : `${pendingSelection.y + pendingSelection.h}%`}
                          transform={pendingSelection.y + pendingSelection.h / 2 > 50
                            ? 'translate(-50%, calc(-100% - 56px))'
                            : 'translate(-50%, 14px)'}
                          bg="white"
                          px={4}
                          py={2}
                          borderRadius="xl"
                          boxShadow="xl"
                          border="1px solid"
                          borderColor="gray.200"
                          zIndex={25}
                          spacing={3}
                          sx={{ transition: 'top 120ms ease-out, left 120ms ease-out' }}
                        >
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">
                            Valider cette sélection ?
                          </Text>
                          <Button
                            size="sm"
                            colorScheme="teal"
                            borderRadius="lg"
                            onClick={handleConfirmSelection}
                            isLoading={isSubmittingBlot}
                            loadingText="Analyse..."
                          >
                            Valider
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="gray"
                            borderRadius="lg"
                            onClick={handleCancelSelection}
                            isDisabled={isSubmittingBlot}
                          >
                            Recommencer
                          </Button>
                        </HStack>
                      </>
                    )}

                    {/* Drawing mode banner */}
                    {isDrawingMode && !pendingSelection && !isSubmittingBlot && (
                      <Box
                        position="absolute"
                        top="12px"
                        left="50%"
                        transform="translateX(-50%)"
                        bg="orange.500"
                        color="white"
                        px={4}
                        py={2}
                        borderRadius="lg"
                        fontSize="sm"
                        fontWeight="bold"
                        zIndex={20}
                        boxShadow="lg"
                        whiteSpace="nowrap"
                      >
                        Dessinez un rectangle autour du blot manquant
                      </Box>
                    )}
                  </Box>
                </Box>
                </Collapse>
              </Box>
            )}

            {/* ── Blot Gallery + Validation workflow ──────────────────────── */}
            {analysisResult.blots && analysisResult.blots.length > 0 && (() => {
              const hiddenIds = new Set((analysisResult as any).hiddenBlotIds ?? [])
              const visibleBlots = analysisResult.blots.filter((b: any) => !hiddenIds.has(b.id))
              const controlIds = new Set([
                (analysisResult as any).selectedPositiveControlId,
                (analysisResult as any).selectedPositiveControl2Id,
                (analysisResult as any).selectedPositiveControl3Id,
              ].filter(Boolean))
              const sampleBlotsOnly = visibleBlots.filter((b: any) => !controlIds.has(b.id))

              return (
                <Box data-tour-id="blot-gallery">
                  <HStack justify="space-between" mb={3}>
                    <VStack align="start" spacing={0.5}>
                      <HStack spacing={2.5}>
                        <Box w="4px" h="22px" borderRadius="full" bgGradient="linear(to-b, teal.400, cyan.400)" />
                        <Text fontWeight="bold" color="gray.800" fontSize="lg" letterSpacing="-0.01em">
                          {t('blotAnalysis.workspace.detectedBlotsWithProfiles')}
                        </Text>
                      </HStack>
                      {!aiAccessible && (
                        <Text fontSize="xs" color="gray.500" pl="14px">
                          Cliquez sur une bandelette pour la valider
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  {/* La comparaison, une fois les deux lectures figées. Elle
                      vient d'instantanés enregistrés et jamais d'un calcul
                      refait à l'affichage : les gabarits d'IRIS changent, et
                      une concordance qui se recalcule n'est pas une mesure. */}
                  {/* Reparaît tant qu'il reste des bandelettes à figer — une
                      recherche à la fois sur une plaque multitest, et non une
                      seule fois pour toute la plaque. */}
                  {canReadOnBands
                    && benchPending.inFilter.length === 0
                    && (analysisResult as any).benchReading?.blots
                    && snapshotReadiness.scope.length > 0 && (
                    <Box
                      bg="purple.50"
                      border="1px solid"
                      borderColor="purple.200"
                      borderRadius="xl"
                      px={5}
                      py={4}
                      mb={6}
                    >
                      <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
                        <Box>
                          <Text fontWeight="600" color="purple.800">
                            Votre lecture est enregistrée. Reste à figer celle d’IRIS
                            {activePathologyFilter ? ` pour ${pathologyDisplayName(activePathologyFilter)}` : ''}.
                          </Text>
                          <Text fontSize="sm" color="purple.700">
                            {snapshotReadiness.blockedReason
                              ?? (irisReadingForSnapshot?.bandsFound
                                ? `IRIS retient ${irisReadingForSnapshot.bandsFound} bande${irisReadingForSnapshot.bandsFound > 1 ? 's' : ''} sur l’ensemble des échantillons. Ce que vous figez est ce qui sera comparé, et ne changera plus.`
                                : 'IRIS n’a encore identifié aucune bande — calibrez le témoin, sans quoi il n’a rien à proposer.')}
                          </Text>
                        </Box>
                        <Tooltip
                          hasArrow
                          maxW="320px"
                          isDisabled={!snapshotReadiness.blockedReason}
                          label={snapshotReadiness.blockedReason ?? ''}
                        >
                          <Box flexShrink={0}>
                            <Button
                              colorScheme="purple"
                              leftIcon={<Icon as={LuLock} />}
                              isDisabled={
                                !irisReadingForSnapshot?.bandsFound
                                || Boolean(snapshotReadiness.blockedReason)
                              }
                              isLoading={freezingSnapshot}
                              loadingText="Figer…"
                              onClick={handleFreezeIrisSnapshot}
                            >
                              Figer et comparer
                            </Button>
                          </Box>
                        </Tooltip>
                      </HStack>
                    </Box>
                  )}

                  {canReadOnBands
                    && (analysisResult as any).benchReading?.lockedAt
                    && (analysisResult as any).irisSnapshot?.blots && (
                    <RetrospectiveComparison
                      immunoblotConfig={activeConfig as any}
                      {...(() => {
                        // Une comparaison porte sur UNE recherche : les bandes
                        // d'un test ne sont pas celles d'un autre, et un même
                        // tableau ne peut pas avoir deux jeux de colonnes.
                        // Sur une plaque multitest, on ne montre donc que la
                        // recherche affichée.
                        const ofFilter = new Set(
                          (analysisResult.blots ?? [])
                            .filter((b: any) => !activePathologyFilter
                              || b.pathology_label === activePathologyFilter)
                            .map((b: any) => String(b.id)),
                        )
                        const keep = (d: Record<string, any>) => Object.fromEntries(
                          Object.entries(d ?? {}).filter(([id]) => ofFilter.has(id)),
                        )
                        return {
                          benchBlots: keep((analysisResult as any).benchReading.blots),
                          irisBlots: keep((analysisResult as any).irisSnapshot.blots),
                        }
                      })()}
                      stripLabels={Object.fromEntries(
                        (analysisResult.blots ?? []).map((b: any) => [b.id, b.strip_id || `Blot ${b.id}`]),
                      )}
                      lockedAt={(analysisResult as any).benchReading.lockedAt}
                      takenAt={(analysisResult as any).irisSnapshot?.takenAt}
                      manualCorrections={{
                        placed: Object.values((analysisResult as any).placedBands ?? {})
                          .reduce((n: number, arr: any) => n + (Array.isArray(arr) ? arr.length : 0), 0),
                        suppressed: Object.values((analysisResult as any).suppressedBands ?? {})
                          .reduce((n: number, arr: any) => n + (Array.isArray(arr) ? arr.length : 0), 0),
                        repositioned: Object.values((analysisResult as any).repositionedBlots ?? {})
                          .reduce((n: number, c: any) => n + (Number(c) || 0), 0),
                      }}
                    />
                  )}

                  {/* Lecture de paillasse : un bandeau, pas une seconde
                      galerie. Les bandes se cochent dans les cartes
                      elles-mêmes (voir benchReadingMode) — dupliquer les
                      vignettes affichait deux fois la même plaque, et obligeait
                      à désigner le témoin ou corriger un identifiant en bas
                      pour les voir changer en haut. */}
                  {/* Sans notice — la vue « Toutes » d'une plaque multitest —
                      il n'y a ni bandes à cocher ni colonnes à comparer : les
                      bandes d'un test ne sont pas celles d'un autre. On
                      demande de choisir une recherche plutôt que d'afficher
                      des tableaux vides. */}
                  {!canReadOnBands && benchPending.inFilter.length > 0 && (
                    <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="xl" px={5} py={4} mb={5}>
                      <HStack spacing={3} align="start">
                        <Icon as={LuEyeOff} color="purple.600" boxSize={5} mt={0.5} />
                        <Box>
                          <Text fontWeight="600" color="purple.800">
                            Choisissez une recherche pour saisir votre lecture
                          </Text>
                          <Text fontSize="sm" color="purple.700">
                            Cette plaque porte plusieurs tests, et chacun a ses propres bandes.
                            La saisie comme la comparaison se font recherche par recherche —
                            utilisez le filtre ci-dessous.
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  )}

                  {canReadOnBands && benchPending.inFilter.length > 0 && (
                    <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="xl" px={5} py={4} mb={5}>
                      <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
                        <HStack spacing={3} align="start">
                          <Icon as={LuEyeOff} color="purple.600" boxSize={5} mt={0.5} />
                          <Box>
                            <Text fontWeight="600" color="purple.800">
                              Votre lecture de paillasse, avant IRIS
                              {activePathologyFilter ? ` — ${pathologyDisplayName(activePathologyFilter)}` : ''}
                            </Text>
                            <Text fontSize="sm" color="purple.700">
                              Reportez sur chaque bandelette d’échantillon les bandes vues à l’œil
                              <Text as="span" fontWeight="700"> sur la membrane</Text>. Les images, les
                              profils et la fiche détaillée restent fermés jusqu’au verrouillage, témoin
                              compris : seul l’identifiant imprimé reste visible, pour savoir à quelle
                              bandelette vous attribuez votre lecture. Le témoin se désigne dès
                              maintenant, mais se calibre après.
                            </Text>
                            {switchedAfterReveal && (
                              <Text fontSize="xs" color="purple.600" mt={1} fontStyle="italic">
                                Ce cas avait déjà été analysé : la lecture sera enregistrée comme
                                déclarée en aveugle, et non garantie par le serveur.
                              </Text>
                            )}
                          </Box>
                        </HStack>
                        <Tooltip
                          hasArrow
                          maxW="300px"
                          isDisabled={!benchReadiness.blockedReason}
                          label={benchReadiness.blockedReason ?? ''}
                        >
                          <Box flexShrink={0}>
                            <Button
                              colorScheme="purple"
                              leftIcon={<Icon as={LuLock} />}
                              isLoading={lockingBench}
                              loadingText="Verrouillage…"
                              isDisabled={!benchReadiness.hasControl}
                              onClick={() => handleLockBenchReading()}
                            >
                              Verrouiller et dévoiler IRIS
                            </Button>
                          </Box>
                        </Tooltip>
                      </HStack>
                    </Box>
                  )}

                  {/* Même source que les bandeaux pour le mode de saisie : une
                      bandelette déjà verrouillée garde son profil, donc ses
                      cases ne s'affichent pas — c'est le voile qui décide.

                      LE VOILE NE DÉPEND PAS DE LA NOTICE AFFICHÉE. Sur une
                      plaque multitest vue en « Toutes », la notice du cas est
                      « DYNABLOT MULTITEST », sans bandes : canReadOnBands est
                      faux, et le voile tombait avec lui — galerie dévoilée et
                      fiches ouvertes, alors que le MÊME cas filtré sur une
                      recherche était voilé. L'aveugle protège des bandelettes
                      en attente de saisie, qu'on puisse saisir ici ou non ;
                      la saisie, elle, reste affaire de notice (bandeaux et
                      cases par bandelette). */}
                  <BlotGallery
                    module={module}
                    detectionProfile={(analysisResult as any).detection_profile ?? null}
                    benchReadingMode={benchPending.inFilter.length > 0}
                    benchBands={benchBands}
                    onBenchBandToggle={handleBenchBandToggle}
                    key={blotGalleryKey}
                    blots={analysisResult.blots}
                    caseId={selectedCaseId}
                    savedPositiveControlId={(analysisResult as any).selectedPositiveControlId}
                    savedPositiveControl2Id={(analysisResult as any).selectedPositiveControl2Id}
                    savedPositiveControl3Id={(analysisResult as any).selectedPositiveControl3Id}
                    savedPositiveControlsByPathology={(analysisResult as any).positiveControlsByPathology}
                    onPositiveControlChange={handlePositiveControlChange}
                    savedBandCalibration={(analysisResult as any).bandCalibration}
                    onBandCalibrationChange={handleBandCalibrationChange}
                    savedControlLineOverrides={(analysisResult as any).controlLineOverrides}
                    onControlLineOverrideChange={handleControlLineOverrideChange}
                    savedPairedRoleSwaps={(analysisResult as any).pairedRoleSwaps}
                    onPairedRoleSwapChange={handlePairedRoleSwapChange}
                    immunoblotConfig={immunoblotConfig}
                    pathologyConfigMap={pathologyConfigMap}
                    initialPathologyFilter={initialGalleryPathologyFilter}
                    onPathologyFilterChange={setActivePathologyFilter}
                    onInterpretationsChange={setBlotInterpretations}
                    savedHiddenBlotIds={(analysisResult as any).hiddenBlotIds ?? []}
                    onHiddenBlotsChange={handleHiddenBlotsChange}
                    restoreHiddenNonce={restoreHiddenNonce}
                    manualBlotResults={manualBlotResults}
                    startTourSignal={tourSignal}
                    onStripIdChange={handleStripIdChange}
                    onManualResultChange={(blotId, result) => {
                      setManualBlotResults(prev => {
                        const updated = { ...prev, [blotId]: result }
                        saveManualResults(updated)
                        return updated
                      })
                    }}
                    onBlotCommentChange={(blotId, comment) =>
                      setBlotComments(prev => ({ ...prev, [blotId]: comment }))
                    }
                    savedPlacedBands={(analysisResult as any).placedBands ?? {}}
                    onPlacedBandsChange={handlePlacedBandsChange}
                    savedSuppressedBands={(analysisResult as any).suppressedBands ?? {}}
                    onSuppressedBandsChange={handleSuppressedBandsChange}
                    aiAccessible={aiAccessible}
                    readOnly={isReadOnly}
                  />
                </Box>
              )
            })()}
          </VStack>
        )}
      </Box>

      {/* Collaboration modal — mounted once, opened on demand */}
      {chuFeatures.collaboration.enabled && selectedCaseId && (
        <CollaborationModal
          isOpen={isCollabOpen}
          onClose={onCollabClose}
          caseId={selectedCaseId}
          caseName={analysisResult?.filename ?? undefined}
        />
      )}

      {/* ── Floating action dock (right edge) ─────────────────────────────
          Vertically-stacked sticky shortcuts to: show / hide blots, add a
          blot, re-run the analysis, export the PDF. Positioned mid-right so
          it stays accessible while scrolling. Sits at top:50% with a negative
          translate so it auto-centers vertically — clear of Aletheia's FAB
          when minimized (bottom-right, ~84px tall), but the EXPANDED chat
          panel is 480×680px at the same bottom-right corner and, being
          vertically centered itself on most screens, squarely overlaps this
          dock. Slide the dock further right→left while the chat is open
          instead of trying to out-z-index a panel the user is actively
          reading. */}
      {selectedCaseId && analysisResult && (() => {
        const hiddenIds = new Set((analysisResult as any).hiddenBlotIds ?? [])
        const blots = (analysisResult?.blots ?? []).filter((b: any) => !hiddenIds.has(b.id))
        // Bordeaux/Dynablot multitest stores each pathology's own témoin in
        // positiveControlsByPathology instead of the 3 legacy columns above —
        // without also reading those, a multitest témoin stayed counted as a
        // regular sample the biologist still had to validate, permanently
        // blocking export at "N-1/N" even when every real sample was done.
        const byPathologyControlIds = Object.values(
          (analysisResult as any).positiveControlsByPathology ?? {}
        ).flatMap((slot: any) => [slot?.positiveControlId, slot?.positiveControl2Id, slot?.positiveControl3Id])
        const controlIds = new Set([
          (analysisResult as any).selectedPositiveControlId,
          (analysisResult as any).selectedPositiveControl2Id,
          (analysisResult as any).selectedPositiveControl3Id,
          ...byPathologyControlIds,
        ].filter(Boolean))
        const sampleBlots = blots.filter((b: any) => !controlIds.has(b.id))
        // Bordeaux/Dynablot multitest: while a pathology filter is active, the
        // export gate below only needs THAT pathology's own sample blots done —
        // otherwise a fully-validated Schisto group stayed stuck behind an
        // unfinished Toxocara group elsewhere in the same case. No filter (the
        // whole-case view) keeps the original all-pathologies requirement.
        const gateScopedBlots = activePathologyFilter
          ? sampleBlots.filter((b: any) => b.pathology_label === activePathologyFilter)
          : sampleBlots
        // Export requires EVERY sample blot (in scope) to be validated by the
        // biologist — in AI mode too (validation via the "Valider" button in the modal).
        const allValidated = gateScopedBlots.every((b: any) => manualBlotResults[b.id] != null)
        const validatedCount = gateScopedBlots.filter((b: any) => manualBlotResults[b.id] != null).length
        // Export also needs every SAMPLE strip (in scope) to carry a complete id
        // (letter + number); controls are excluded (no patient id). Merged into
        // the single button gate below so the button stays disabled until BOTH
        // hold — no "clickable then blocked in the menu" two-step. The order of
        // what's missing drives the tooltip message.
        const allStripIdsComplete = gateScopedBlots.every((b: any) => isCompleteStripId(b.strip_id))
        const missingIdCount = gateScopedBlots.filter((b: any) => !isCompleteStripId(b.strip_id)).length
        const canExport = allValidated && allStripIdsComplete

        // Shared button style helper — centralises all the styling so the four
        // FABs stay strictly aligned (same dimensions, same animation curves,
        // same icon centring). `tone` drives the colour palette in active state.
        const fabBaseSx = {
          width: '52px',
          height: '52px',
          minWidth: '52px',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '16px',
          position: 'relative' as const,
          cursor: 'pointer',
          transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease, background 0.22s ease',
        }
        const tonePalette = {
          teal:   { from: '#0d9488', to: '#14b8a6', soft: 'teal.50',   line: 'teal.100',   border: 'teal.500',   glow: 'rgba(20,184,166,0.42)', text: 'teal.600' },
          orange: { from: '#ea580c', to: '#f97316', soft: 'orange.50', line: 'orange.100', border: 'orange.500', glow: 'rgba(249,115,22,0.42)', text: 'orange.600' },
          blue:   { from: '#1d4ed8', to: '#3b82f6', soft: 'blue.50',   line: 'blue.100',   border: 'blue.500',   glow: 'rgba(59,130,246,0.42)', text: 'blue.600' },
          purple: { from: '#7c3aed', to: '#a855f7', soft: 'purple.50', line: 'purple.100', border: 'purple.500', glow: 'rgba(168,85,247,0.42)', text: 'purple.600' },
        } as const

        const FabButton = forwardRef<HTMLButtonElement, {
          tone: keyof typeof tonePalette
          active?: boolean
          disabled?: boolean
          onClick?: () => void
          icon: any
          spin?: boolean
          title?: string
          'data-tour'?: string
        }>(({
          tone, active, disabled, onClick, icon, spin = false, ...rest
        }, ref) => {
          const p = tonePalette[tone]
          return (
            <Box
              as="button"
              ref={ref}
              onClick={onClick}
              disabled={disabled}
              // Spread Tooltip-injected props (ref handled above, plus the
              // onMouseEnter/onFocus/aria handlers) so hover tooltips fire.
              {...rest}
              sx={fabBaseSx}
              bgGradient={active ? `linear(135deg, ${p.from}, ${p.to})` : 'linear(180deg, white, gray.50)'}
              color={active ? 'white' : p.text}
              border="1.5px solid"
              borderColor={active ? 'transparent' : p.line}
              opacity={disabled ? 0.5 : 1}
              boxShadow={active
                ? `0 10px 22px -6px ${p.glow}, 0 4px 8px -2px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.30)`
                : '0 1px 2px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.55)'}
              _hover={disabled ? undefined : {
                transform: 'translateY(-2px) scale(1.06)',
                bg: active ? undefined : p.soft,
                boxShadow: active
                  ? `0 14px 28px -8px ${p.glow}, 0 6px 12px -4px rgba(15,23,42,0.14), inset 0 1px 0 rgba(255,255,255,0.40)`
                  : `0 8px 16px -8px ${p.glow}, inset 0 1px 0 rgba(255,255,255,0.55)`,
                borderColor: active ? 'transparent' : p.border,
              }}
              _active={disabled ? undefined : { transform: 'translateY(0) scale(0.96)' }}
            >
              {/* Glossy highlight overlay on active state */}
              {active && (
                <Box
                  position="absolute"
                  top="2px"
                  left="2px"
                  right="2px"
                  h="40%"
                  borderTopRadius="14px"
                  bgGradient="linear(180deg, rgba(255,255,255,0.28), transparent)"
                  pointerEvents="none"
                />
              )}
              <Icon
                as={icon}
                boxSize="22px"
                sx={spin
                  ? { animation: 'fabSpin 0.9s linear infinite', '@keyframes fabSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }
                  : undefined}
              />
            </Box>
          )
        })

        return (
          <Box
            position="fixed"
            top="50%"
            right={{ base: '12px', md: isAiChatOpen ? '510px' : '24px' }}
            transition="right 0.25s ease"
            transform="translateY(-50%)"
            zIndex={1000}
            display={{ base: 'none', md: 'flex' }}
            flexDirection="column"
            alignItems="center"
            gap={4}
          >
          <Box
            data-tour-id="blot-fab-dock"
            display="flex"
            flexDirection="column"
            gap={2.5}
            p={3}
            borderRadius="3xl"
            bg="rgba(255, 255, 255, 0.78)"
            backdropFilter="blur(18px) saturate(180%)"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.65)"
            boxShadow="0 24px 60px -16px rgba(15, 23, 42, 0.22), 0 8px 24px -10px rgba(15, 118, 110, 0.20), inset 0 1px 0 rgba(255,255,255,0.55)"
            sx={{
              backgroundImage:
                'radial-gradient(120% 80% at 100% 0%, rgba(20,184,166,0.06), transparent 60%),' +
                'radial-gradient(120% 80% at 0% 100%, rgba(168,85,247,0.05), transparent 55%)',
            }}
          >
            {analysisResult.blots && analysisResult.blots.length > 0 && (
              <Tooltip label={showBlotBoxes ? 'Masquer les blots et replier l’image source' : 'Afficher les blots et déplier l’image source'} placement="left" hasArrow openDelay={220}>
                <FabButton
                  data-tour="show-blots-toggle"
                  tone="teal"
                  active={showBlotBoxes}
                  onClick={() => {
                    const next = !showBlotBoxes
                    setShowBlotBoxes(next)
                    // Keep the source image panel in sync: unfold it when we
                    // reveal the boxes, refold it when we hide them (the boxes
                    // are drawn on the source image, so it must be open to see them).
                    if (next !== isSourceOpen) onToggleSource()
                  }}
                  icon={showBlotBoxes ? MdVisibilityOff : MdVisibility}
                />
              </Tooltip>
            )}

            <Tooltip
              label={isReadOnly ? 'Désactivé en mode lecture admin' : (isDrawingMode ? 'Annuler le tracé' : 'Ajouter un blot')}
              placement="left"
              hasArrow
              openDelay={220}
            >
              <FabButton
                tone="orange"
                active={isDrawingMode}
                onClick={isReadOnly ? undefined : handleToggleDrawingMode}
                icon={MdAdd}
                disabled={isReadOnly || isSubmittingBlot}
              />
            </Tooltip>

            <Tooltip
              label={isReadOnly ? 'Désactivé en mode lecture admin' : (reanalyzing ? 'Re-analyse en cours…' : 'Relancer l’analyse')}
              placement="left"
              hasArrow
              openDelay={220}
            >
              <FabButton
                tone="blue"
                onClick={isReadOnly ? undefined : onReanalyzeOpen}
                icon={MdRefresh}
                disabled={isReadOnly || reanalyzing}
                spin={reanalyzing}
              />
            </Tooltip>

            {/* Mode rétrospectif : la lecture de paillasse d'abord, IRIS ensuite.
                Basculer relance l'analyse, parce qu'une plaque déjà lue avec
                IRIS sous les yeux ne peut pas servir de lecture en aveugle —
                le cas repart à zéro, comme un cas neuf. */}
            {/* Le bouton disparaît une fois la lecture verrouillée : il n'y
                aurait plus rien à basculer. Le voile est levé, le cas se lit
                comme un cas ordinaire, et le seul effet du changement serait
                de détruire une lecture en aveugle qui ne peut pas être
                refaite. */}
            {canReadOnBands && isRetrospective && !(analysisResult as any)?.benchReading?.lockedAt && (
              <Tooltip
                label="Repasser en mode prospectif : dévoile IRIS et abandonne la saisie"
                placement="left"
                hasArrow
                openDelay={220}
              >
                <FabButton
                  tone="purple"
                  active={isRetrospective}
                  onClick={isReadOnly ? undefined : requestEvaluationModeSwitch}
                  // Le carnet de paillasse, et non un œil : l'œil dit déjà
                  // « montrer ou masquer les boxes » partout ailleurs dans cet
                  // écran, et deux grammaires du même symbole se contredisent.
                  // Ce bouton ne parle pas de ce qu'on voit, mais de ce qu'on
                  // reporte — on note d'abord sa lecture, IRIS vient après.
                  // L'état actif se lit à la pastille violette, pas à l'icône.
                  icon={LuClipboardPen}
                  disabled={isReadOnly || reanalyzing || switchingMode}
                  spin={switchingMode}
                />
              </Tooltip>
            )}

            {/* Visual divider between actions and the export CTA */}
            <Box h="1px" mx={1} my={0.5} bgGradient="linear(to-r, transparent, gray.300, transparent)" />

            <Tooltip
              label={!allValidated
                ? `Validez toutes les bandelettes avant d'exporter (${validatedCount}/${gateScopedBlots.length})`
                : !allStripIdsComplete
                  ? `Complétez l'identifiant de ${missingIdCount} bandelette${missingIdCount > 1 ? 's' : ''} avant d'exporter`
                  : 'Exporter le rapport PDF'}
              placement="left"
              hasArrow
              openDelay={200}
            >
              <Box
                opacity={canExport ? 1 : 0.5}
                // Keep pointer events ON even when blocked so the tooltip still
                // shows WHY on hover — the button itself is disabled via isDisabled.
                pointerEvents="auto"
                sx={{
                  // Force-size the ExportButton's inner trigger into the 52×52 FAB grid.
                  '& > div, & button': {
                    width: '52px !important',
                    height: '52px !important',
                    minWidth: '52px !important',
                    borderRadius: '16px !important',
                    padding: '0 !important',
                    display: 'grid !important',
                    placeItems: 'center !important',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.55) !important',
                    background: 'linear-gradient(180deg, white, var(--chakra-colors-gray-50)) !important',
                    border: '1.5px solid var(--chakra-colors-purple-100) !important',
                    color: 'var(--chakra-colors-purple-600) !important',
                    transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease, background 0.22s ease !important',
                  },
                  '& > div:hover, & button:hover': {
                    transform: 'translateY(-2px) scale(1.06) !important',
                    background: 'var(--chakra-colors-purple-50) !important',
                    boxShadow: '0 8px 16px -8px rgba(168,85,247,0.42), inset 0 1px 0 rgba(255,255,255,0.55) !important',
                    borderColor: 'var(--chakra-colors-purple-500) !important',
                  },
                  '& span:not(:first-of-type)': { display: 'none !important' },
                  '& svg': { width: '22px', height: '22px' },
                }}
              >
                {/* Controls carry no patient id → excluded from the export id gate
                    (sampleBlots, not blots) so a control never blocks the export and
                    the biologist is never pushed to invent an id like "T+". */}
                <ExportButton
                  caseId={selectedCaseId}
                  sampleId={analysisResult?.sample_id}
                  pathologyName={analysisResult?.pathology_name}
                  sourceImageDataUrl={sourceImageDataUrl}
                  blotInterpretations={blotInterpretations}
                  blotStripIds={gateScopedBlots.map((b: any) => ({ id: b.id, stripId: b.strip_id ?? null }))}
                  // Reagent lots read on the plate, for the strips actually in
                  // scope for this export (so a pathology filter narrows them
                  // too). Several values = the scope mixes lots, which a single
                  // export field cannot represent — see ExportButton.
                  scopedLots={Array.from(
                    new Set(
                      gateScopedBlots
                        .map((b: any) => (b.lot_label ?? '').trim())
                        .filter(Boolean),
                    ),
                  )}
                  isDisabled={!canExport}
                  manualBlotResults={manualBlotResults}
                  operatorName={operatorValidation?.name ?? null}
                  operatorTimestamp={operatorValidation?.at ?? null}
                  caseComment={caseComment || undefined}
                  onCaseCommentChange={setCaseComment}
                  blotComments={blotComments}
                  placedBands={placedBandsForExport}
                  includeBlotIds={
                    activePathologyFilter
                      ? (analysisResult?.blots ?? [])
                          .filter((b) => b.pathology_label === activePathologyFilter)
                          .map((b) => b.id)
                      : undefined
                  }
                />
              </Box>
            </Tooltip>

            {/* "Aidez-moi" — static tips for the interactions the guided tour
                doesn't cover (delete / drag / rotate / this dock). Lives here in
                the dock rather than in the gallery header so every editing action
                sits in one place. Opens leftwards since the dock hugs the edge. */}
            {aiAccessible && (
              <>
                <Box h="1px" mx={1} my={0.5} bgGradient="linear(to-r, transparent, gray.300, transparent)" />
                <Popover placement="left" trigger="click">
                  <PopoverTrigger>
                    {/* Plain bulb, not "tips_and_updates": the latter carries its
                        sparkles in the top-left of the viewBox, so the bulb itself
                        reads as pushed right next to the symmetric icons above.
                        Same metaphor, optically centred. */}
                    <FabButton
                      tone="teal"
                      icon={MdLightbulbOutline}
                      title="Aidez-moi — astuces d'édition"
                    />
                  </PopoverTrigger>
                  <PopoverContent
                    w="320px"
                    border="none"
                    borderRadius="2xl"
                    overflow="hidden"
                    boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15,23,42,0.08)"
                    _focus={{ boxShadow: '0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15,23,42,0.08)' }}
                  >
                    <PopoverArrow bg="teal.500" />
                    <PopoverCloseButton color="white" top={3} _hover={{ bg: 'whiteAlpha.300' }} />
                    <Box bgGradient="linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)" px={4} py={3} position="relative">
                      <Box
                        position="absolute" top={0} left={0} right={0} bottom={0}
                        background="radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25) 0%, transparent 55%)"
                        pointerEvents="none"
                      />
                      <HStack spacing={3} position="relative" zIndex={1}>
                        <Box
                          w="34px" h="34px"
                          borderRadius="full"
                          bg="whiteAlpha.300"
                          display="grid"
                          sx={{ placeItems: 'center' }}
                          color="white"
                          flexShrink={0}
                        >
                          <Icon as={MdTipsAndUpdates} boxSize={4} />
                        </Box>
                        <Text fontSize="sm" fontWeight="bold" color="white">Astuces d'édition</Text>
                      </HStack>
                    </Box>
                    <PopoverBody px={5} py={4}>
                      <VStack align="stretch" spacing={3} py={1}>
                        {/* IRIS seulement : un gel de recherche (PROTIS) n'a
                            pas de ligne de contrôle imprimée — l'astuce y
                            parlerait d'un repère qui n'existe pas. */}
                        {module === 'iris' && (
                          <HStack align="start" spacing={3}>
                            <Icon as={MdDragHandle} color="yellow.500" boxSize={5} mt={0.5} flexShrink={0} />
                            <Box>
                              <Text fontSize="sm" fontWeight="600" color="gray.800">Vérifier la ligne de contrôle</Text>
                              <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                                Elle cale toutes les bandes suggérées. Si la <b>ligne jaune en tirets</b> du témoin
                                ne tombe pas sur sa bande de contrôle, glissez-la à sa place : les suggestions suivent.
                              </Text>
                            </Box>
                          </HStack>
                        )}
                        <HStack align="start" spacing={3}>
                          <Icon as={MdDelete} color="red.500" boxSize={5} mt={0.5} flexShrink={0} />
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.800">Supprimer un ghost blot</Text>
                            <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                              <b>Corbeille rouge</b> 🗑 en haut de la carte : la fausse détection sort
                              du rapport et de l'export.
                            </Text>
                          </Box>
                        </HStack>
                        <HStack align="start" spacing={3}>
                          <Icon as={MdOpenWith} color="teal.500" boxSize={5} mt={0.5} flexShrink={0} />
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.800">Repositionner un blot</Text>
                            <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                              Glissez la box sur l'image source : la bandelette est ré-analysée.
                            </Text>
                          </Box>
                        </HStack>
                        <HStack align="start" spacing={3}>
                          <Icon as={MdRotateRight} color="purple.500" boxSize={5} mt={0.5} flexShrink={0} />
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.800">Redresser une bandelette</Text>
                            <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                              Cadre de travers : attrapez la <b>poignée ronde au-dessus de la box</b> et pivotez.
                              L'angle s'aimante à 0°, 45° et 90°, puis ré-analyse au relâchement.
                            </Text>
                          </Box>
                        </HStack>
                        <HStack align="start" spacing={3}>
                          <Icon as={MdTune} color="purple.500" boxSize={5} mt={0.5} flexShrink={0} />
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color="gray.800">Cette barre d'outils</Text>
                            <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                              De haut en bas : afficher/masquer blots et image source, ajouter un blot,
                              relancer l'analyse, exporter.
                            </Text>
                          </Box>
                        </HStack>

                        <Button
                          size="sm"
                          colorScheme="teal"
                          leftIcon={<Icon as={MdHelpOutline} boxSize={4} />}
                          onClick={() => setTourSignal((n) => n + 1)}
                          borderRadius="lg"
                          mt={1}
                          bgGradient="linear(135deg, teal.500, cyan.500)"
                          _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
                          color="white"
                        >
                          Revoir la visite guidée
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="red"
                          leftIcon={<Icon as={LuBug} boxSize={4} />}
                          onClick={onBugReportOpen}
                          borderRadius="lg"
                        >
                          Signaler un bug
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </>
            )}
          </Box>
          </Box>
        )
      })()}

      {/* First-visit onboarding walkthrough — mounted in every non-selection
          view so users can replay/continue the tour from anywhere via the
          ? button. The component is idempotent: only one auto-launch per
          device (gated by localStorage). */}
      <BlotOnboardingTour
        currentView={viewMode}
        phase={tourPhase}
        setPhase={setTourPhase}
        onChangeView={setViewMode}
        blockedByWhatsNew={isWhatsNewOpen}
      />
      <BlotWhatsNew isOpen={isWhatsNewOpen} onClose={onWhatsNewClose} />
      <FeedbackModal isOpen={isFeedbackOpen} onClose={onFeedbackClose} onSubmitted={onFeedbackSubmitted} context={feedbackContextRef.current} />
      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={onBugReportClose}
        context={{ viewMode, caseId: selectedCaseId, pathologyName: analysisResult?.pathology_name }}
      />

      {/* Repasser en prospectif se confirme TOUJOURS, même sans une seule case
          cochée. Le geste dévoile IRIS, et le mode ne se choisit plus qu'au
          dépôt du fichier : la plaque ne pourra donc plus jamais fournir de
          lecture en aveugle. Ce n'est plus une saisie qu'on abandonne, c'est
          une série qu'on retire du recueil. */}
      <AlertDialog
        isOpen={confirmLeaveRetrospective}
        leastDestructiveRef={leaveRetroCancelRef}
        onClose={() => setConfirmLeaveRetrospective(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <HStack spacing={2}>
                <Icon as={LuEye} color="purple.500" boxSize={5} />
                <Text>Abandonner la lecture en cours ?</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={3} mb={4}>
                <Text fontSize="sm" color="purple.800">
                  {Object.values(benchBands).reduce((n, b) => n + b.length, 0) > 0 ? (
                    <>
                      Vous avez coché{' '}
                      <Text as="span" fontWeight="700">
                        {Object.values(benchBands).reduce((n, b) => n + b.length, 0)} bande(s)
                      </Text>{' '}
                      sans avoir verrouillé. Revenir en prospectif les efface et dévoile IRIS.
                    </>
                  ) : (
                    <>
                      Revenir en prospectif{' '}
                      <Text as="span" fontWeight="700">dévoile IRIS</Text>{' '}
                      sur cette plaque.
                    </>
                  )}
                </Text>
              </Box>
              <Text fontSize="sm" color="gray.700">
                Le geste est sans retour. Une fois IRIS affiché, cette plaque ne pourra
                plus fournir de lecture en aveugle, et le mode rétrospectif ne se choisit
                qu’au dépôt du fichier : il faudrait redéposer la plaque pour la
                reprendre dans le recueil.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={leaveRetroCancelRef} colorScheme="purple" onClick={() => setConfirmLeaveRetrospective(false)}>
                Continuer la saisie
              </Button>
              <Button
                variant="ghost"
                color="gray.600"
                ml={3}
                onClick={() => {
                  setConfirmLeaveRetrospective(false)
                  switchEvaluationModeRef.current?.('prospective')
                }}
              >
                Abandonner et voir IRIS
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Aucune bande cochée sur toute la plaque : plutôt qu'un blocage, qui
          rendrait une plaque entièrement négative impossible à saisir, on
          demande de confirmer. Le clic distrait s'arrête là, le cas légitime
          passe. */}
      <AlertDialog
        isOpen={confirmEmptyBench}
        leastDestructiveRef={emptyBenchCancelRef}
        onClose={() => setConfirmEmptyBench(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <HStack spacing={2}>
                <Icon as={LuEyeOff} color="purple.500" boxSize={5} />
                <Text>Aucune bande cochée</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={3} mb={4}>
                <Text fontSize="sm" color="purple.800">
                  Vous n’avez coché aucune bande sur l’ensemble de la plaque. Toutes les
                  bandelettes seront donc enregistrées comme négatives.
                </Text>
              </Box>
              <Text fontSize="sm" color="gray.700">
                C’est un résultat possible, et c’est aussi ce qui arrive quand on verrouille
                trop tôt. Le verrouillage est définitif : la lecture ne pourra plus être
                modifiée.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={emptyBenchCancelRef} colorScheme="purple" onClick={() => setConfirmEmptyBench(false)}>
                Revenir cocher
              </Button>
              <Button
                variant="ghost"
                color="gray.600"
                ml={3}
                onClick={() => { setConfirmEmptyBench(false); handleLockBenchReading(true) }}
              >
                Plaque entièrement négative
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Départ retenu : il reste des bandelettes sans verdict. Le but n'est
          pas d'interdire mais de le dire au moment où la personne part —
          observé sur le pilote, le cas s'ouvre, les résultats se regardent, et
          rien n'est validé. La question nomme ce qui reste et laisse sortir
          d'un clic : un garde qu'on ne peut pas franchir est un garde qu'on
          contourne. */}
      <AlertDialog
        isOpen={leaveGuard.state === 'blocked' || newAnalysisBlocked}
        leastDestructiveRef={stayCancelRef}
        onClose={() => { setNewAnalysisBlocked(false); leaveGuard.reset?.() }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <HStack spacing={2}>
                <Icon as={LuCircleCheck} color="orange.500" boxSize={5} />
                <Text>
                  {pendingValidationCount} bandelette{pendingValidationCount > 1 ? 's' : ''} sans verdict
                </Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" p={3} mb={4}>
                <Text fontSize="sm" color="orange.800">
                  Vous quittez ce cas avant d’avoir validé{' '}
                  <Text as="span" fontWeight="700">
                    {pendingValidationCount > 1
                      ? `${pendingValidationCount} bandelettes`
                      : 'une bandelette'}
                  </Text>
                  . Sans verdict, le cas ne peut pas être exporté.
                </Text>
              </Box>
              {/* Dire vrai : le cas n'est pas perdu de vue. Il reste dans les
                  cas en cours de l'accueil, et le rappel de 9 h le reprend dès
                  le lendemain — un cas touché le jour même est sauté, car la
                  personne est probablement encore dessus (voir
                  careflai-be/src/jobs/case-reminder.js). */}
              <Text fontSize="sm" color="gray.700">
                Il restera dans vos cas en cours sur l’accueil, et un rappel vous parviendra
                demain à 9 h. Il ne se validera pas tout seul pour autant.
              </Text>
              <Text mt={3} fontSize="sm" color="gray.500">
                Cliquer sur une bandelette ouvre sa fiche, où le verdict se pose en un clic.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={stayCancelRef}
                colorScheme="orange"
                onClick={() => { setNewAnalysisBlocked(false); leaveGuard.reset?.() }}
              >
                Rester et valider
              </Button>
              <Button
                variant="ghost"
                color="gray.600"
                ml={3}
                onClick={() => {
                  // Deux départs mènent ici : une navigation retenue par le
                  // routeur, ou « Nouvelle analyse », qui ne navigue pas.
                  // Chacun repart par son propre chemin.
                  if (newAnalysisBlocked) {
                    setNewAnalysisBlocked(false)
                    // Le départ est accordé : on ne touche à rien ici, c'est
                    // l'adresse qui remet l'écran à zéro une fois le cas quitté.
                    setPendingDeparture('new-analysis')
                    return
                  }
                  leaveGuard.proceed?.()
                }}
              >
                Quitter quand même
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Confirmation de relance — deux issues : récupérer les bandelettes
          supprimées (non destructif, rien n'est relancé) ou tout redétecter,
          ce qui efface le travail manuel du cas. */}
      {(() => {
        const hiddenCount = ((analysisResult as any)?.hiddenBlotIds ?? []).length
        return (
          <AlertDialog
            isOpen={isReanalyzeOpen}
            leastDestructiveRef={reanalyzeCancelRef}
            onClose={onReanalyzeClose}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Relancer l’analyse
                </AlertDialogHeader>
                <AlertDialogBody>
                  {hiddenCount > 0 && (
                    <Box
                      bg="teal.50"
                      border="1px solid"
                      borderColor="teal.200"
                      borderRadius="lg"
                      p={3}
                      mb={4}
                    >
                      <HStack spacing={2} mb={1}>
                        <Icon as={MdRefresh} color="teal.600" boxSize={4} />
                        <Text fontWeight="700" fontSize="sm" color="teal.800">
                          {hiddenCount > 1
                            ? `${hiddenCount} bandelettes supprimées`
                            : '1 bandelette supprimée'}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="teal.800">
                        Vous pouvez simplement {hiddenCount > 1 ? 'les' : 'la'} remettre, sans
                        rien relancer : témoins, bandes placées, validations et commentaires
                        sont conservés. Les crédits remboursés à la suppression sont redébités.
                      </Text>
                      <Button
                        mt={3}
                        size="sm"
                        colorScheme="teal"
                        variant="solid"
                        onClick={handleRestoreHiddenBlots}
                        isLoading={restoringHidden}
                        leftIcon={<Icon as={MdRefresh} boxSize={4} />}
                      >
                        {hiddenCount > 1
                          ? `Récupérer les ${hiddenCount} bandelettes`
                          : 'Récupérer la bandelette'}
                      </Button>
                    </Box>
                  )}
                  <Text fontSize="sm" color="gray.700">
                    Une relance redétecte toutes les bandelettes depuis l’image source et
                    efface tout le travail manuel de ce cas :
                  </Text>
                  <List spacing={0.5} mt={2} fontSize="sm" color="gray.700">
                    <ListItem>
                      <ListIcon as={MdWarning} color="orange.400" />
                      les témoins sélectionnés
                    </ListItem>
                    <ListItem>
                      <ListIcon as={MdWarning} color="orange.400" />
                      les bandes placées à la main et le calibrage
                    </ListItem>
                    <ListItem>
                      <ListIcon as={MdWarning} color="orange.400" />
                      les validations et les commentaires
                    </ListItem>
                    <ListItem>
                      <ListIcon as={MdWarning} color="orange.400" />
                      les lignes de contrôle ajustées et les inversions mère/enfant
                    </ListItem>
                  </List>
                  <Text fontSize="sm" color="red.500" mt={3}>
                    Cette action est irréversible, et chaque bandelette redétectée est
                    de nouveau facturée.
                  </Text>
                  {reanalyzeProfileConflict && (
                    <Box
                      bg="purple.50"
                      border="1px solid"
                      borderColor="purple.300"
                      borderRadius="lg"
                      p={3}
                      mt={4}
                    >
                      <Text fontWeight="700" fontSize="sm" color="purple.800" mb={1}>
                        Config de détection différente
                      </Text>
                      <Text fontSize="sm" color="purple.800" mb={2}>
                        Votre sélecteur admin est sur{' '}
                        <Text as="span" fontWeight="700">
                          {reanalyzeProfileConflict.overrideProfile.toUpperCase()}
                        </Text>
                        , mais ce cas a été analysé avec{' '}
                        <Text as="span" fontWeight="700">
                          {reanalyzeProfileConflict.caseProfile.toUpperCase()}
                        </Text>
                        . Relancer avec :
                      </Text>
                      <RadioGroup
                        value={reanalyzeProfileChoice}
                        onChange={(v) => setReanalyzeProfileChoice(v as 'case' | 'loaded')}
                      >
                        <Stack spacing={1}>
                          <Radio value="case" size="sm" colorScheme="purple">
                            <Text as="span" fontSize="sm">
                              La config initiale du cas —{' '}
                              <Text as="span" fontWeight="700">
                                {reanalyzeProfileConflict.caseProfile.toUpperCase()}
                              </Text>
                            </Text>
                          </Radio>
                          <Radio value="loaded" size="sm" colorScheme="purple">
                            <Text as="span" fontSize="sm">
                              La config chargée —{' '}
                              <Text as="span" fontWeight="700">
                                {reanalyzeProfileConflict.overrideProfile.toUpperCase()}
                              </Text>
                            </Text>
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </Box>
                  )}
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={reanalyzeCancelRef} onClick={onReanalyzeClose} isDisabled={reanalyzing || restoringHidden}>
                    Annuler
                  </Button>
                  <Button
                    colorScheme="orange"
                    onClick={handleReanalyze}
                    ml={3}
                    isLoading={reanalyzing}
                    isDisabled={restoringHidden}
                  >
                    Tout relancer
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
      })()}
    </VStack>
  )
}
